import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import OffersModuleLayout from "./OffersModuleLayout";
import CMASpotOnPanel from "../components/CMASpotOnPanel";
import PortPairSearchPanel from "../components/PortPairSearchPanel";
import FreightlinksChargesAdmin from "../components/FreightlinksChargesAdmin";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import axios from "axios";
import OfferPreviewPage from "./OfferPreviewPage";
import { SmartLocationInput } from "../components/SmartLocationInput";
import PlaceInput from "../components/PlaceInput";
import { CMA_COMMODITIES, findCommodity } from "../data/commodities";
import { API_URL, API_BASE_URL } from "../config/api";
import { PantheonCompanySelector } from "../components/PantheonCompanySelector";
import {
  Plus,
  Send,
  Archive,
  Save,
  GripVertical,
  Trash2,
  Edit3,
  X,
  ChevronDown,
  ChevronRight,
  FileText,
  Package,
  Ship,
  Plane,
  Truck,
  Layers,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Copy,
  Eye,
  RefreshCw,
  MessageSquare,
  Settings,
  Search,
  Filter,
  ArrowRight,
  Sparkles,
  Printer,
  BookOpen,
  Info,
  AlertTriangle,
  Train,
  Warehouse,
  Upload,
  MapPin,
  History,
  ExternalLink,
  Mail,
  Users,
  Bell,
  CalendarClock,
  UserPlus,
  Loader2,
  ImageIcon,
  GripHorizontal,
} from "lucide-react";


const SERVICE_ICONS = {
  fcl: Ship,
  lcl: Package,
  air: Plane,
  inland: Truck,
  multimodal: Layers,
};

// --- Cargo metrics & percentage-based pricing helpers ---
// Compute derived cargo metrics: CBM (direct or from dims), W/M and air CW.
const computeCargoMetrics = (cargo) => {
  const c = cargo || {};
  const weightKg = parseFloat(c.weight_kg) || 0;
  const L = parseFloat(c.length_cm) || 0;
  const W = parseFloat(c.width_cm) || 0;
  const H = parseFloat(c.height_cm) || 0;
  const pieces = parseFloat(c.pieces) || 0;
  const dimsCbm = (L * W * H * (pieces || 1)) / 1000000;
  let cbm = parseFloat(c.volume_cbm) || 0;
  if (!cbm && dimsCbm) cbm = dimsCbm;
  const factor = parseFloat(c.volumetric_factor) || 167;
  const wm = Math.max(cbm, weightKg / 1000);
  const volumetricKg = cbm * factor;
  const cw = Math.max(weightKg, volumetricKg);
  const rateRaw = c.insurance_rate;
  const insuranceRate = (rateRaw === undefined || rateRaw === null || rateRaw === "") ? 0.2 : parseFloat(rateRaw);
  return {
    weightKg, cbm, dimsCbm, wm, volumetricKg, cw,
    pieces,
    value: parseFloat(c.value) || 0,
    valueCurrency: c.value_currency || "EUR",
    factor,
    insuranceRate: isNaN(insuranceRate) ? 0.2 : insuranceRate,
    insuranceMin: parseFloat(c.insurance_min) || 0,
  };
};

const PERCENT_UNITS = ["%", "%_of"];
const normUnit = (u) => String(u || "").replace(/^per_/, "").toLowerCase();
const isPercentUnit = (u) => PERCENT_UNITS.includes(normUnit(u));

// Raw amount for a component in its OWN currency. Percentage lines are computed
// as a percent of the chosen base line's amount.
const rawComponentAmount = (comp, comps) => {
  if (!comp) return 0;
  if (isPercentUnit(comp.unit)) {
    const base = (comps || []).find((x) => x.component_id === comp.base_line_id);
    if (!base) return 0;
    const baseAmt = (parseFloat(base.unit_price) || 0) * (parseFloat(base.quantity) || 0);
    return ((parseFloat(comp.unit_price) || 0) / 100) * baseAmt;
  }
  return (parseFloat(comp.unit_price) || 0) * (parseFloat(comp.quantity) || 0);
};


// Pre-carriage/On-carriage transport mode options
const TRANSPORT_MODES = {
  truck: { 
    label: "Truck Only", 
    icon: Truck, 
    description: "Direct truck transport",
    available: true 
  },
  rail_truck: { 
    label: "Rail + Truck (Metrans)", 
    icon: Train, 
    description: "Rail via Metrans depots + truck last mile",
    available: true 
  },
  tilt_truck: {
    label: "Tilt Truck",
    icon: Truck,
    description: "Tilt/curtain-side truck for LDM cargo",
    available: true
  },
  warehouse: { 
    label: "Warehouse + Transloading", 
    icon: Warehouse, 
    description: "Delivery to warehouse with transloading",
    available: true
  },
};

// Tilt Truck Rate Options
const TILT_TRUCK_OPTIONS = {
  "6ldm_11t": {
    label: "6 LDM / <11t gross",
    ldm: 6,
    maxWeight: 11000,
    description: "Half truck load"
  },
  "12ldm_24t": {
    label: "12 LDM / <24t gross", 
    ldm: 12,
    maxWeight: 24000,
    description: "Full truck load"
  },
};

// Standard notes for Warehouse + Transloading
const WAREHOUSE_TRANSLOADING_NOTES = `*incl drayage CY-CFS-CY

Storage
--------------
7 days free
8th-15th day: 0,50 EUR/cbm/day
16th-30th day: 1,00 EUR/cbm/day
from then on: 2,00 EUR/cbm/day

General conditions
------------------
*excluding 3rd party billing other than transloading (correcting customs manifest, dunnage removal, additional assistance on reworking cargo,…)
*goods have to be forkliftable – if loose cartons, we will palletise and dispatch cgo on PT
*cgo over 2,5tons/piece has addon EUR 100/cntr
*fixing+lashing (upon request) based on time+material used, min EUR 125/cntr
*ADR/IMO factor +30% - accepted in direct manipulation only
*RF factor +30% - cargo accepted in direct manipulation only
*if printing of documents is required 5 EUR/every started 10 pages
*For goods worth more than EUR 300,000/ctn-trk, which are stored in our warehouses, an additional 0.02% is charged on the invoice value of the goods.`;

// Standard notes for Truck rate
const TRUCK_RATE_NOTES = `[NOTE] rate reflects current market conditions. We reserve the right to change rate prior to service. Please re-check rate prior to delivery

annotation:
*excl FSC, if FSC more than 20,- EUR
*multistop for c/c 50,- EUR
*3 hrs free, then 20 EUR/started hr
*after 6 hrs layday 350 EUR/day
*excluding carrier locals, TCC and customs formalities (T1 or import/export clearance)
*one way trucking is subject to ssline mty d/o approval and fee d/o fee if any
*GPS URL live tracking 20 EUR/trip
*max GW per composition is 40tons (truck+trailor+cntr tare+cargo)`;

// Warehouse Transloading Rate Cards (EUR)
const TRANSLOADING_RATES = {
  // Standard Palletized Cargo
  palletized: {
    label: "Palletized Cargo",
    description: "Forkliftable palletized goods",
    base_rates: {
      "20": 260,
      "40": 290,
    },
    admin_fee: 25,
    storage: {
      unit: "cbm",
      free_days: 7,
      tiers: [
        { from: 8, to: 15, rate: 0.50 },
        { from: 16, to: 30, rate: 1.00 },
        { from: 31, to: 999, rate: 2.00 }
      ]
    },
    surcharges: {
      heavy_cargo: 100,
      fixing_lashing_min: 125,
      adr_imo_factor: 0.30,
      rf_factor: 0.30,
      printing_per_10_pages: 5,
      high_value_factor: 0.0002
    }
  },
  
  // Oversized/OOG Cargo (MAFI/FR/BULK)
  oversized_import: {
    label: "Oversized Import (MAFI/FR/BULK)",
    description: "Ship's hold → Warehouse → Vehicle",
    base_rates: {
      "20FR": 490,
      "40FR": 590,
    },
    admin_fee: 25,
    storage: {
      unit: "ton",
      free_days: 14,
      tiers: [
        { from: 15, to: 28, rate: 0.26 },
        { from: 29, to: 999, rate: 1.00 }
      ]
    },
    surcharges: {
      dunnage_removal: "Labour hours based",
      dunnage_disposal: "CBM & type based",
      printing_per_10_pages: 5,
      high_value_factor: 0.0002
    },
    notes: "Excludes 3rd party billing (customs manifest, dunnage removal, etc.)"
  },
  
  oversized_export: {
    label: "Oversized Export (FCL OOG)",
    description: "Vehicle → Warehouse → MAFI/FR/BULK",
    base_rates: {
      "40FR": 650,
    },
    admin_fee: 25,
    storage: {
      unit: "ton",
      free_days: 14,
      tiers: [
        { from: 15, to: 28, rate: 0.26 },
        { from: 29, to: 999, rate: 1.00 }
      ]
    },
    surcharges: {
      fixing_lashing_min: 540,
      lashing_certificate: 300,
      printing_per_10_pages: 5,
      high_value_factor: 0.0002
    },
    notes: "Fixing & lashing based on time + material"
  },
  
  // Non-Palletized/Loose Cargo
  loose_large: {
    label: "Loose Cargo (up to 1000 pcs)",
    description: "Manual transloading, max 20kg/carton",
    base_rates: {
      "20": 750,
      "40": 900,
    },
    admin_fee: 25,
    palletizing: {
      pallet_cost: 12,
      palletizing_fee: 18
    },
    storage: {
      unit: "cbm",
      free_days: 7,
      tiers: [
        { from: 8, to: 15, rate: 0.50 },
        { from: 16, to: 30, rate: 1.00 },
        { from: 31, to: 999, rate: 2.00 }
      ]
    },
    surcharges: {
      heavy_cargo: 100,
      fixing_lashing_min: 125,
      adr_imo_factor: 0.30,
      rf_factor: 0.30,
      printing_per_10_pages: 5,
      high_value_factor: 0.0002
    }
  },
  
  loose_small: {
    label: "Loose Cargo (up to 500 pcs)",
    description: "Manual transloading, max 20kg/carton",
    base_rates: {
      "20": 345,
      "40": 500,
    },
    admin_fee: 25,
    palletizing: {
      pallet_cost: 12,
      palletizing_fee: 18
    },
    storage: {
      unit: "cbm",
      free_days: 7,
      tiers: [
        { from: 8, to: 15, rate: 0.50 },
        { from: 16, to: 30, rate: 1.00 },
        { from: 31, to: 999, rate: 2.00 }
      ]
    },
    surcharges: {
      heavy_cargo: 100,
      fixing_lashing_min: 125,
      adr_imo_factor: 0.30,
      rf_factor: 0.30,
      printing_per_10_pages: 5,
      high_value_factor: 0.0002
    }
  }
};

// OOG Trucking Terms & Conditions
const OOG_TRUCKING_TERMS = {
  notice_days: 15,
  includes: [
    "Permit and escort",
    "Carrier liability €2,500,000"
  ],
  excludes: [
    "Loading and unloading costs",
    "Cargo insurance",
    "Customs costs or shipping costs over €50",
    "VAT",
    "Truck toll fees",
    "Cargo tarpaulin covering",
    "Route survey",
    "Additional load capacity (wood, etc.)",
    "Roundabouts/additional km",
    "Removal of road signs (VLM)",
    "Additional escorts (BF3/BF4 in Germany, police)"
  ],
  waiting_costs: {
    daily: 850,
    hourly: 80,
    free_hours: 3
  },
  cancellation: {
    days_7: 0.25,  // 25%
    days_3: 0.50,  // 50%
    days_1: 1.00   // 100%
  }
};

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-700", icon: Edit3 },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700", icon: Send },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-700", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: X },
  revised: { label: "Revised", color: "bg-amber-100 text-amber-700", icon: RefreshCw },
  expired: { label: "Expired", color: "bg-gray-100 text-gray-500", icon: Clock },
};

const CATEGORY_COLORS = {
  freight: "#3B82F6",
  origin_charges: "#10B981",
  destination_charges: "#F59E0B",
  surcharges: "#EF4444",
  inland: "#8B5CF6",
  customs: "#EC4899",
  insurance: "#06B6D4",
  warehousing: "#84CC16",
  other: "#6B7280",
};

// Fallback exchange rates relative to EUR (base currency) - used if API fails
const FALLBACK_EXCHANGE_RATES = {
  EUR: 1.0,
  USD: 1.08,    // 1 EUR = 1.08 USD
  GBP: 0.86,    // 1 EUR = 0.86 GBP
  CHF: 0.95,    // 1 EUR = 0.95 CHF
  CNY: 7.85,    // 1 EUR = 7.85 CNY
  JPY: 162.5,   // 1 EUR = 162.5 JPY
  KRW: 1450,    // 1 EUR = 1450 KRW
  INR: 90.5,    // 1 EUR = 90.5 INR
};

export default function PricingModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("quotes"); // "quotes", "offers", "rate-cards", "services"
  const [quotes, setQuotes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [expandedService, setExpandedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [offerBuilder, setOfferBuilder] = useState(null);
  const [offerImages, setOfferImages] = useState([]); // Screenshots/images to include in offer
  // 3 Free Comment Sections (text + images each)
  const [commentSections, setCommentSections] = useState([
    { id: 1, title: "Comment Section 1", text: "", images: [] },
    { id: 2, title: "Comment Section 2", text: "", images: [] },
    { id: 3, title: "Comment Section 3", text: "", images: [] },
  ]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [previewOffer, setPreviewOffer] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);
  
  // Quote Reminder State
  const [reminderQuote, setReminderQuote] = useState(null);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);
  
  const [showStandaloneForm, setShowStandaloneForm] = useState(false);
  const [standaloneData, setStandaloneData] = useState({
    pantheon_company: null, // Required Pantheon company selection
    crm_company_id: null,   // CRM company ID for linking
    customer_company: "",
    customer_contact: "",
    customer_email: "",
    customer_phone: "",
    service_type: "fcl",
    incoterm: "DAP",
    // Routing fields
    poa: "", // Place of Acceptance
    poa_code: "",
    origin_cc: "", // Origin Customs Clearance location
    pol: "", // Port of Loading
    pol_code: "",
    pod: "", // Port of Discharge
    pod_code: "",
    dest_cc: "", // Destination Customs Clearance location
    pofd: "", // Place of Final Delivery
    pofd_code: "",
    // Carrier routing info (copy-paste from carrier website)
    carrier_routing_info: "",
    // Cargo details - support multiple container types
    containers: [
      { id: 1, type: "40HC", quantity: 1 }
    ],
    // Legacy single container fields (for backward compatibility)
    container_type: "40HC",
    container_quantity: 1,
    commodity: "",
    cargo_description: "",
    cargo_ready_date: "",
    is_imo: false,
    // LCL specific - pallet details
    pallets: [{ qty: 1, length: "", width: "", height: "", weight: "" }],
    // Air specific - cargo details
    air_cargo: {
      weight_kg: "",
      volume_cbm: "",
      chargeable_weight: ""
    }
  });
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [localDrafts, setLocalDrafts] = useState([]);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [pendingContact, setPendingContact] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [exchangeRates, setExchangeRates] = useState(FALLBACK_EXCHANGE_RATES);
  const [exchangeRatesLastUpdated, setExchangeRatesLastUpdated] = useState(null);
  const [newService, setNewService] = useState({
    name: "",
    description: "",
    category: "freight",
    quantity: 1,
    unit_price: 0,
    currency: "EUR",
    unit: "container",
    notes: "",
    saveAsTemplate: false,
  });
  
  // Metrans Rail Rate Lookup state
  const [metransLookup, setMetransLookup] = useState({
    show: false,
    mode: 'precarriage', // 'precarriage' or 'oncarriage'
    transportMode: 'truck', // 'truck', 'rail_truck', 'warehouse'
    destination: '',
    destinationCountry: 'HU',
    containerType: '40',
    weightKg: 25000,
    profitType: 'percentage', // 'percentage' or 'fixed'
    profitValue: 10,
    result: null,
    loading: false,
    searchResults: [],
    selectedDepotIndex: 0, // Index for the selected depot option (0 = best rate)
  });

  // Email parsing for pricing enquiry
  const [parsingEmail, setParsingEmail] = useState(false);
  const [emailDragOver, setEmailDragOver] = useState(false);

  // Warehouse Transloading Calculator state
  const [transloadingCalc, setTransloadingCalc] = useState({
    cargoType: 'palletized', // palletized, oversized_import, oversized_export, loose_large, loose_small
    containerType: '40',
    containerQty: 1,
    cargoCbm: 0,
    cargoTons: 0, // for oversized storage calculation
    storageDays: 0,
    // Surcharges
    hasHeavyCargo: false,
    needsFixingLashing: false,
    needsLashingCertificate: false, // for oversized export
    isAdrImo: false,
    isReefer: false,
    printingPages: 0,
    cargoValue: 0,
    // Loose cargo specific
    palletCount: 0,
    needsPalletizing: false,
    // Profit
    profitType: 'percentage',
    profitValue: 10,
  });

  // Tilt Truck Calculator state
  const [tiltTruckCalc, setTiltTruckCalc] = useState({
    fromLocation: '',
    toLocation: '',
    loadType: '6ldm_11t', // '6ldm_11t' or '12ldm_24t'
    price: '',
    comments: '',
    // Profit
    profitType: 'percentage',
    profitValue: 10,
  });
  
  const token = localStorage.getItem("token");
  const [commodityList, setCommodityList] = useState(CMA_COMMODITIES);

  // Service categories for custom services
  const SERVICE_CATEGORIES = [
    { id: "freight", name: "Freight", description: "Ocean/Air freight charges" },
    { id: "origin_charges", name: "Origin Charges", description: "THC, handling at origin" },
    { id: "destination_charges", name: "Destination Charges", description: "THC, handling at destination" },
    { id: "customs", name: "Customs", description: "Import/Export clearance, T1" },
    { id: "inland", name: "Inland Transport", description: "Pre-carriage, on-carriage, trucking" },
    { id: "surcharges", name: "Surcharges", description: "BAF, CAF, PSS, etc." },
    { id: "documentation", name: "Documentation", description: "BL, VGM, certificates" },
    { id: "insurance", name: "Insurance", description: "Cargo insurance" },
    { id: "warehousing", name: "Warehousing", description: "Storage, handling" },
    { id: "other", name: "Other", description: "Miscellaneous charges" },
  ];

  // LCL Rate Lookup state
  const [lclRates, setLclRates] = useState({
    loading: false,
    primary: null,  // Freightlinks (FLE) = our rate
    reference: [],  // Shipco / ECU competition rates
    lastLookup: null,
    note: null,
    expired: 0,
  });
  // CMA SpotOn live-rate lookup inside the Offer Builder (port-to-port).
  const [cmaBuilder, setCmaBuilder] = useState({ loading: false, result: null, error: null });

  // LCL Import Comparison state
  const [lclImport, setLclImport] = useState({
    loading: false,
    searchQuery: "",
    selectedRegion: null,
    regions: [],
    searchResults: [],
    comparison: null,
    rateCards: [],
  });

  // Trucking Rate Suggestions state (based on historical transport orders)
  const [truckingSuggestions, setTruckingSuggestions] = useState({
    show: false,
    loading: false,
    mode: 'precarriage', // 'precarriage' or 'oncarriage'
    suggestions: null,
    sampleOrders: [],
  });

  // Task assignment state
  const [assignTaskModal, setAssignTaskModal] = useState({
    show: false,
    quote: null,
    loading: false,
  });
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [taskNote, setTaskNote] = useState("");
  const [taskDueDate, setTaskDueDate] = useState("");

  // Unit types for services
  const UNIT_TYPES = [
    { id: "container", name: "per container" },
    { id: "bl", name: "per B/L" },
    { id: "shipment", name: "per shipment" },
    { id: "cbm", name: "per CBM" },
    { id: "kg", name: "per kg" },
    { id: "ton", name: "per ton" },
    { id: "pallet", name: "per pallet" },
    { id: "package", name: "per package" },
    { id: "day", name: "per day" },
    { id: "hour", name: "per hour" },
    { id: "trip", name: "per trip" },
    { id: "flat", name: "flat rate" },
    { id: "percentage", name: "percentage" },
  ];

  // Load live CMA commodity list (Goods Codification) — falls back to the
  // bundled list if CMA is unreachable / quota-limited.
  useEffect(() => {
    const loadCommodities = async () => {
      try {
        const res = await axios.get(`${API_URL}/cma-cgm/commodities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = res.data?.commodities || [];
        if (items.length) setCommodityList(items);
      } catch (e) {
        // keep bundled fallback list
      }
    };
    loadCommodities();
  }, [token]);

  // Load local drafts from localStorage on mount
  useEffect(() => {
    const savedDrafts = localStorage.getItem("offer_drafts");
    if (savedDrafts) {
      try {
        setLocalDrafts(JSON.parse(savedDrafts));
      } catch (e) {
        console.error("Error loading drafts:", e);
      }
    }
  }, []);

  // Fetch live exchange rates from free API
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        // Using exchangerate-api.com free tier (EUR as base)
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        if (response.ok) {
          const data = await response.json();
          // Map API response to our format
          const rates = {
            EUR: 1.0,
            USD: data.rates.USD || FALLBACK_EXCHANGE_RATES.USD,
            GBP: data.rates.GBP || FALLBACK_EXCHANGE_RATES.GBP,
            CHF: data.rates.CHF || FALLBACK_EXCHANGE_RATES.CHF,
            CNY: data.rates.CNY || FALLBACK_EXCHANGE_RATES.CNY,
            JPY: data.rates.JPY || FALLBACK_EXCHANGE_RATES.JPY,
            KRW: data.rates.KRW || FALLBACK_EXCHANGE_RATES.KRW,
            INR: data.rates.INR || FALLBACK_EXCHANGE_RATES.INR,
          };
          setExchangeRates(rates);
          setExchangeRatesLastUpdated(new Date(data.time_last_updated * 1000));
          console.log("Exchange rates updated:", rates);
        }
      } catch (error) {
        console.error("Failed to fetch exchange rates, using fallback:", error);
        // Keep fallback rates
      }
    };

    fetchExchangeRates();
    // Refresh rates every hour
    const interval = setInterval(fetchExchangeRates, 3600000);
    return () => clearInterval(interval);
  }, []);

  // Fetch staff list for task assignment
  useEffect(() => {
    const fetchStaffList = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/users/staff`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStaffList(response.data || []);
      } catch (error) {
        console.error("Failed to fetch staff list:", error);
      }
    };
    fetchStaffList();
  }, [token]);

  // Load offer from URL parameter (e.g., /offers/pricing?offer=xxx or direct link from email)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const offerId = params.get('offer');
    const currentToken = localStorage.getItem("token");
    
    if (offerId && currentToken) {
      // Small delay to ensure page is fully loaded
      const loadTimer = setTimeout(() => {
        // Load the offer draft directly
        const loadOfferFromUrl = async () => {
          try {
            console.log("Loading offer from URL:", offerId);
            const response = await axios.get(`${API_URL}/pricing/offers/${offerId}`, {
              headers: { Authorization: `Bearer ${currentToken}` }
            });
            const offer = response.data.offer;  // API returns {offer: {...}, quote_request: {...}}
            console.log("Loaded offer:", offer);
            
            // If it's a draft from a quote request, load it into the builder
            if (offer.status === 'draft' && offer.source === 'quote_request') {
              // Create offer builder from the draft
              const offerBuilderData = {
                existingOfferId: offer.id,
                quoteRequest: {
                  id: offer.quote_request_id,
                  quote_number: offer.quote_number,
                  service_type: offer.service_type,
                  company_name: offer.customer_company,
                  contact_name: offer.customer_contact,
                  email: offer.customer_email,
                  phone: offer.customer_phone,
                  origin_city: offer.origin_city,
                  origin_country: offer.origin_country,
                  origin_port_airport: offer.origin_port,
                  destination_city: offer.destination_city,
                  destination_country: offer.destination_country,
                  destination_port_airport: offer.destination_port,
                  container_type: offer.container_type,
                  container_quantity: offer.container_quantity,
                  commodity: offer.commodity,
                  total_weight_kg: offer.total_weight_kg,
                  total_volume_cbm: offer.total_volume_cbm,
                  cargo_ready_date: offer.cargo_ready_date,
                  additional_services: offer.additional_services,
                  special_requirements: offer.special_requirements,
                  incoterm: offer.incoterm,
                },
                components: offer.components || [],
                matchingRateCards: [],
                categories: [],
                currency: offer.currency || "EUR",
                validityDays: offer.validity_days || 14,
                incoterm: offer.incoterm || "",
                transitTime: offer.transit_time || "",
                routing: offer.routing || "",
                vesselSchedule: offer.vessel_schedule || "",
                paymentTerms: offer.payment_terms || "30 days from B/L date",
                specialConditions: offer.special_conditions || "",
                internalNotes: offer.internal_notes || "",
                cargo: offer.cargo || {
                  weight_kg: offer.total_weight_kg || "",
                  volume_cbm: offer.total_volume_cbm || "",
                  value: offer.cargo_value || "",
                  value_currency: offer.cargo_value_currency || "EUR",
                },
                isStandalone: false,
              };
              
              // Load comment sections if saved
              if (offer.comment_sections?.length) {
                setCommentSections(offer.comment_sections.map((s, i) => ({
                  id: s.id || i + 1,
                  title: s.title || `Comment Section ${i + 1}`,
                  text: s.text || "",
                  images: s.images || [],
                })));
              }
              
              setOfferBuilder(offerBuilderData);
              setSelectedQuote(offerBuilderData.quoteRequest);
              setActiveTab("offers");
              toast.success(`Loaded offer draft ${offer.offer_number} from quote request`);
            } else {
              // For other offers, just show in preview or clone mode
              setPreviewOffer(offer);
              setActiveTab("offers");
            }
          } catch (error) {
            console.error("Error loading offer from URL:", error);
            toast.error("Failed to load offer");
          }
        };
        
        loadOfferFromUrl();
      }, 500);
      
      return () => clearTimeout(loadTimer);
    }
  }, [location.search]);

  // Prefill a standalone offer from a Rate Finder rate (handoff via localStorage).
  useEffect(() => {
    const raw = localStorage.getItem("rate_to_offer");
    if (!raw) return;
    localStorage.removeItem("rate_to_offer");
    try {
      const r = JSON.parse(raw);
      const freight = {
        component_id: `rate_freight_${Date.now()}`,
        name: "Ocean Freight (FCL)",
        category: "freight",
        quantity: r.container_quantity || 1,
        unit_price: r.rate || 0,
        currency: r.currency || "USD",
        unit: "container",
        description: `${r.pol} → ${r.pod}${r.carrier ? ` · ${r.carrier}` : ""}`,
        notes: `From Rate Finder · ${r.source || "api"}${r.included_charges?.length ? ` · incl: ${r.included_charges.join(", ")}` : ""}`,
        is_included: true,
      };
      const builder = {
        quoteRequest: {
          id: null, quote_number: "DIRECT", service_type: "fcl",
          company_name: "", contact_name: "", email: "",
          origin_city: r.pol, destination_city: r.pod,
          origin_port: r.pol, destination_port: r.pod,
          container_type: r.container_type, container_quantity: r.container_quantity,
        },
        components: [freight],
        matchingRateCards: [], categories: [],
        currency: r.currency || "USD", validityDays: 14, incoterm: "FOB",
        transitTime: r.transit_days ? `${r.transit_days} days` : "",
        routing: `POL: ${r.pol} → POD: ${r.pod}`,
        vesselSchedule: "", paymentTerms: "30 days from B/L date",
        specialConditions: "", internalNotes: "", isStandalone: true,
        standaloneData: { service_type: "fcl", pol: r.pol, pod: r.pod, container_type: r.container_type, container_quantity: r.container_quantity },
      };
      setOfferBuilder(builder);
      setStandaloneData((prev) => ({ ...prev, service_type: "fcl", pol: r.pol, pod: r.pod, pol_code: r.pol, pod_code: r.pod, container_type: r.container_type, container_quantity: r.container_quantity }));
      setSelectedQuote(builder.quoteRequest);
      setActiveTab("offers");
      toast.success(`Loaded ${r.carrier || "carrier"} rate ${r.rate} ${r.currency} into Offer Builder`);
    } catch (e) { /* ignore malformed handoff */ }
  }, []);

  // Autosave offer builder every 30 seconds
  useEffect(() => {
    if (!offerBuilder) return;
    
    const autoSaveInterval = setInterval(() => {
      saveOfferDraftLocally();
    }, 30000); // 30 seconds
    
    return () => clearInterval(autoSaveInterval);
  }, [offerBuilder]);

  // Save draft to localStorage
  const saveOfferDraftLocally = () => {
    if (!offerBuilder) {
      toast.error("No offer to save");
      return;
    }
    
    try {
      const draft = {
        id: offerBuilder.draftId || `draft_${Date.now()}`,
        savedAt: new Date().toISOString(),
        offerBuilder: offerBuilder,
        selectedQuote: selectedQuote,
      };
      
      // Update offerBuilder with draft ID if new
      if (!offerBuilder.draftId) {
        setOfferBuilder(prev => ({ ...prev, draftId: draft.id }));
      }
      
      // Update local drafts
      setLocalDrafts(prev => {
        const existing = prev.findIndex(d => d.id === draft.id);
        let updated;
        if (existing >= 0) {
          updated = [...prev];
          updated[existing] = draft;
        } else {
          updated = [draft, ...prev].slice(0, 10); // Keep max 10 drafts
        }
        localStorage.setItem("offer_drafts", JSON.stringify(updated));
        return updated;
      });
      
      setLastAutoSave(new Date());
      toast.success("Draft saved!");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    }
  };

  // Load a draft
  const loadDraft = (draft) => {
    setOfferBuilder(draft.offerBuilder);
    setSelectedQuote(draft.selectedQuote);
    toast.success("Draft loaded");
  };

  // Delete a draft
  const deleteDraft = (draftId) => {
    setLocalDrafts(prev => {
      const updated = prev.filter(d => d.id !== draftId);
      localStorage.setItem("offer_drafts", JSON.stringify(updated));
      return updated;
    });
    toast.success("Draft deleted");
  };

  // Convert amount from one currency to another using live exchange rates
  const convertCurrency = useCallback((amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    // Convert to EUR first (base), then to target currency
    const amountInEUR = amount / (exchangeRates[fromCurrency] || 1);
    return amountInEUR * (exchangeRates[toCurrency] || 1);
  }, [exchangeRates]);

  // Search Metrans destinations
  const searchMetransDestinations = async (query, country) => {
    if (query.length < 2) return;
    try {
      const response = await axios.get(`${API_URL}/pricing/metrans/search`, {
        params: { q: query, country },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetransLookup(prev => ({ ...prev, searchResults: response.data }));
    } catch (error) {
      console.error("Error searching Metrans destinations:", error);
    }
  };

  // Lookup Metrans rail rate
  const lookupMetransRate = async () => {
    setMetransLookup(prev => ({ ...prev, loading: true, result: null, selectedDepotIndex: 0 }));
    try {
      const response = await axios.post(`${API_URL}/pricing/metrans/lookup`, {
        destination_country: metransLookup.destinationCountry,
        destination_city: metransLookup.destination,
        container_type: metransLookup.containerType,
        weight_kg: metransLookup.weightKg,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetransLookup(prev => ({ ...prev, result: response.data, loading: false, selectedDepotIndex: 0 }));
    } catch (error) {
      console.error("Error looking up Metrans rate:", error);
      toast.error("Failed to lookup Metrans rate");
      setMetransLookup(prev => ({ ...prev, loading: false }));
    }
  };

  // Calculate selling price with profit margin
  const calculateSellingPrice = (baseRate, toll = 0) => {
    const totalCost = baseRate + toll;
    if (metransLookup.profitType === 'percentage') {
      return totalCost * (1 + metransLookup.profitValue / 100);
    }
    return totalCost + metransLookup.profitValue;
  };

  // Get all depot options from Metrans lookup result (best rate + alternatives)
  const getMetransDepotOptions = () => {
    if (!metransLookup.result?.found) return [];
    
    // Create the "best" option from the main result
    const bestOption = {
      via_depot: metransLookup.result.via_depot,
      rate: metransLookup.result.base_rate,
      toll: metransLookup.result.toll || 0,
      total: (metransLookup.result.base_rate || 0) + (metransLookup.result.toll || 0),
      destination: metransLookup.result.destination,
      container_type: metransLookup.result.container_type,
      weight_bracket: metransLookup.result.weight_bracket,
      notes: metransLookup.result.notes,
      surcharges_link: metransLookup.result.surcharges_link,
    };
    
    // Combine best option with alternatives
    const allOptions = [bestOption, ...(metransLookup.result.alternatives || [])];
    
    // Filter to unique depot options (keep the one with lowest total for each depot)
    const uniqueDepots = {};
    allOptions.forEach(opt => {
      const depot = opt.via_depot;
      if (!uniqueDepots[depot] || opt.total < uniqueDepots[depot].total) {
        uniqueDepots[depot] = opt;
      }
    });
    
    return Object.values(uniqueDepots);
  };

  // Get the currently selected Metrans rate based on depot selection
  const getSelectedMetransRate = () => {
    const options = getMetransDepotOptions();
    if (options.length === 0) return null;
    
    const selectedIndex = Math.min(metransLookup.selectedDepotIndex, options.length - 1);
    return options[selectedIndex];
  };

  // Add Metrans rate to offer
  const addMetransRateToOffer = () => {
    if (!metransLookup.result?.found) return;
    
    const selectedRate = getSelectedMetransRate();
    if (!selectedRate) return;
    
    const baseRate = selectedRate.rate;
    const toll = selectedRate.toll || 0;
    const sellingPrice = calculateSellingPrice(baseRate, toll);
    
    const componentName = metransLookup.mode === 'precarriage' 
      ? `Pre-carriage (Rail+Truck via ${selectedRate.via_depot})`
      : `On-carriage (Rail+Truck via ${selectedRate.via_depot})`;
    
    const newComponent = {
      component_id: `metrans_${Date.now()}`,
      name: componentName,
      category: metransLookup.mode === 'precarriage' ? 'origin_charges' : 'destination_charges',
      quantity: standaloneData.container_quantity || 1,
      unit_price: Math.round(sellingPrice * 1000) / 1000,
      currency: 'EUR',
      unit: 'container',
      description: `${selectedRate.container_type || metransLookup.result.container_type} ${selectedRate.weight_bracket || metransLookup.result.weight_bracket} to ${selectedRate.destination || metransLookup.result.destination}`,
      notes: `Metrans rail via ${selectedRate.via_depot}`,
      is_included: true,
    };
    
    setOfferBuilder(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }));
    
    toast.success(`Added ${componentName} to offer`);
    setMetransLookup(prev => ({ ...prev, show: false }));
  };

  // Handle image paste from clipboard
  const handleImagePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        await uploadOfferImage(file);
        return;
      }
    }
  };

  // Handle image file selection
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      await uploadOfferImage(file);
    }
  };

  // Upload image to server and store URL
  const uploadOfferImage = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/offers/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.url) {
        const newImage = {
          id: Date.now().toString(),
          url: response.data.url,
          name: file.name || 'Screenshot',
          uploadedAt: new Date().toISOString(),
        };
        setOfferImages(prev => [...prev, newImage]);
        toast.success('Image added to offer');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      // Fallback: Convert to base64 for local storage
      const reader = new FileReader();
      reader.onload = () => {
        const newImage = {
          id: Date.now().toString(),
          url: reader.result,
          name: file.name || 'Screenshot',
          uploadedAt: new Date().toISOString(),
          isBase64: true,
        };
        setOfferImages(prev => [...prev, newImage]);
        toast.success('Image added to offer (stored locally)');
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove image from offer
  const removeOfferImage = (imageId) => {
    setOfferImages(prev => prev.filter(img => img.id !== imageId));
    toast.success('Image removed');
  };

  // Upload image for a specific comment section
  const uploadCommentImage = async (file, sectionId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/offers/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.url) {
        const newImage = {
          id: Date.now().toString(),
          url: response.data.url,
          name: file.name || 'Screenshot',
          uploadedAt: new Date().toISOString(),
        };
        setCommentSections(prev => prev.map(s => 
          s.id === sectionId ? { ...s, images: [...s.images, newImage] } : s
        ));
        toast.success('Image added to comment section');
      }
    } catch (error) {
      console.error('Error uploading comment image:', error);
      const reader = new FileReader();
      reader.onload = () => {
        const newImage = {
          id: Date.now().toString(),
          url: reader.result,
          name: file.name || 'Screenshot',
          uploadedAt: new Date().toISOString(),
          isBase64: true,
        };
        setCommentSections(prev => prev.map(s => 
          s.id === sectionId ? { ...s, images: [...s.images, newImage] } : s
        ));
        toast.success('Image added (stored locally)');
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCommentImage = (sectionId, imageId) => {
    setCommentSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, images: s.images.filter(img => img.id !== imageId) } : s
    ));
  };

  // Calculate transloading costs
  const calculateTransloadingCosts = () => {
    const tc = transloadingCalc;
    const rateCard = TRANSLOADING_RATES[tc.cargoType];
    
    if (!rateCard) return { totalCost: 0, surchargeDetails: [] };
    
    // Base transloading rate
    const containerKey = tc.cargoType.includes('oversized') 
      ? (tc.containerType === '20' ? '20FR' : '40FR')
      : tc.containerType;
    const baseRate = rateCard.base_rates[containerKey] || Object.values(rateCard.base_rates)[0] || 0;
    let transloadingCost = baseRate * tc.containerQty;
    
    // Admin fee
    const adminFee = (rateCard.admin_fee || 0) * tc.containerQty;
    
    // Storage calculation
    let storageCost = 0;
    const storageUnit = rateCard.storage.unit === 'ton' ? tc.cargoTons : tc.cargoCbm;
    
    if (tc.storageDays > rateCard.storage.free_days && storageUnit > 0) {
      let remainingDays = tc.storageDays - rateCard.storage.free_days;
      
      for (const tier of rateCard.storage.tiers) {
        if (remainingDays <= 0) break;
        const tierStart = tier.from - rateCard.storage.free_days - 1;
        const tierDays = Math.min(remainingDays, tier.to - tier.from + 1);
        storageCost += tierDays * tier.rate * storageUnit;
        remainingDays -= tierDays;
      }
    }
    
    // Surcharges
    let surcharges = 0;
    let surchargeDetails = [];
    
    // Heavy cargo (not for oversized)
    if (tc.hasHeavyCargo && rateCard.surcharges?.heavy_cargo) {
      const heavyCharge = rateCard.surcharges.heavy_cargo * tc.containerQty;
      surcharges += heavyCharge;
      surchargeDetails.push(`Heavy cargo (>2.5t/pc): €${heavyCharge}`);
    }
    
    // Fixing/Lashing
    if (tc.needsFixingLashing && rateCard.surcharges?.fixing_lashing_min) {
      const fixingCharge = rateCard.surcharges.fixing_lashing_min * tc.containerQty;
      surcharges += fixingCharge;
      surchargeDetails.push(`Fixing/Lashing (min): €${fixingCharge}`);
    }
    
    // Lashing certificate (oversized export only)
    if (tc.needsLashingCertificate && rateCard.surcharges?.lashing_certificate) {
      const lashingCertCharge = rateCard.surcharges.lashing_certificate * tc.containerQty;
      surcharges += lashingCertCharge;
      surchargeDetails.push(`Lashing Certificate: €${lashingCertCharge}`);
    }
    
    // ADR/IMO
    if (tc.isAdrImo && rateCard.surcharges?.adr_imo_factor) {
      const adrCharge = transloadingCost * rateCard.surcharges.adr_imo_factor;
      surcharges += adrCharge;
      surchargeDetails.push(`ADR/IMO (+30%): €${adrCharge.toFixed(2)}`);
    }
    
    // Reefer
    if (tc.isReefer && rateCard.surcharges?.rf_factor) {
      const rfCharge = transloadingCost * rateCard.surcharges.rf_factor;
      surcharges += rfCharge;
      surchargeDetails.push(`Reefer (+30%): €${rfCharge.toFixed(2)}`);
    }
    
    // Printing
    if (tc.printingPages > 0 && rateCard.surcharges?.printing_per_10_pages) {
      const printingCharge = Math.ceil(tc.printingPages / 10) * rateCard.surcharges.printing_per_10_pages;
      surcharges += printingCharge;
      surchargeDetails.push(`Printing (${tc.printingPages} pgs): €${printingCharge}`);
    }
    
    // High value
    if (tc.cargoValue > 300000 && rateCard.surcharges?.high_value_factor) {
      const highValueCharge = tc.cargoValue * rateCard.surcharges.high_value_factor;
      surcharges += highValueCharge;
      surchargeDetails.push(`High value (>€300k): €${highValueCharge.toFixed(2)}`);
    }
    
    // Palletizing (loose cargo only)
    let palletizingCost = 0;
    if (tc.needsPalletizing && rateCard.palletizing && tc.palletCount > 0) {
      palletizingCost = tc.palletCount * (rateCard.palletizing.pallet_cost + rateCard.palletizing.palletizing_fee);
      surchargeDetails.push(`Palletizing (${tc.palletCount} pallets): €${palletizingCost.toFixed(2)}`);
    }
    
    const totalCost = transloadingCost + adminFee + storageCost + surcharges + palletizingCost;
    
    return {
      cargoType: tc.cargoType,
      cargoTypeLabel: rateCard.label,
      transloadingCost,
      adminFee,
      storageCost,
      storageUnit: rateCard.storage.unit,
      surcharges,
      palletizingCost,
      surchargeDetails,
      totalCost,
      containerType: tc.containerType,
      containerQty: tc.containerQty
    };
  };

  // Add transloading charges to offer
  const addTransloadingToOffer = () => {
    const costs = calculateTransloadingCosts();
    const tc = transloadingCalc;
    
    // Apply profit margin
    let sellingPrice;
    if (tc.profitType === 'percentage') {
      sellingPrice = costs.totalCost * (1 + tc.profitValue / 100);
    } else {
      sellingPrice = costs.totalCost + tc.profitValue;
    }
    
    const cargoTypeLabel = costs.cargoTypeLabel || 'Transloading';
    // Use "Warehouse + Transloading" as the main component name
    const componentName = metransLookup.mode === 'precarriage' 
      ? `Pre-carriage (Warehouse + Transloading)`
      : `On-carriage (Warehouse + Transloading)`;
    
    const containerLabel = tc.cargoType.includes('oversized') 
      ? `${tc.containerType}FR` 
      : `${tc.containerType}'`;
    
    const description = [
      `${containerLabel} x ${tc.containerQty}`,
      `Cargo: ${cargoTypeLabel}`,
      tc.storageDays > 0 ? `Storage: ${tc.storageDays} days` : null,
      costs.storageUnit === 'ton' && tc.cargoTons > 0 ? `${tc.cargoTons} tons` : null,
      costs.storageUnit === 'cbm' && tc.cargoCbm > 0 ? `${tc.cargoCbm} CBM` : null,
    ].filter(Boolean).join(', ');
    
    // Use standard warehouse/transloading notes
    const notes = WAREHOUSE_TRANSLOADING_NOTES;
    
    const newComponent = {
      component_id: `transload_${Date.now()}`,
      name: componentName,
      category: metransLookup.mode === 'precarriage' ? 'origin_charges' : 'destination_charges',
      quantity: 1,
      unit_price: Math.round(sellingPrice * 1000) / 1000,
      currency: 'EUR',
      unit: 'lumpsum',
      description: description,
      notes: notes,
      is_included: true,
    };
    
    setOfferBuilder(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }));
    
    toast.success(`Added ${componentName} to offer`);
    setMetransLookup(prev => ({ ...prev, show: false }));
    
    // Reset calculator
    setTransloadingCalc({
      cargoType: 'palletized',
      containerType: '40',
      containerQty: 1,
      cargoCbm: 0,
      cargoTons: 0,
      storageDays: 0,
      hasHeavyCargo: false,
      needsFixingLashing: false,
      needsLashingCertificate: false,
      isAdrImo: false,
      isReefer: false,
      printingPages: 0,
      cargoValue: 0,
      palletCount: 0,
      needsPalletizing: false,
      profitType: 'percentage',
      profitValue: 10,
    });
  };

  // Look up LCL rates (Freightlinks + competition)
  const lookupLCLRates = async () => {
    const qr = offerBuilder?.quoteRequest || {};
    // Port-to-port only: derive POL/POD, ignoring EXW/DDP/DAP door legs.
    const pol = standaloneData.pol || qr.origin_port || qr.pol || qr.origin_city || "";
    const pod = standaloneData.pod || qr.destination_port || qr.pod || qr.destination_city || "";
    
    if (!pol || !pod) {
      toast.error("Add POL / POD in the port-pair fields above to look up port-to-port rates.");
      return;
    }
    
    // Determine direction based on POD (if Koper = import, else export)
    const direction = pod.toLowerCase().includes('koper') || pod.toLowerCase().includes('sikop') 
      ? 'import' 
      : 'export';
    
    setLclRates(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.post(`${API_URL}/pricing/lcl/lookup`, {
        pol: pol,
        pod: pod,
        direction: direction,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLclRates({
        loading: false,
        primary: response.data.primary_rate,
        reference: response.data.reference_rates || [],
        lastLookup: { pol, pod, direction },
        note: response.data.note || null,
        expired: response.data.expired_count || 0,
      });
      
      if (!response.data.found) {
        toast.info("No LCL rates found for this route. Upload rate cards first.");
      }
    } catch (error) {
      console.error("Error looking up LCL rates:", error);
      setLclRates(prev => ({ ...prev, loading: false }));
      toast.error("Failed to lookup LCL rates");
    }
  };

  // Add LCL ocean freight to offer with profit
  const addLCLFreightToOffer = (rate, profitPercent = 10) => {
    const baseRate = rate.rate_per_cbm_with_addon || rate.rate_per_cbm;
    const sellingRate = baseRate * (1 + profitPercent / 100);
    
    const newComponent = {
      component_id: `lcl_freight_${Date.now()}`,
      name: "Ocean Freight (LCL)",
      category: "freight",
      quantity: standaloneData.total_volume_cbm || 1,
      unit_price: Math.round(sellingRate * 1000) / 1000,
      currency: rate.currency || "USD",
      unit: "cbm",
      description: `${rate.pol} → ${rate.pod}${rate.transhipment ? ` via ${rate.transhipment}` : ''}`,
      notes: rate.addon_note ? `${rate.addon_note} · incl. ${profitPercent}% margin` : `${rate.vendor} rate · incl. ${profitPercent}% margin`,
      is_included: true,
    };
    
    setOfferBuilder(prev => ({
      ...prev,
      components: [...prev.components.filter(c => c.name !== "LCL Freight"), newComponent]
    }));
    
    toast.success("Added LCL Ocean Freight to offer");
  };

  // CMA SpotOn live rate from the Offer Builder. Resolves the port-pair text
  // (name or UN/LOCODE) to CMA UN/LOCODEs, then calls SpotOn. Port-to-port
  // only — door legs (EXW/DAP) stay out of the live rate.
  const lookupCMABuilder = async () => {
    const qr = offerBuilder?.quoteRequest || {};
    const polText = (standaloneData.pol || qr.origin_port || qr.pol || qr.origin_city || "").trim();
    const podText = (standaloneData.pod || qr.destination_port || qr.pod || qr.destination_city || "").trim();
    if (!polText || !podText) {
      toast.error("Add POL / POD in the port-pair fields above first.");
      return;
    }
    setCmaBuilder({ loading: true, result: null, error: null });
    try {
      const resolve = async (q) => {
        if (/^[A-Za-z]{2}[A-Za-z0-9]{3}$/.test(q)) return q.toUpperCase();
        const { data } = await axios.get(`${API_URL}/cma-cgm/ref/search`, {
          params: { q }, headers: { Authorization: `Bearer ${token}` },
        });
        const hit = (data.results || []).find(r => r.unlocode) || (data.results || [])[0];
        return hit?.unlocode || null;
      };
      const polCode = await resolve(polText);
      const podCode = await resolve(podText);
      if (!polCode || !podCode) {
        setCmaBuilder({ loading: false, result: null, error: "Couldn't resolve a CMA port code. Enter a UN/LOCODE (e.g. CNSHA) or 'City, CC' (e.g. Haiphong, VN)." });
        return;
      }
      const eq = [{ equipmentGroupIsoCode: "40HC", numberOfContainers: 1, weightPerContainer: 18000 }];
      const { data } = await axios.post(`${API_URL}/cma-cgm/spot/search`, {
        portOfLoading: polCode, portOfDischarge: podCode, commodityCode: "FAK", requestedEquipments: eq,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (!data.best_offer) {
        setCmaBuilder({ loading: false, result: data, error: "No CMA SpotOn offer for this lane." });
        return;
      }
      setCmaBuilder({ loading: false, result: data, error: null });
    } catch (e) {
      setCmaBuilder({ loading: false, result: null, error: e.response?.data?.detail || "CMA lookup failed." });
    }
  };

  const addCMASpotToOffer = (offer, marginPct = 15) => {
    const cost = offer.total_amount || 0;
    const isLcl = offerBuilder?.quoteRequest?.service_type === 'lcl';
    // FCL: CMA rate + $50/TEU (40HC default = 2 TEU) plus standard B/L, handling
    // & admin add-ons. LCL: simple percentage uplift.
    const teu = 2;
    const sell = isLcl
      ? Math.round(cost * (1 + marginPct / 100) * 100) / 100
      : Math.round((cost + 50 * teu) * 100) / 100;
    const freightComp = {
      component_id: `cma_freight_${Date.now()}`,
      name: isLcl ? "Ocean Freight (CMA SpotOn)" : "Ocean Freight (FCL)",
      category: "freight", quantity: 1, unit_price: sell,
      currency: offer.currency || "USD", unit: isLcl ? "cbm" : "container",
      description: `${cmaBuilder.result?.pol_point_code || ''} → ${cmaBuilder.result?.pod_point_code || ''}`,
      notes: isLcl ? `CMA SpotOn rate · incl. ${marginPct}% margin` : `CMA SpotOn rate · incl. $50/TEU margin`,
      is_included: true,
    };
    const extras = isLcl ? [] : [
      { name: "B/L fee", unit: "bl", price: 50 },
      { name: "Handling", unit: "bl", price: 50 },
      { name: "ADMIN", unit: "invoice", price: 13 },
    ].map((x, i) => ({
      component_id: `cma_sur_${Date.now()}_${i}`, name: x.name, category: "surcharges",
      quantity: 1, unit_price: x.price, currency: "EUR", unit: x.unit, total: x.price, is_included: true,
    }));
    const drop = ["Ocean Freight", "LCL Freight"];
    setOfferBuilder(prev => ({
      ...prev,
      components: [...prev.components.filter(c => !drop.includes(c.name)), freightComp, ...extras],
    }));
    toast.success("Added CMA ocean freight + FCL charges to offer");
  };


  // Lookup trucking rate suggestions based on historical transport orders
  const lookupTruckingSuggestions = async (mode) => {
    if (!offerBuilder) return;
    
    const qr = offerBuilder.quoteRequest;
    const sd = offerBuilder.standaloneData || {};
    
    // Determine origin and destination based on mode
    let origin, destination;
    
    if (mode === 'precarriage') {
      // Pre-carriage: From pickup location (POA) to port (POL)
      origin = sd.poa || qr.origin_city || qr.poa || '';
      destination = sd.pol || qr.origin_port_airport || qr.pol || 'Koper';
    } else {
      // On-carriage: From port (POD) to final destination (POFD)
      origin = sd.pod || qr.destination_port_airport || qr.pod || 'Koper';
      destination = sd.pofd || qr.destination_city || qr.pofd || '';
    }
    
    if (!origin && !destination) {
      toast.error("Please fill in routing information first");
      return;
    }
    
    setTruckingSuggestions(prev => ({ ...prev, loading: true, mode, show: true }));
    
    try {
      const response = await axios.post(
        `${API_URL}/pricing/trucking-rate-suggestions`,
        {
          origin: origin,
          destination: destination,
          service_type: qr.service_type || 'fcl',
          container_type: qr.container_type || sd.container_type || '40HC',
          container_quantity: qr.container_quantity || sd.container_quantity || 1,
          weight_kg: qr.total_weight_kg || sd.total_weight_kg || null,
          volume_cbm: qr.total_volume_cbm || sd.total_volume_cbm || null,
          is_precarriage: mode === 'precarriage'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTruckingSuggestions({
        show: true,
        loading: false,
        mode: mode,
        suggestions: response.data.suggestions,
        sampleOrders: response.data.sample_orders || [],
      });
      
      if (response.data.suggestions?.stats?.total_matches === 0) {
        toast.info("No similar transport orders found. Try creating manual entry.");
      }
    } catch (error) {
      console.error("Error fetching trucking suggestions:", error);
      setTruckingSuggestions(prev => ({ ...prev, loading: false }));
      toast.error("Failed to get trucking rate suggestions");
    }
  };

  // Add trucking rate from suggestions to offer
  const addTruckingFromSuggestion = (suggestedRate, adjustmentPercent = 0) => {
    if (!truckingSuggestions.suggestions) return;
    
    const finalRate = suggestedRate * (1 + adjustmentPercent / 100);
    const mode = truckingSuggestions.mode;
    const serviceType = offerBuilder?.quoteRequest?.service_type || 'fcl';
    
    const newComponent = {
      component_id: `trucking_${Date.now()}`,
      name: mode === 'precarriage' ? 'Pre-carriage (Trucking)' : 'On-carriage (Trucking)',
      category: 'inland',
      quantity: 1,
      unit_price: Math.round(finalRate * 1000) / 1000,
      currency: 'EUR',
      unit: serviceType === 'fcl' ? 'container' : 'shipment',
      description: `${truckingSuggestions.suggestions.origin} → ${truckingSuggestions.suggestions.destination}`,
      notes: `Based on ${truckingSuggestions.suggestions.stats.total_matches} similar orders (avg €${truckingSuggestions.suggestions.stats.avg_total_price})`,
      is_included: true,
    };
    
    setOfferBuilder(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }));
    
    setTruckingSuggestions(prev => ({ ...prev, show: false }));
    toast.success(`Added ${mode === 'precarriage' ? 'Pre-carriage' : 'On-carriage'} to offer`);
  };

  // Clone an existing offer
  const cloneOffer = async (offer) => {
    try {
      // Fetch full offer details if needed
      const response = await axios.get(`${API_URL}/pricing/offers/${offer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullOffer = response.data.offer || response.data;
      
      // Create offer builder from the offer
      const clonedBuilder = {
        quoteRequest: {
          id: null,
          quote_number: "CLONE",
          service_type: fullOffer.service_type,
          company_name: fullOffer.customer_company,
          contact_name: fullOffer.customer_contact,
          email: fullOffer.customer_email,
          origin_city: fullOffer.origin,
          destination_city: fullOffer.destination,
          container_type: fullOffer.container_type,
          container_quantity: fullOffer.container_quantity,
          commodity: fullOffer.commodity,
        },
        components: (fullOffer.components || []).map((c, idx) => ({
          ...c,
          component_id: `clone_${Date.now()}_${idx}`,
        })),
        matchingRateCards: [],
        categories: [],
        currency: fullOffer.currency || "EUR",
        validityDays: fullOffer.validity_days || 14,
        incoterm: fullOffer.incoterm || "",
        transitTime: fullOffer.transit_time || "",
        routing: fullOffer.routing || "",
        vesselSchedule: fullOffer.vessel_schedule || "",
        paymentTerms: fullOffer.payment_terms || "30 days from B/L date",
        specialConditions: fullOffer.special_conditions || "",
        internalNotes: fullOffer.internal_notes || "",
        isStandalone: true,
        isClone: true,
        clonedFrom: fullOffer.offer_number,
        standaloneData: {
          customer_company: fullOffer.customer_company,
          customer_contact: fullOffer.customer_contact,
          customer_email: fullOffer.customer_email,
          customer_phone: fullOffer.customer_phone || "",
          service_type: fullOffer.service_type,
          incoterm: fullOffer.incoterm,
          pol: fullOffer.origin,
          pod: fullOffer.destination,
          container_type: fullOffer.container_type,
          container_quantity: fullOffer.container_quantity || 1,
          commodity: fullOffer.commodity,
        },
      };
      
      setOfferBuilder(clonedBuilder);
      setSelectedQuote(clonedBuilder.quoteRequest);
      // Load comment sections if saved
      if (fullOffer.comment_sections?.length) {
        setCommentSections(fullOffer.comment_sections.map((s, i) => ({
          id: s.id || i + 1,
          title: s.title || `Comment Section ${i + 1}`,
          text: s.text || "",
          images: s.images || [],
        })));
      }
      setActiveTab("offers");
      toast.success(`Cloned offer ${fullOffer.offer_number}`);
    } catch (error) {
      console.error("Error cloning offer:", error);
      toast.error("Failed to clone offer");
    }
  };

  // Edit an existing draft offer
  const editOffer = async (offer) => {
    try {
      // Fetch full offer details
      const response = await axios.get(`${API_URL}/pricing/offers/${offer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fullOffer = response.data.offer || response.data;
      
      // Create offer builder from the offer for editing
      const editBuilder = {
        quoteRequest: {
          id: fullOffer.quote_id || null,
          quote_number: fullOffer.quote_number || "DIRECT",
          service_type: fullOffer.service_type,
          company_name: fullOffer.customer_company,
          contact_name: fullOffer.customer_contact,
          email: fullOffer.customer_email,
          origin_city: fullOffer.origin,
          destination_city: fullOffer.destination,
          container_type: fullOffer.container_type,
          container_quantity: fullOffer.container_quantity,
          commodity: fullOffer.commodity,
        },
        components: (fullOffer.components || []).map((c, idx) => ({
          ...c,
          component_id: c.component_id || `edit_${Date.now()}_${idx}`,
        })),
        matchingRateCards: [],
        categories: [],
        currency: fullOffer.currency || "EUR",
        validityDays: fullOffer.validity_days || 14,
        incoterm: fullOffer.incoterm || "",
        transitTime: fullOffer.transit_time || "",
        routing: fullOffer.routing || "",
        vesselSchedule: fullOffer.vessel_schedule || "",
        paymentTerms: fullOffer.payment_terms || "30 days from B/L date",
        specialConditions: fullOffer.special_conditions || "",
        internalNotes: fullOffer.internal_notes || "",
        cargo: fullOffer.cargo || {
          weight_kg: fullOffer.total_weight_kg || "",
          volume_cbm: fullOffer.total_volume_cbm || "",
          value: fullOffer.cargo_value || "",
          value_currency: fullOffer.cargo_value_currency || "EUR",
        },
        isStandalone: !fullOffer.quote_id,
        isEditing: true,
        existingOfferId: offer.id,
        existingOfferNumber: fullOffer.offer_number,
        standaloneData: {
          customer_company: fullOffer.customer_company,
          customer_contact: fullOffer.customer_contact,
          customer_email: fullOffer.customer_email,
          customer_phone: fullOffer.customer_phone || "",
          service_type: fullOffer.service_type,
          incoterm: fullOffer.incoterm,
          pol: fullOffer.origin,
          pod: fullOffer.destination,
          container_type: fullOffer.container_type,
          container_quantity: fullOffer.container_quantity || 1,
          commodity: fullOffer.commodity,
        },
      };
      
      setOfferBuilder(editBuilder);
      setSelectedQuote(editBuilder.quoteRequest);
      // Load comment sections if saved
      if (fullOffer.comment_sections?.length) {
        setCommentSections(fullOffer.comment_sections.map((s, i) => ({
          id: s.id || i + 1,
          title: s.title || `Comment Section ${i + 1}`,
          text: s.text || "",
          images: s.images || [],
        })));
      }
      setActiveTab("offers");
      toast.success(`Editing offer ${fullOffer.offer_number || offer.offer_number}`);
    } catch (error) {
      console.error("Error loading offer for editing:", error);
      toast.error("Failed to load offer for editing");
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [quotesRes, offersRes, statsRes, rateCardsRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/quotes?status=pending`, { headers }),
        axios.get(`${API_URL}/pricing/offers`, { headers }),
        axios.get(`${API_URL}/pricing/stats`, { headers }),
        axios.get(`${API_URL}/pricing/rate-cards`, { headers }),
        axios.get(`${API_URL}/pricing/service-templates`, { headers }),
      ]);
      
      setQuotes(quotesRes.data.quotes || quotesRes.data || []);
      setOffers(offersRes.data.offers || []);
      setStats(statsRes.data);
      setRateCards(rateCardsRes.data || []);
      setServiceTemplates(servicesRes.data.templates || []);
      setServiceCategories(servicesRes.data.categories || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load pricing data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch LCL Import regions when switching to that tab
  const fetchLclImportRegions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pricing/lcl-import/regions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLclImport(prev => ({ ...prev, regions: response.data || [] }));
    } catch (error) {
      console.error("Error fetching LCL import regions:", error);
    }
  }, [token]);

  // Fetch LCL Import rate cards
  const fetchLclImportRateCards = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pricing/lcl-import/rate-cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLclImport(prev => ({ ...prev, rateCards: response.data || [] }));
    } catch (error) {
      console.error("Error fetching LCL import rate cards:", error);
    }
  }, [token]);

  // Search LCL Import ports
  const searchLclImportPorts = useCallback(async (query) => {
    if (query.length < 2) {
      setLclImport(prev => ({ ...prev, searchResults: [] }));
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/pricing/lcl-import/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLclImport(prev => ({ ...prev, searchResults: response.data || [] }));
    } catch (error) {
      console.error("Error searching LCL import ports:", error);
    }
  }, [token]);

  // Compare LCL Import rates for a specific origin
  const compareLclImportRates = useCallback(async (origin) => {
    setLclImport(prev => ({ ...prev, loading: true, comparison: null }));
    try {
      const response = await axios.get(`${API_URL}/pricing/lcl-import/compare?origin=${encodeURIComponent(origin)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLclImport(prev => ({ ...prev, loading: false, comparison: response.data }));
    } catch (error) {
      console.error("Error comparing LCL import rates:", error);
      setLclImport(prev => ({ ...prev, loading: false }));
      toast.error("Failed to compare rates");
    }
  }, [token]);

  // Seed Sinergy Cargo rates (admin only)
  const seedSinergyRates = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/pricing/lcl-import/seed-sinergy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchLclImportRegions();
      fetchLclImportRateCards();
    } catch (error) {
      console.error("Error seeding Sinergy rates:", error);
      toast.error("Failed to seed Sinergy rates");
    }
  }, [token, fetchLclImportRegions, fetchLclImportRateCards]);

  // Fetch LCL import data when switching to that tab
  useEffect(() => {
    if (activeTab === "lcl-import") {
      fetchLclImportRegions();
      fetchLclImportRateCards();
    }
  }, [activeTab, fetchLclImportRegions, fetchLclImportRateCards]);

  const startOffer = async (quote) => {
    try {
      const response = await axios.get(`${API_URL}/pricing/quotes/${quote.id}/auto-populate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOfferBuilder({
        quoteRequest: response.data.quote_request,
        components: response.data.suggested_components,
        matchingRateCards: response.data.matching_rate_cards,
        categories: response.data.categories,
        // Offer metadata
        currency: "EUR",
        validityDays: 14,
        incoterm: quote.incoterm || "",
        transitTime: "",
        routing: "",
        vesselSchedule: "",
        paymentTerms: "30 days from B/L date",
        specialConditions: "",
        internalNotes: "",
        isStandalone: false,
      });
      
      setSelectedQuote(quote);
      toast.success("Offer builder loaded with suggested components");
    } catch (error) {
      toast.error("Failed to start offer builder");
    }
  };

  // Quote Reminder Functions
  const openReminderModal = (quote) => {
    setReminderQuote(quote);
    // Default to 3 days from now
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 3);
    setReminderDate(defaultDate.toISOString().split('T')[0]);
    setReminderNote("");
  };

  const setQuoteReminder = async () => {
    if (!reminderQuote || !reminderDate) return;
    
    setSendingReminder(true);
    try {
      await axios.post(
        `${API_URL}/quotes/${reminderQuote.id}/set-reminder`,
        { 
          reminder_date: reminderDate,
          reminder_note: reminderNote 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Reminder set for ${new Date(reminderDate).toLocaleDateString()}`);
      setReminderQuote(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to set reminder");
    } finally {
      setSendingReminder(false);
    }
  };

  const sendReminderNow = async (quote) => {
    try {
      await axios.post(
        `${API_URL}/quotes/${quote.id}/send-reminder-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reminder email sent to customer!");
    } catch (error) {
      toast.error("Failed to send reminder");
    }
  };

  // Mark a pending quote as "offer sent by email" — moves it out of Pending.
  const markQuoteEmailed = async (quote) => {
    if (!window.confirm(`Mark ${quote.quote_number} as "offer sent by email"? It will move out of Pending Quotes.`)) return;
    try {
      await axios.post(
        `${API_URL}/quotes/${quote.id || quote.quote_number}/mark-emailed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${quote.quote_number} marked as sent by email`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as sent");
    }
  };

  // Open the Email Offer Archive pre-filtered by this customer.
  const findInOfferArchive = (quote) => {
    const term = quote.company_name || quote.customer_name || quote.contact_name || quote.email || "";
    navigate(`/offer-archive?q=${encodeURIComponent(term)}`);
  };

  // Delete a quote request
  const handleDeleteQuote = async (quote) => {
    if (!window.confirm(`Are you sure you want to delete quote ${quote.quote_number}?`)) return;
    
    try {
      const quoteId = quote.id || quote._id;
      await axios.delete(
        `${API_URL}/quotes/${quoteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Quote deleted successfully");
      // Remove from local state immediately for better UX
      setQuotes(prev => prev.filter(q => (q.id || q._id) !== quoteId));
      // Also refresh stats
      fetchData();
    } catch (error) {
      console.error("Delete quote error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  // Delete an offer
  const handleDeleteOffer = async (offer) => {
    if (!window.confirm(`Are you sure you want to delete offer ${offer.offer_number}?`)) return;
    
    try {
      const offerId = offer.id || offer._id;
      await axios.delete(
        `${API_URL}/offers/${offerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Offer deleted successfully");
      // Remove from local state immediately for better UX
      setOffers(prev => prev.filter(o => (o.id || o._id) !== offerId));
    } catch (error) {
      console.error("Delete offer error:", error);
      toast.error(error.response?.data?.detail || "Failed to delete offer");
    }
  };

  // Open assign task modal for a quote
  const openAssignTaskModal = (quote) => {
    setAssignTaskModal({ show: true, quote, loading: false });
    setSelectedStaff("");
    setTaskNote("");
    setTaskDueDate("");
  };

  // Handle task assignment
  const handleAssignTask = async () => {
    if (!selectedStaff || !assignTaskModal.quote) {
      toast.error("Please select a staff member");
      return;
    }

    const selectedStaffMember = staffList.find(s => s.email === selectedStaff);
    if (!selectedStaffMember) {
      toast.error("Invalid staff member");
      return;
    }

    setAssignTaskModal(prev => ({ ...prev, loading: true }));

    try {
      const quote = assignTaskModal.quote;
      await axios.post(
        `${API_URL}/staff-tasks`,
        {
          title: `Process quote request: ${quote.quote_number}`,
          description: taskNote || `Quote from ${quote.company_name} - ${quote.origin_city} to ${quote.destination_city}`,
          source: "quote_request",
          reference: quote.quote_number,
          assigned_to: selectedStaff,
          assigned_to_name: selectedStaffMember.full_name || selectedStaffMember.email,
          due_date: taskDueDate || null,
          priority: "normal",
          metadata: {
            quote_id: quote.id,
            quote_number: quote.quote_number,
            company_name: quote.company_name,
            origin: `${quote.origin_city}, ${quote.origin_country}`,
            destination: `${quote.destination_city}, ${quote.destination_country}`
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Task assigned to ${selectedStaffMember.full_name || selectedStaff}`);
      setAssignTaskModal({ show: false, quote: null, loading: false });
    } catch (error) {
      console.error("Failed to assign task:", error);
      toast.error("Failed to assign task");
      setAssignTaskModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Parse email for pricing enquiry
  const handlePricingEmailDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEmailDragOver(false);
    
    const files = e.dataTransfer?.files || e.target?.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    const filename = file.name.toLowerCase();
    
    if (!filename.endsWith('.msg') && !filename.endsWith('.eml')) {
      toast.error("Please drop a .msg or .eml email file");
      return;
    }
    
    setParsingEmail(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/pricing/parse-enquiry-email`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      if (response.data.success) {
        const data = response.data.extracted_data;
        
        // Map service type
        let serviceType = 'fcl';
        if (data.service_type) {
          const st = data.service_type.toLowerCase();
          if (st.includes('lcl')) serviceType = 'lcl';
          else if (st.includes('air')) serviceType = 'air';
          else if (st.includes('inland') || st.includes('truck')) serviceType = 'inland';
          else if (st.includes('fcl') || st.includes('container')) serviceType = 'fcl';
        }
        
        // Map containers
        let containers = [{ id: 1, type: "40HC", quantity: 1 }];
        if (data.containers && data.containers.length > 0) {
          containers = data.containers.map((c, idx) => ({
            id: idx + 1,
            type: c.type || "40HC",
            quantity: c.quantity || 1
          }));
        } else if (data.container_type) {
          containers = [{ id: 1, type: data.container_type, quantity: data.container_quantity || 1 }];
        }
        
        // Pre-populate the form
        // For AIR shipments, map poa to pol if pol is empty (poa is pickup, which becomes AOL)
        let polValue = data.pol || '';
        let podValue = data.pod || '';
        let poaValue = data.poa || '';
        let pofdValue = data.pofd || '';
        
        // Use pallets from backend if available
        let parsedPallets = data.pallets && data.pallets.length > 0 
          ? data.pallets.map((p, idx) => ({
              qty: p.qty || 1,
              length: p.length_cm?.toString() || '',
              width: p.width_cm?.toString() || '',
              height: p.height_cm?.toString() || '',
              weight: p.weight_kg?.toString() || '',
              stackable: p.stackable || false,
            }))
          : standaloneData.pallets;
        
        // Air freight: if pol is empty but poa has a value, use poa as the origin
        if (serviceType === 'air' && !polValue && poaValue) {
          polValue = poaValue;
          poaValue = ''; // Clear POA since we moved it to POL (AOL)
        }
        
        // Extract weight and volume from backend response
        let airCargoWeight = data.weight_kg?.toString() || '';
        let airCargoVolume = data.volume_cbm?.toString() || '';
        
        // Fallback: Parse weight from string if weight_kg not available
        if (!airCargoWeight && data.weight) {
          const weightMatch = data.weight.match(/([\d.,]+)\s*kg/i);
          if (weightMatch) {
            airCargoWeight = weightMatch[1].replace(',', '.');
          }
        }
        
        // Fallback: Calculate volume from pallet dimensions if volume_cbm not available
        if (!airCargoVolume && parsedPallets.length > 0 && parsedPallets[0].length) {
          const p = parsedPallets[0];
          const volumeCbm = (parseFloat(p.length) * parseFloat(p.width) * parseFloat(p.height) * parseInt(p.qty || 1)) / 1000000;
          airCargoVolume = volumeCbm.toFixed(3);
        }
        
        setStandaloneData(prev => ({
          ...prev,
          customer_company: data.customer_company || prev.customer_company,
          customer_contact: data.customer_contact || prev.customer_contact,
          customer_email: data.customer_email || prev.customer_email,
          customer_phone: data.customer_phone || prev.customer_phone,
          service_type: serviceType,
          incoterm: data.incoterm || prev.incoterm,
          poa: poaValue,
          pol: polValue,
          pod: podValue,
          pofd: pofdValue,
          containers: containers,
          container_type: containers[0]?.type || "40HC",
          container_quantity: containers[0]?.quantity || 1,
          commodity: data.commodity || data.remarks || prev.commodity,
          cargo_ready_date: data.cargo_ready_date || prev.cargo_ready_date,
          is_imo: data.is_imo || data.is_hazardous || prev.is_imo,
          // Air cargo specific fields
          air_cargo: serviceType === 'air' ? {
            weight_kg: airCargoWeight,
            volume_cbm: airCargoVolume,
            chargeable_weight: '',
          } : prev.air_cargo,
          pallets: serviceType === 'air' || serviceType === 'lcl' ? parsedPallets : prev.pallets,
        }));
        
        // Show more informative toast for air freight
        const routeDisplay = serviceType === 'air' 
          ? `${polValue || poaValue || '?'} → ${podValue || '?'}` 
          : `${data.pol || '?'} → ${data.pod || '?'}`;
        toast.success(`Email parsed! Customer: ${data.customer_company || 'Unknown'}, Route: ${routeDisplay}`);
        
        // Check if contact needs to be created
        if (response.data.suggested_contact?.needs_confirmation) {
          setPendingContact({
            ...response.data.suggested_contact,
            suggested_company: response.data.crm_contact?.suggested_company,
            suggested_company_id: response.data.crm_contact?.suggested_company_id,
          });
          setShowContactModal(true);
        } else if (response.data.crm_contact?.exists) {
          toast.info(`Contact found: ${response.data.crm_contact.contact_name}`);
        }
      } else {
        toast.error("Failed to extract data from email");
      }
    } catch (error) {
      console.error("Email parsing error:", error);
      toast.error(error.response?.data?.detail || "Failed to parse email");
    } finally {
      setParsingEmail(false);
    }
  };

  // Save confirmed CRM contact from email parsing
  const saveConfirmedContact = async () => {
    if (!pendingContact?.email) {
      toast.error("No contact to save");
      return;
    }
    
    setSavingContact(true);
    try {
      await axios.post(
        `${API_URL}/crm/contacts`,
        {
          name: pendingContact.name,
          email: pendingContact.email,
          company_name: pendingContact.company_name,
          phone: pendingContact.phone,
          status: "active",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Contact "${pendingContact.name}" added to CRM`);
      setShowContactModal(false);
      setPendingContact(null);
    } catch (error) {
      console.error("Failed to save contact:", error);
      toast.error(error.response?.data?.detail || "Failed to save contact");
    } finally {
      setSavingContact(false);
    }
  };

  // Start a standalone offer (from scratch)
  const startStandaloneOffer = async () => {
    // Validate required fields
    if (!standaloneData.customer_company || !standaloneData.customer_contact || !standaloneData.customer_email) {
      toast.error("Please fill in customer details");
      return;
    }
    if (!standaloneData.pol || !standaloneData.pod) {
      toast.error("Please fill in POL and POD");
      return;
    }

    try {
      // Fetch component templates for the service type
      const response = await axios.get(
        `${API_URL}/pricing/component-templates?service_type=${standaloneData.service_type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Create default components based on templates
      const defaultComponents = response.data
        .filter(t => t.category === "freight" || t.category === "origin_charges" || t.category === "destination_charges")
        .slice(0, 5)
        .map((t, idx) => ({
          component_id: `new_${Date.now()}_${idx}`,
          name: t.name,
          category: t.category,
          description: "",
          quantity: standaloneData.container_quantity || 1,
          unit_price: t.default_rate || 0,
          currency: "EUR",
          unit: t.unit,
          notes: "",
          is_included: true,
          source_rate_card_id: null,
          position: idx,
        }));

      // If a CMA SpotOn rate was applied, build the offer as SEPARATE lines
      // (each with a comment) so the sales rep can keep/delete as needed:
      // Ocean Freight (+margin) · each destination charge · 3 EP service fees.
      if (standaloneData.cma_applied || standaloneData.cma_sell_rate) {
        // Drop any template-default Ocean Freight line to avoid a duplicate.
        for (let i = defaultComponents.length - 1; i >= 0; i--) {
          if (String(defaultComponents[i].name).toLowerCase().includes("ocean freight")) {
            defaultComponents.splice(i, 1);
          }
        }
        const qty = standaloneData.container_quantity || 1;
        const cur = standaloneData.cma_currency || standaloneData.cma_cost_currency || "USD";
        const marginLabel = standaloneData.cma_margin_type === 'percent'
          ? `${standaloneData.cma_margin_value}%`
          : `${standaloneData.cma_margin_value} ${cur}`;
        const inclNote = (standaloneData.cma_included_charges || []).join(" / ");
        const routeDesc = [
          "CMA CGM SpotOn",
          standaloneData.cma_service,
          standaloneData.cma_vessel,
          standaloneData.cma_transit_days ? `${standaloneData.cma_transit_days}d transit` : null,
        ].filter(Boolean).join(" · ");

        const cmaLines = [];
        // 1) Ocean Freight (basic + margin)
        cmaLines.push({
          name: "Ocean Freight",
          category: "freight",
          description: routeDesc,
          quantity: qty,
          unit_price: standaloneData.cma_ocean_freight_sell || standaloneData.cma_sell_rate || 0,
          currency: cur,
          unit: "container",
          notes: `CMA cost ${standaloneData.cma_ocean_freight ?? standaloneData.cma_cost_rate} ${cur} + ${marginLabel} margin.${inclNote ? ` Incl: ${inclNote}` : ""}`,
        });
        // 2) Each destination / import charge as its own line (pass-through cost)
        (standaloneData.cma_additional_charges || []).forEach((ch) => {
          cmaLines.push({
            name: ch.label || "Destination charge",
            category: "destination_charges",
            description: `CMA — payable at import${ch.payment_method ? ` (${ch.payment_method})` : ""}`,
            quantity: qty,
            unit_price: ch.amount || 0,
            currency: ch.currency || cur,
            unit: "container",
            notes: (ch.pivot_amount != null && ch.pivot_currency && ch.pivot_currency !== ch.currency)
              ? `≈ ${ch.pivot_amount} ${ch.pivot_currency}` : "",
          });
        });
        // 3) Europacific standard service charges (EUR) — keep/delete as needed
        [
          { name: "Bill of Lading (B/L) fee", price: 50, unit: "B/L", note: "+/BL" },
          { name: "FOB handling", price: 50, unit: "B/L", note: "+/FOB handling per B/L" },
          { name: "Administration fee", price: 10, unit: "invoice", note: "+/admin per invoice" },
        ].forEach((s) => cmaLines.push({
          name: s.name,
          category: "origin_charges",
          description: s.note,
          quantity: 1,
          unit_price: s.price,
          currency: "EUR",
          unit: s.unit,
          notes: "Europacific standard service",
        }));

        // Prepend, preserving order (reverse so unshift keeps sequence).
        cmaLines.reverse().forEach((l, i) => defaultComponents.unshift({
          component_id: `cma_${Date.now()}_${i}`,
          is_included: true,
          source_rate_card_id: null,
          position: 0,
          ...l,
        }));
        defaultComponents.forEach((c, i) => { c.position = i; });
      }
      // Build routing string
      const routingParts = [];
      if (standaloneData.poa) routingParts.push(`POA: ${standaloneData.poa}`);
      if (standaloneData.origin_cc) routingParts.push(`C/C: ${standaloneData.origin_cc}`);
      if (standaloneData.pol) routingParts.push(`POL: ${standaloneData.pol}${standaloneData.pol_code ? ` (${standaloneData.pol_code})` : ''}`);
      if (standaloneData.pod) routingParts.push(`POD: ${standaloneData.pod}${standaloneData.pod_code ? ` (${standaloneData.pod_code})` : ''}`);
      if (standaloneData.dest_cc) routingParts.push(`C/C: ${standaloneData.dest_cc}`);
      if (standaloneData.pofd) routingParts.push(`POFD: ${standaloneData.pofd}${standaloneData.pofd_code ? ` (${standaloneData.pofd_code})` : ''}`);

      // Build cargo description for LCL
      let cargoDescription = standaloneData.commodity;
      if (standaloneData.service_type === 'lcl' && standaloneData.pallets?.length > 0) {
        const palletDetails = standaloneData.pallets
          .filter(p => p.length && p.width && p.height)
          .map(p => `${p.qty}x pallet: ${p.length}×${p.width}×${p.height} cm, ${p.weight || '?'} kg`)
          .join('\n');
        if (palletDetails) cargoDescription = `${cargoDescription}\n${palletDetails}`;
      }

      // Create a pseudo quote request for the offer builder
      const pseudoQuote = {
        id: null,
        quote_number: "DIRECT",
        service_type: standaloneData.service_type,
        company_name: standaloneData.customer_company,
        contact_name: standaloneData.customer_contact,
        email: standaloneData.customer_email,
        // Routing fields for display
        poa: standaloneData.poa,
        origin_city: standaloneData.poa || standaloneData.pol,
        origin_country: "",
        origin_port: standaloneData.pol,
        pol: standaloneData.pol,
        pol_code: standaloneData.pol_code,
        pod: standaloneData.pod,
        pod_code: standaloneData.pod_code,
        pofd: standaloneData.pofd,
        destination_city: standaloneData.pofd || standaloneData.pod,
        destination_country: "",
        destination_port: standaloneData.pod,
        container_type: standaloneData.container_type,
        container_quantity: standaloneData.container_quantity,
        commodity: cargoDescription,
        cargo_description: standaloneData.cargo_description,
        cargo_ready_date: standaloneData.cargo_ready_date,
        is_imo: standaloneData.is_imo,
        pallets: standaloneData.pallets,
      };

      setOfferBuilder({
        quoteRequest: pseudoQuote,
        components: defaultComponents,
        matchingRateCards: [],
        categories: [],
        currency: "EUR",
        validityDays: 14,
        incoterm: standaloneData.incoterm,
        transitTime: standaloneData.cma_transit_days ? `${standaloneData.cma_transit_days} days` : "",
        routing: routingParts.join(' → '),
        vesselSchedule: standaloneData.carrier_routing_info || "",
        carrierRoutingInfo: standaloneData.carrier_routing_info || "",
        paymentTerms: "30 days from B/L date",
        specialConditions: "",
        internalNotes: "",
        isStandalone: true,
        standaloneData: standaloneData,
        // Air freight data from form - pre-populate
        airWeight: standaloneData.air_cargo?.weight_kg || "",
        airVolume: standaloneData.air_cargo?.volume_cbm || "",
        airCW: standaloneData.air_cargo?.chargeable_weight || "",
        airRatePerKg: "",
        airCarrier: "",
        airTransitDays: "",
        cargo: {
          weight_kg: standaloneData.total_weight_kg || standaloneData.air_cargo?.weight_kg || "",
          volume_cbm: standaloneData.total_volume_cbm || standaloneData.air_cargo?.volume_cbm || "",
          value: standaloneData.cargo_value || "",
          value_currency: standaloneData.cargo_value_currency || "EUR",
        },
      });

      setSelectedQuote(pseudoQuote);
      setShowStandaloneForm(false);
      // Clear consumed CMA fields so a subsequent offer doesn't reuse a stale rate.
      setStandaloneData(prev => ({
        ...prev,
        cma_applied: undefined, cma_currency: undefined,
        cma_ocean_freight: undefined, cma_ocean_freight_sell: undefined, cma_all_in: undefined,
        cma_additional_charges: undefined, cma_included_charges: undefined,
        cma_sell_rate: undefined, cma_cost_rate: undefined, cma_cost_currency: undefined,
        cma_margin_type: undefined, cma_margin_value: undefined,
        cma_carrier: undefined, cma_service: undefined, cma_vessel: undefined, cma_transit_days: undefined,
      }));
      toast.success("Standalone offer builder opened");
    } catch (error) {
      console.error("Error starting standalone offer:", error);
      toast.error("Failed to start standalone offer");
    }
  };

  const saveOffer = async (asDraft = true) => {
    if (!offerBuilder) return;
    
    try {
      let response;
      
      // Common ocean freight fields
      const oceanFreightFields = {
        carrier: offerBuilder.carrier,
        transit_days: offerBuilder.transitDays,
        transitDays: offerBuilder.transitDays,
        transhipment: offerBuilder.transhipment,
        direct_service: offerBuilder.directService,
        directService: offerBuilder.directService,
        ofr_valid_until: offerBuilder.ofrValidUntil,
        ofrValidUntil: offerBuilder.ofrValidUntil,
      };
      
      // Air freight fields
      const airFreightFields = {
        airWeight: offerBuilder.airWeight,
        airVolume: offerBuilder.airVolume,
        airCW: offerBuilder.airCW,
        airRatePerKg: offerBuilder.airRatePerKg,
        airCarrier: offerBuilder.airCarrier,
        airTransitDays: offerBuilder.airTransitDays,
      };

      // Cargo metrics fields (weight, CBM, W/M, value, insurance settings)
      const cargoMetrics = computeCargoMetrics(offerBuilder.cargo);
      const cargoPayload = {
        total_weight_kg: cargoMetrics.weightKg || null,
        total_volume_cbm: cargoMetrics.cbm || null,
        cargo_value: cargoMetrics.value || null,
        cargo_value_currency: cargoMetrics.valueCurrency,
        cargo: {
          ...(offerBuilder.cargo || {}),
          computed_cbm: cargoMetrics.cbm,
          computed_wm: cargoMetrics.wm,
          computed_cw: cargoMetrics.cw,
        },
      };
      
      // Check if we're updating an existing offer draft (e.g., from quote request)
      if (offerBuilder.existingOfferId) {
        // Update the existing offer draft using PATCH
        const payload = {
          components: offerBuilder.components.map((c, idx) => ({
            ...c,
            position: idx,
            total: rawComponentAmount(c, offerBuilder.components)
          })),
          currency: offerBuilder.currency,
          validity_days: offerBuilder.validityDays,
          incoterm: offerBuilder.incoterm,
          transit_time: offerBuilder.transitTime,
          routing: offerBuilder.routing,
          vessel_schedule: offerBuilder.vesselSchedule,
          payment_terms: offerBuilder.paymentTerms,
          special_conditions: offerBuilder.specialConditions,
          internal_notes: offerBuilder.internalNotes,
          ...cargoPayload,
          ...oceanFreightFields,
          ...airFreightFields,
        };
        
        response = await axios.patch(`${API_URL}/pricing/offers/${offerBuilder.existingOfferId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(`Offer ${response.data.offer_number} updated!`);
      } else if (offerBuilder.isStandalone) {
        // Save standalone offer
        const payload = {
          ...offerBuilder.standaloneData,
          components: offerBuilder.components.map((c, idx) => ({
            ...c,
            position: idx,
            total: rawComponentAmount(c, offerBuilder.components)
          })),
          currency: offerBuilder.currency,
          validity_days: offerBuilder.validityDays,
          incoterm: offerBuilder.incoterm,
          transit_time: offerBuilder.transitTime,
          routing: offerBuilder.routing,
          vessel_schedule: offerBuilder.vesselSchedule,
          payment_terms: offerBuilder.paymentTerms,
          special_conditions: offerBuilder.specialConditions,
          internal_notes: offerBuilder.internalNotes,
          comment_sections: commentSections.map(s => ({
            id: s.id,
            title: s.title,
            text: s.text,
            images: s.images,
          })),
          ...cargoPayload,
          ...oceanFreightFields,
          ...airFreightFields,
        };
        
        response = await axios.post(`${API_URL}/pricing/offers/standalone`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Offer ${response.data.offer_number} created!`);
      } else {
        // Save regular offer (linked to quote)
        if (!selectedQuote) return;
        
        const payload = {
          quote_request_id: selectedQuote.id,
          components: offerBuilder.components.map((c, idx) => ({
            ...c,
            position: idx,
            total: rawComponentAmount(c, offerBuilder.components)
          })),
          currency: offerBuilder.currency,
          validity_days: offerBuilder.validityDays,
          incoterm: offerBuilder.incoterm,
          transit_time: offerBuilder.transitTime,
          routing: offerBuilder.routing,
          vessel_schedule: offerBuilder.vesselSchedule,
          payment_terms: offerBuilder.paymentTerms,
          special_conditions: offerBuilder.specialConditions,
          internal_notes: offerBuilder.internalNotes,
          comment_sections: commentSections.map(s => ({
            id: s.id,
            title: s.title,
            text: s.text,
            images: s.images,
          })),
          ...cargoPayload,
          ...oceanFreightFields,
          ...airFreightFields,
        };
        
        response = await axios.post(`${API_URL}/pricing/offers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Offer ${response.data.offer_number} created!`);
      }
      
      // Delete the draft after successful save
      if (offerBuilder.draftId) {
        deleteDraft(offerBuilder.draftId);
      }
      
      if (!asDraft) {
        // Send immediately
        const offerId = offerBuilder.existingOfferId || response.data.id;
        await axios.post(`${API_URL}/pricing/offers/${offerId}/send`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Offer sent to customer!");
      }
      
      setOfferBuilder(null);
      setSelectedQuote(null);
      fetchData();
    } catch (error) {
      let errorMsg = "Failed to save offer";
      try {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(e => typeof e === 'string' ? e : (e.msg || JSON.stringify(e))).join(', ');
        } else if (typeof detail === 'object' && detail !== null) {
          errorMsg = JSON.stringify(detail);
        }
      } catch (e) {
        // Keep default error message
      }
      toast.error(errorMsg);
    }
  };

  const sendOffer = async (offerId) => {
    try {
      await axios.post(`${API_URL}/pricing/offers/${offerId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offer sent to customer!");
      setPreviewOffer(null);
      setPreviewQuote(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to send offer");
    }
  };

  const viewOffer = async (offer) => {
    try {
      const response = await axios.get(`${API_URL}/pricing/offers/${offer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewOffer(response.data.offer);
      // For standalone offers, quote_request may be null - create a synthetic one
      const qr = response.data.quote_request || {
        company_name: response.data.offer?.customer_company || '',
        contact_name: response.data.offer?.customer_contact || '',
        email: response.data.offer?.customer_email || '',
        service_type: response.data.offer?.service_type || 'fcl',
        origin_city: (response.data.offer?.origin || '').split(',')[0] || '',
        destination_city: (response.data.offer?.destination || '').split(',')[0] || '',
      };
      setPreviewQuote(qr);
    } catch (error) {
      toast.error("Failed to load offer");
    }
  };

  const previewCurrentOffer = () => {
    if (!offerBuilder || !selectedQuote) return;
    
    // Create a preview object from the current builder state
    const now = new Date();
    const validUntil = new Date(now);
    validUntil.setDate(validUntil.getDate() + offerBuilder.validityDays);
    
    const previewOfferData = {
      id: "preview",
      offer_number: "PREVIEW",
      status: "draft",
      components: offerBuilder.components,
      subtotal: calculateTotal(),
      currency: offerBuilder.currency,
      validity_days: offerBuilder.validityDays,
      valid_until: validUntil.toISOString(),
      validity_date: validUntil.toISOString(),
      incoterm: offerBuilder.incoterm,
      transit_time: offerBuilder.transitTime,
      routing: offerBuilder.routing,
      vessel_schedule: offerBuilder.vesselSchedule,
      payment_terms: offerBuilder.paymentTerms,
      special_conditions: offerBuilder.specialConditions,
      internal_notes: offerBuilder.internalNotes,
      service_type: offerBuilder.quoteRequest?.service_type,
      // Ocean Freight Details
      carrier: offerBuilder.carrier,
      transitDays: offerBuilder.transitDays,
      transit_days: offerBuilder.transitDays,
      transhipment: offerBuilder.transhipment,
      directService: offerBuilder.directService,
      direct_service: offerBuilder.directService,
      ofrValidUntil: offerBuilder.ofrValidUntil,
      ofr_valid_until: offerBuilder.ofrValidUntil,
      // Air Freight Details
      airWeight: offerBuilder.airWeight,
      airVolume: offerBuilder.airVolume,
      airCW: offerBuilder.airCW,
      airRatePerKg: offerBuilder.airRatePerKg,
      airCarrier: offerBuilder.airCarrier,
      airTransitDays: offerBuilder.airTransitDays,
      // Cargo metrics
      cargo: {
        ...(offerBuilder.cargo || {}),
        computed_cbm: computeCargoMetrics(offerBuilder.cargo).cbm,
        computed_wm: computeCargoMetrics(offerBuilder.cargo).wm,
        computed_cw: computeCargoMetrics(offerBuilder.cargo).cw,
      },
      total_weight_kg: computeCargoMetrics(offerBuilder.cargo).weightKg || null,
      total_volume_cbm: computeCargoMetrics(offerBuilder.cargo).cbm || null,
      cargo_value: computeCargoMetrics(offerBuilder.cargo).value || null,
      cargo_value_currency: computeCargoMetrics(offerBuilder.cargo).valueCurrency,
      comment_sections: commentSections,
      created_at: now.toISOString(),
    };
    
    setPreviewOffer(previewOfferData);
    setPreviewQuote(offerBuilder.quoteRequest);
  };

  const updateComponent = (index, field, value) => {
    setOfferBuilder(prev => {
      const updated = [...prev.components];
      updated[index] = { ...updated[index], [field]: value };
      // When a charge basis changes, auto-fill quantity from cargo metrics
      if (field === "unit") {
        const m = computeCargoMetrics(prev.cargo);
        if (value === "w/m" && m.wm) {
          updated[index].quantity = Math.round(m.wm * 1000) / 1000;
        } else if (value === "cbm" && m.cbm) {
          updated[index].quantity = Math.round(m.cbm * 1000) / 1000;
        } else if (value === "kg" && m.cw) {
          updated[index].quantity = Math.round(m.cw * 100) / 100;
        } else if (isPercentUnit(value)) {
          // Default the base line to the first freight line (or first other line)
          if (!updated[index].base_line_id) {
            const freight = updated.find((c, i) => i !== index && c.category === "freight");
            const fallback = updated.find((c, i) => i !== index && !isPercentUnit(c.unit));
            const base = freight || fallback;
            if (base) {
              updated[index].base_line_id = base.component_id;
              updated[index].currency = base.currency || updated[index].currency;
            }
          }
          updated[index].quantity = 1;
        }
      }
      // Recompute totals for all lines (percentage lines depend on their base)
      const recomputed = updated.map((c) => ({ ...c, total: rawComponentAmount(c, updated) }));
      return { ...prev, components: recomputed };
    });
  };

  // Update cargo details on the offer being built
  const updateCargo = (field, value) => {
    setOfferBuilder(prev => {
      const cargo = { ...(prev.cargo || {}), [field]: value };
      // Auto-refresh quantities on lines whose unit is driven by cargo metrics
      const m = computeCargoMetrics(cargo);
      const updated = (prev.components || []).map((c) => {
        let q = c.quantity;
        const u = normUnit(c.unit);
        if (u === "w/m" && m.wm) q = Math.round(m.wm * 1000) / 1000;
        else if (u === "cbm" && m.cbm) q = Math.round(m.cbm * 1000) / 1000;
        else if (u === "kg" && m.cw) q = Math.round(m.cw * 100) / 100;
        return { ...c, quantity: q };
      });
      const recomputed = updated.map((c) => ({ ...c, total: rawComponentAmount(c, updated) }));
      return { ...prev, cargo, components: recomputed };
    });
  };

  // Add an auto-calculated insurance line from cargo value
  const addInsuranceLine = () => {
    setOfferBuilder(prev => {
      const m = computeCargoMetrics(prev.cargo);
      if (!m.value) {
        toast.error("Enter a cargo value first");
        return prev;
      }
      const premium = Math.max((m.value * m.insuranceRate) / 100, m.insuranceMin);
      const comp = {
        component_id: `insurance_${Date.now()}`,
        name: "Cargo Insurance",
        category: "insurance",
        description: `All-risk insurance ${m.insuranceRate}% of cargo value ${m.value.toLocaleString()} ${m.valueCurrency}${m.insuranceMin ? ` (min ${m.insuranceMin})` : ""}`,
        quantity: 1,
        unit_price: Math.round(premium * 100) / 100,
        currency: m.valueCurrency,
        unit: "shipment",
        is_included: true,
        is_custom: true,
        total: Math.round(premium * 100) / 100,
        position: (prev.components || []).length,
      };
      toast.success("Insurance line added");
      return { ...prev, components: [...(prev.components || []), comp] };
    });
  };

  // Move a component to another charge group. We splice it out and push it to
  // the end so framer-motion's Reorder sees a clean add (fixes the dropdown
  // "jumping back" when changing a line's group).
  const moveComponentToCategory = (index, newCategory) => {
    setOfferBuilder(prev => {
      const comps = [...prev.components];
      const [moved] = comps.splice(index, 1);
      moved.category = newCategory;
      comps.push(moved);
      return { ...prev, components: comps.map((c, i) => ({ ...c, position: i })) };
    });
  };

  // Category display order for grouping the pricing components.
  const CATEGORY_ORDER = ["freight", "origin_charges", "destination_charges", "customs", "inland", "surcharges", "documentation", "insurance", "warehousing", "other"];
  const catRank = (c) => {
    const i = CATEGORY_ORDER.indexOf(c);
    return i === -1 ? 999 : i;
  };

  // Components stably sorted into their category groups (single flat list so
  // framer-motion can drag a row across group boundaries).
  const orderedComponents = () => {
    if (!offerBuilder) return [];
    return [...offerBuilder.components]
      .map((c, i) => ({ c, i }))
      .sort((a, b) => (catRank(a.c.category) - catRank(b.c.category)) || (a.i - b.i))
      .map(x => x.c);
  };

  // Actively-dragged component id (set on drag start; category adoption is
  // applied only on drag END to avoid framer-motion's per-swap onReorder ticks
  // corrupting neighbours).
  const [draggingId, setDraggingId] = useState(null);

  // Normalize the underlying array to grouped display order, then flag drag.
  const startDrag = (id) => {
    setOfferBuilder(prev => {
      const sorted = [...prev.components]
        .map((c, i) => ({ c, i }))
        .sort((a, b) => (catRank(a.c.category) - catRank(b.c.category)) || (a.i - b.i))
        .map((x, idx) => ({ ...x.c, position: idx }));
      return { ...prev, components: sorted };
    });
    setDraggingId(id);
  };

  // During drag: pure reorder of the flat list (no category change).
  const handleFlatReorder = (newOrderIds) => {
    setOfferBuilder(prev => {
      const map = {};
      prev.components.forEach(c => { map[c.component_id] = c; });
      const reordered = newOrderIds
        .map(id => map[id])
        .filter(Boolean)
        .map((c, idx) => ({ ...c, position: idx }));
      return { ...prev, components: reordered };
    });
  };

  // On drop: the dragged row adopts the category of its resting neighbour,
  // then the list is re-grouped. One clean move, direction-independent.
  const endDrag = (id) => {
    setOfferBuilder(prev => {
      const comps = [...prev.components];
      const pos = comps.findIndex(c => c.component_id === id);
      if (pos !== -1) {
        const neighbor = pos > 0 ? comps[pos - 1] : comps[pos + 1];
        const newCat = neighbor ? neighbor.category : comps[pos].category;
        if (newCat) comps[pos] = { ...comps[pos], category: newCat };
      }
      const regrouped = comps
        .map((c, i) => ({ c, i }))
        .sort((a, b) => (catRank(a.c.category) - catRank(b.c.category)) || (a.i - b.i))
        .map((x, idx) => ({ ...x.c, position: idx }));
      return { ...prev, components: regrouped };
    });
    setDraggingId(null);
  };

  const removeComponent = (index) => {
    setOfferBuilder(prev => ({
      ...prev,
      components: prev.components.filter((_, i) => i !== index)
    }));
  };

  const addComponent = (template) => {
    const newComponent = {
      component_id: `new_${Date.now()}`,
      name: template.name,
      category: template.category,
      description: "",
      quantity: 1,
      unit_price: template.default_rate || 0,
      currency: offerBuilder.currency,
      unit: template.unit,
      notes: "",
      is_included: true,
      source_rate_card_id: null,
      position: offerBuilder.components.length,
    };
    
    setOfferBuilder(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }));
  };

  const calculateTotal = () => {
    if (!offerBuilder) return 0;
    const displayCurrency = offerBuilder.currency || "EUR";
    return offerBuilder.components
      .filter(c => c.is_included)
      .reduce((sum, c) => {
        const componentCurrency = c.currency || displayCurrency;
        const amount = rawComponentAmount(c, offerBuilder.components);
        // Convert to display currency
        const convertedAmount = convertCurrency(amount, componentCurrency, displayCurrency);
        return sum + convertedAmount;
      }, 0);
  };

  // Calculate component total in display currency (for showing conversion)
  const getComponentTotalInDisplayCurrency = (comp) => {
    if (!offerBuilder) return 0;
    const displayCurrency = offerBuilder.currency || "EUR";
    const componentCurrency = comp.currency || displayCurrency;
    const amount = rawComponentAmount(comp, offerBuilder.components);
    return convertCurrency(amount, componentCurrency, displayCurrency);
  };

  // Add custom service to offer
  const addCustomService = async () => {
    if (!newService.name) {
      toast.error("Please enter a service name");
      return;
    }
    
    const component = {
      component_id: `custom_${Date.now()}`,
      name: newService.name,
      category: newService.category,
      description: newService.description,
      quantity: newService.quantity,
      unit_price: newService.unit_price,
      currency: newService.currency,
      unit: newService.unit,
      notes: newService.notes,
      is_included: true,
      is_custom: true,
      position: offerBuilder.components.length,
    };
    
    setOfferBuilder(prev => ({
      ...prev,
      components: [...prev.components, component]
    }));
    
    // Save as template if checkbox is checked
    if (newService.saveAsTemplate) {
      try {
        await axios.post(
          `${API_URL}/pricing/component-templates`,
          {
            name: newService.name,
            category: newService.category,
            description: newService.description,
            default_rate: newService.unit_price,
            currency: newService.currency,
            unit: newService.unit,
            notes: newService.notes,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Service added & saved as template for future use`);
      } catch (error) {
        console.error("Error saving template:", error);
        toast.success("Service added (template save failed)");
      }
    } else {
      toast.success("Service added");
    }
    
    // Reset form
    setNewService({
      name: "",
      description: "",
      category: "freight",
      quantity: 1,
      unit_price: 0,
      currency: offerBuilder.currency || "EUR",
      unit: "container",
      notes: "",
      saveAsTemplate: false,
    });
    setShowAddServiceModal(false);
  };

  const groupedComponents = () => {
    if (!offerBuilder) return {};
    const groups = {};
    offerBuilder.components.forEach(comp => {
      if (!groups[comp.category]) {
        groups[comp.category] = [];
      }
      groups[comp.category].push(comp);
    });
    return groups;
  };

  // Filter quotes
  const filteredQuotes = quotes.filter(q => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      if (!q.quote_number?.toLowerCase().includes(search) &&
          !q.company_name?.toLowerCase().includes(search) &&
          !q.origin_city?.toLowerCase().includes(search) &&
          !q.destination_city?.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  // Filter offers
  const filteredOffers = offers.filter(o => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      if (!o.offer_number?.toLowerCase().includes(search) &&
          !o.customer_company?.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  return (
    <OffersModuleLayout>
      <main className="pb-12 px-4 sm:px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between pt-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="pricing-title">
              Pricing & Quotes
            </h1>
            <p className="text-slate-600 mt-1">Create and manage customer offers</p>
          </div>
          <Button
            onClick={() => setShowStandaloneForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-standalone-offer-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Offer from Scratch
          </Button>
        </div>

        {/* Standalone Offer Form Modal */}
        <AnimatePresence>
          {showStandaloneForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center overflow-y-auto py-8"
              onClick={() => setShowStandaloneForm(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        <Plus className="h-5 w-5" />
                        Create Offer from Scratch
                      </h2>
                      <p className="text-emerald-100 text-sm mt-1">
                        Create a direct offer without a customer quote request
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowStandaloneForm(false)}
                      className="text-white hover:bg-white/20"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                  {/* Email Drop Zone */}
                  <div
                    onDrop={handlePricingEmailDrop}
                    onDragOver={(e) => { e.preventDefault(); setEmailDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setEmailDragOver(false); }}
                    className={`relative border-2 border-dashed rounded-xl p-4 text-center transition-all cursor-pointer ${
                      emailDragOver
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50 bg-slate-50"
                    }`}
                    onClick={() => document.getElementById('pricing-email-input')?.click()}
                  >
                    {parsingEmail ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-emerald-600 font-medium text-sm">Parsing enquiry email with AI...</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Mail className="h-5 w-5 text-slate-400" />
                          <ArrowRight className="h-4 w-4 text-slate-300" />
                          <FileText className="h-5 w-5 text-emerald-500" />
                        </div>
                        <p className="text-slate-700 font-medium text-sm">
                          Drop an enquiry email or click to browse
                        </p>
                        <p className="text-xs text-slate-500">
                          .msg or .eml • Extracts customer, routing, cargo details
                        </p>
                        <input
                          id="pricing-email-input"
                          type="file"
                          accept=".msg,.eml"
                          onChange={handlePricingEmailDrop}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          data-testid="pricing-email-upload"
                        />
                      </>
                    )}
                  </div>

                  {/* Customer Details */}
                  <div className="space-y-4">
                    {/* Pantheon Company Selector */}
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                      <PantheonCompanySelector
                        value={standaloneData.pantheon_company}
                        onChange={(company) => {
                          setStandaloneData(prev => ({ 
                            ...prev, 
                            pantheon_company: company,
                            customer_company: company?.name || '',
                            crm_company_id: company?.id || null
                          }));
                        }}
                        placeholder="Search Pantheon companies..."
                        required={true}
                        showGroupOption={false}
                      />
                      <div className="mt-3 p-2.5 bg-emerald-100/50 rounded-lg border border-emerald-200">
                        <p className="text-xs text-emerald-700">
                          <strong>Required:</strong> Select a company from Pantheon list. If not found, register a new company.
                        </p>
                      </div>
                    </div>

                    {/* Contact Details */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-500" />
                        Contact Details
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-slate-500">Company Name</label>
                          <Input
                            value={standaloneData.customer_company}
                            onChange={(e) => !standaloneData.pantheon_company && setStandaloneData(prev => ({ ...prev, customer_company: e.target.value }))}
                            placeholder={standaloneData.pantheon_company ? "Auto-filled from Pantheon" : "Select company above"}
                            className={`mt-1 ${standaloneData.pantheon_company ? 'bg-slate-50 text-slate-600' : ''}`}
                            disabled={!!standaloneData.pantheon_company}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Contact Person *</label>
                          <Input
                            value={standaloneData.customer_contact}
                            onChange={(e) => setStandaloneData(prev => ({ ...prev, customer_contact: e.target.value }))}
                            placeholder="John Smith"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Email *</label>
                          <Input
                            type="email"
                            value={standaloneData.customer_email}
                            onChange={(e) => setStandaloneData(prev => ({ ...prev, customer_email: e.target.value }))}
                            placeholder="john@company.com"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Phone</label>
                          <Input
                            value={standaloneData.customer_phone}
                            onChange={(e) => setStandaloneData(prev => ({ ...prev, customer_phone: e.target.value }))}
                            placeholder="+386 1 234 5678"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Service Type & Incoterm */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Service Type & Incoterm</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[
                        { value: "fcl", label: "FCL", icon: Ship },
                        { value: "lcl", label: "LCL", icon: Package },
                        { value: "air", label: "Air", icon: Plane },
                        { value: "inland", label: "Inland", icon: Truck },
                        { value: "multimodal", label: "Multimodal", icon: Layers },
                      ].map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          onClick={() => setStandaloneData(prev => ({ ...prev, service_type: value }))}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                            standaloneData.service_type === value
                              ? "bg-blue-50 border-blue-500 text-blue-700"
                              : "border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500">Incoterm *</label>
                        <select
                          value={standaloneData.incoterm}
                          onChange={(e) => setStandaloneData(prev => ({ ...prev, incoterm: e.target.value }))}
                          className="w-full mt-1 rounded-lg border-slate-200 text-sm p-2"
                        >
                          <option value="EXW">EXW - Ex Works</option>
                          <option value="FCA">FCA - Free Carrier</option>
                          <option value="CPT">CPT - Carriage Paid To</option>
                          <option value="CIP">CIP - Carriage & Insurance Paid</option>
                          <option value="DAP">DAP - Delivered at Place</option>
                          <option value="DPU">DPU - Delivered at Place Unloaded</option>
                          <option value="DDP">DDP - Delivered Duty Paid</option>
                          <option value="FAS">FAS - Free Alongside Ship</option>
                          <option value="FOB">FOB - Free on Board</option>
                          <option value="CFR">CFR - Cost and Freight</option>
                          <option value="CIF">CIF - Cost, Insurance & Freight</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Routing - Full Logistics Chain */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <ArrowRight className="h-4 w-4 text-slate-500" />
                      Routing
                    </h3>
                    <div className="space-y-3">
                      {/* POA - Place of Acceptance */}
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                          <span className="font-medium text-green-600">POA</span>
                          Place of Acceptance
                        </label>
                        <SmartLocationInput
                          value={{
                            city: standaloneData.poa,
                            country: standaloneData.poa_country || "",
                            port_code: standaloneData.poa_code,
                            port_name: standaloneData.poa,
                          }}
                          onChange={(loc) => setStandaloneData(prev => ({
                            ...prev,
                            poa: loc.city || loc.port_name || "",
                            poa_code: loc.port_code || "",
                            poa_country: loc.country || "",
                          }))}
                          showSeaport={true}
                          showAirport={standaloneData.service_type === 'air'}
                          placeholder="Search city or port..."
                          cmaSearch={true}
                          data-testid="poa-smart-input"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id="origin_cc"
                            checked={!!standaloneData.origin_cc}
                            onChange={(e) => setStandaloneData(prev => ({ 
                              ...prev, 
                              origin_cc: e.target.checked ? standaloneData.poa || "Origin" : "" 
                            }))}
                            className="rounded"
                          />
                          <label htmlFor="origin_cc" className="text-xs text-slate-600">C/C Origin</label>
                        </div>
                      </div>

                      {/* POL/AOL */}
                      <div className="p-3 bg-green-50 rounded-lg">
                        <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                          <span className="font-medium text-green-700">
                            {standaloneData.service_type === 'air' ? 'AOL' : 'POL'} *
                          </span>
                          {standaloneData.service_type === 'air' ? 'Airport of Loading' : 'Port of Loading'}
                        </label>
                        <SmartLocationInput
                          value={{
                            city: standaloneData.pol,
                            country: standaloneData.pol_country || "",
                            port_code: standaloneData.pol_code,
                            port_name: standaloneData.pol,
                          }}
                          onChange={(loc) => setStandaloneData(prev => ({
                            ...prev,
                            pol: loc.city || loc.port_name || "",
                            pol_code: loc.port_code || loc.airport_code || "",
                            pol_country: loc.country || "",
                          }))}
                          showSeaport={standaloneData.service_type !== 'air'}
                          showAirport={standaloneData.service_type === 'air'}
                          placeholder={standaloneData.service_type === 'air' ? "Search airport..." : "Search port..."}
                          cmaSearch={standaloneData.service_type !== 'air'}
                          data-testid="pol-smart-input"
                        />
                      </div>

                      {/* POD/AOD */}
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                          <span className="font-medium text-blue-700">
                            {standaloneData.service_type === 'air' ? 'AOD' : 'POD'} *
                          </span>
                          {standaloneData.service_type === 'air' ? 'Airport of Delivery' : 'Port of Discharge'}
                        </label>
                        <SmartLocationInput
                          value={{
                            city: standaloneData.pod,
                            country: standaloneData.pod_country || "",
                            port_code: standaloneData.pod_code,
                            port_name: standaloneData.pod,
                          }}
                          onChange={(loc) => setStandaloneData(prev => ({
                            ...prev,
                            pod: loc.city || loc.port_name || "",
                            pod_code: loc.port_code || loc.airport_code || "",
                            pod_country: loc.country || "",
                          }))}
                          showSeaport={standaloneData.service_type !== 'air'}
                          showAirport={standaloneData.service_type === 'air'}
                          placeholder={standaloneData.service_type === 'air' ? "Search airport..." : "Search port..."}
                          cmaSearch={standaloneData.service_type !== 'air'}
                          data-testid="pod-smart-input"
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            type="checkbox"
                            id="dest_cc"
                            checked={!!standaloneData.dest_cc}
                            onChange={(e) => setStandaloneData(prev => ({ 
                              ...prev, 
                              dest_cc: e.target.checked ? standaloneData.pod || "Destination" : "" 
                            }))}
                            className="rounded"
                          />
                          <label htmlFor="dest_cc" className="text-xs text-slate-600">C/C Destination</label>
                        </div>
                      </div>

                      {/* POFD - Place of Final Delivery */}
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                          <span className="font-medium text-blue-600">POFD</span>
                          Place of Final Delivery
                        </label>
                        <SmartLocationInput
                          value={{
                            city: standaloneData.pofd,
                            country: standaloneData.pofd_country || "",
                            port_code: standaloneData.pofd_code,
                            port_name: standaloneData.pofd,
                          }}
                          onChange={(loc) => setStandaloneData(prev => ({
                            ...prev,
                            pofd: loc.city || loc.port_name || "",
                            pofd_code: loc.port_code || "",
                            pofd_country: loc.country || "",
                          }))}
                          showSeaport={true}
                          showAirport={standaloneData.service_type === 'air'}
                          placeholder="Search city or port..."
                          cmaSearch={true}
                          data-testid="pofd-smart-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Cargo Details */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4 text-slate-500" />
                      Cargo Details
                    </h3>
                    
                    {/* Multiple Containers Support - Hide for Air */}
                    {standaloneData.service_type !== 'air' && (
                    <div className="space-y-3">
                      {standaloneData.containers.map((container, idx) => (
                        <React.Fragment key={container.id}>
                        <div className="flex items-end gap-3 p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <label className="text-xs text-slate-500">Equipment Type {standaloneData.containers.length > 1 && `#${idx + 1}`}</label>
                            <select
                              value={container.type}
                              onChange={(e) => {
                                const newContainers = [...standaloneData.containers];
                                newContainers[idx].type = e.target.value;
                                // Update legacy field with first container
                                const container_type = newContainers[0].type;
                                setStandaloneData(prev => ({ ...prev, containers: newContainers, container_type }));
                              }}
                              className="w-full mt-1 rounded-lg border-slate-200 text-sm p-2"
                            >
                              <option value="20GP">20' Standard (20GP)</option>
                              <option value="40GP">40' Standard (40GP)</option>
                              <option value="40HC">40' High Cube (40HC)</option>
                              <option value="45HC">45' High Cube (45HC)</option>
                              <option value="20RF">20' Reefer (20RF)</option>
                              <option value="40RF">40' Reefer (40RF)</option>
                              <option value="40RHC">40' HC Reefer (40RHC)</option>
                              <option value="20OT">20' Open Top (20OT)</option>
                              <option value="40OT">40' Open Top (40OT)</option>
                              <option value="20FR">20' Flat Rack (20FR)</option>
                              <option value="40FR">40' Flat Rack (40FR)</option>
                              <option value="20PL">20' Platform (20PL)</option>
                              <option value="40PL">40' Platform (40PL)</option>
                              <option value="TANK">ISO Tank Container</option>
                              <option value="LCL">LCL (Loose Cargo)</option>
                            </select>
                          </div>
                          {/* In-Gauge / Out-of-Gauge for special containers */}
                          {['20OT','40OT','20FR','40FR','20PL','40PL'].includes(container.type) && (
                            <div className="flex-1">
                              <label className="text-xs text-slate-500">Gauge</label>
                              <select
                                value={container.gauge || 'in_gauge'}
                                onChange={(e) => {
                                  const newContainers = [...standaloneData.containers];
                                  newContainers[idx].gauge = e.target.value;
                                  setStandaloneData(prev => ({ ...prev, containers: newContainers }));
                                }}
                                className="w-full mt-1 rounded-lg border-slate-200 text-sm p-2"
                                data-testid={`gauge-select-${idx}`}
                              >
                                <option value="in_gauge">In-Gauge (IG)</option>
                                <option value="out_of_gauge">Out-of-Gauge (OOG)</option>
                              </select>
                            </div>
                          )}
                          <div className="w-24">
                            <label className="text-xs text-slate-500">Quantity</label>
                            <Input
                              type="number"
                              min="1"
                              value={container.quantity}
                              onChange={(e) => {
                                const newContainers = [...standaloneData.containers];
                                newContainers[idx].quantity = parseInt(e.target.value) || 1;
                                // Update legacy field with first container
                                const container_quantity = newContainers[0].quantity;
                                setStandaloneData(prev => ({ ...prev, containers: newContainers, container_quantity }));
                              }}
                              className="mt-1"
                            />
                          </div>
                          {standaloneData.containers.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newContainers = standaloneData.containers.filter((_, i) => i !== idx);
                                setStandaloneData(prev => ({ 
                                  ...prev, 
                                  containers: newContainers,
                                  container_type: newContainers[0].type,
                                  container_quantity: newContainers[0].quantity
                                }));
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        {/* OOG Dimensions - shown when Out-of-Gauge is selected */}
                        {container.gauge === 'out_of_gauge' && (
                          <div className="ml-2 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                            <p className="text-xs font-semibold text-amber-700">Out-of-Gauge Dimensions</p>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="text-xs text-slate-500">Length (cm)</label>
                                <Input type="number" placeholder="L" value={container.oog_length || ''} onChange={(e) => {
                                  const nc = [...standaloneData.containers]; nc[idx].oog_length = e.target.value;
                                  setStandaloneData(prev => ({...prev, containers: nc}));
                                }} className="mt-1" data-testid={`oog-length-${idx}`} />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Width (cm)</label>
                                <Input type="number" placeholder="W" value={container.oog_width || ''} onChange={(e) => {
                                  const nc = [...standaloneData.containers]; nc[idx].oog_width = e.target.value;
                                  setStandaloneData(prev => ({...prev, containers: nc}));
                                }} className="mt-1" data-testid={`oog-width-${idx}`} />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Height (cm)</label>
                                <Input type="number" placeholder="H" value={container.oog_height || ''} onChange={(e) => {
                                  const nc = [...standaloneData.containers]; nc[idx].oog_height = e.target.value;
                                  setStandaloneData(prev => ({...prev, containers: nc}));
                                }} className="mt-1" data-testid={`oog-height-${idx}`} />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-slate-500">Gross Weight (kg)</label>
                                <Input type="number" placeholder="Weight" value={container.oog_weight || ''} onChange={(e) => {
                                  const nc = [...standaloneData.containers]; nc[idx].oog_weight = e.target.value;
                                  setStandaloneData(prev => ({...prev, containers: nc}));
                                }} className="mt-1" data-testid={`oog-weight-${idx}`} />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">OOG Notes</label>
                                <Input placeholder="Overhang details..." value={container.oog_notes || ''} onChange={(e) => {
                                  const nc = [...standaloneData.containers]; nc[idx].oog_notes = e.target.value;
                                  setStandaloneData(prev => ({...prev, containers: nc}));
                                }} className="mt-1" data-testid={`oog-notes-${idx}`} />
                              </div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                      ))}
                      
                      {/* Add Container Button */}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newId = Math.max(...standaloneData.containers.map(c => c.id)) + 1;
                          setStandaloneData(prev => ({
                            ...prev,
                            containers: [...prev.containers, { id: newId, type: "20GP", quantity: 1 }]
                          }));
                        }}
                        className="w-full border-dashed border-slate-300 text-slate-600 hover:border-slate-400"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Another Container Type
                      </Button>
                      
                      {standaloneData.containers.length > 1 && (
                        <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                          Multiple container types: Each will need separate pre/on-carriage pricing in the offer builder
                        </p>
                      )}
                    </div>
                    )}

                    {/* Other Cargo Details */}
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div>
                        <label className="text-xs text-slate-500">Cargo Ready Date</label>
                        <Input
                          type="date"
                          value={standaloneData.cargo_ready_date}
                          onChange={(e) => setStandaloneData(prev => ({ ...prev, cargo_ready_date: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={standaloneData.is_imo}
                            onChange={(e) => setStandaloneData(prev => ({ ...prev, is_imo: e.target.checked }))}
                            className="rounded"
                          />
                          <span className="text-xs text-slate-600">{standaloneData.service_type === 'air' ? 'DG Cargo' : 'IMO / DG Cargo'}</span>
                        </label>
                      </div>
                      <div></div>
                    </div>
                    <div className="mt-3">
                      <label className="text-xs text-slate-500">Commodity</label>
                      <input
                        list="cma-commodity-list"
                        value={standaloneData.commodity}
                        onChange={(e) => {
                          const label = e.target.value;
                          const match = commodityList.find((c) => c.label.toLowerCase() === label.toLowerCase());
                          setStandaloneData(prev => ({ ...prev, commodity: label, commodity_code: match ? match.code : "FAK" }));
                        }}
                        placeholder="Freight All Kinds — type to search commodity…"
                        className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="commodity-input"
                      />
                      <datalist id="cma-commodity-list">
                        {commodityList.map((c) => <option key={c.code + c.label} value={c.label} />)}
                      </datalist>
                      <p className="text-[11px] text-slate-400 mt-1">
                        Used for the CMA live rate (defaults to Freight All Kinds).
                      </p>
                    </div>

                    {/* Cargo Description Free Text */}
                    <div className="mt-3">
                      <label className="text-xs text-slate-500">Cargo Description / Data</label>
                      <textarea
                        value={standaloneData.cargo_description}
                        onChange={(e) => setStandaloneData(prev => ({ ...prev, cargo_description: e.target.value }))}
                        placeholder="Enter detailed cargo data, special handling instructions, dimensions, packaging details..."
                        className="w-full mt-1 rounded-lg border border-slate-200 text-sm p-3 min-h-[80px] resize-y focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        rows="3"
                        data-testid="cargo-description-textarea"
                      />
                      <p className="text-xs text-slate-400 mt-1">Free text for additional cargo information</p>
                    </div>

                    {/* CMA CGM Live Rate cross-check (FCL only) — fires
                        only after POL/POD/container/cargo_ready_date are
                        filled. Lets the sales rep compare their manual
                        offer against CMA SpotOn and one-click apply the
                        live rate as the purchase/cost component. */}
                    {standaloneData.service_type !== 'air' && (
                      <CMASpotOnPanel
                        standaloneData={standaloneData}
                        onApplyAsCost={(amount, currency) => {
                          setStandaloneData(prev => ({
                            ...prev,
                            cma_cost_rate: amount,
                            cma_cost_currency: currency,
                          }));
                          toast.success(`Applied CMA rate ${amount} ${currency} as cost. Add your markup in the rate components step.`);
                        }}
                        onApplyWithMargin={(d) => {
                          setStandaloneData(prev => ({
                            ...prev,
                            cma_applied: true,
                            cma_currency: d.currency,
                            cma_ocean_freight: d.oceanFreight,
                            cma_ocean_freight_sell: d.oceanFreightSell,
                            cma_all_in: d.allIn,
                            cma_margin_type: d.marginType,
                            cma_margin_value: d.marginValue,
                            cma_additional_charges: d.additionalCharges,
                            cma_included_charges: d.includedCharges,
                            cma_carrier: d.carrier,
                            cma_service: d.service,
                            cma_vessel: d.vessel,
                            cma_transit_days: d.transitDays,
                            // legacy fields kept for backward compat
                            cma_sell_rate: d.oceanFreightSell,
                            cma_cost_rate: d.oceanFreight,
                            cma_cost_currency: d.currency,
                          }));
                          const marginLabel = d.marginType === 'percent' ? `${d.marginValue}%` : `${d.marginValue} ${d.currency}`;
                          const extras = (d.additionalCharges?.length || 0) + 3;
                          toast.success(`CMA rate applied → Ocean Freight ${d.oceanFreightSell} ${d.currency}/ctr (cost ${d.oceanFreight} + ${marginLabel}) + ${extras} charge line(s). Click "Proceed to Offer Builder".`);
                        }}
                      />
                    )}

                    {/* Port-Pair Search side-panel — appears for LCL offers,
                        gives sales reps a one-shot lookup of buy/sell/competitor
                        rates + agents + origin/dest charges without leaving the
                        offer dialog. */}
                    {standaloneData.service_type !== 'air' && (
                      <details className="mt-3 rounded-lg border border-blue-200 bg-blue-50/40" data-testid="port-pair-offer-dialog">
                        <summary className="cursor-pointer select-none px-4 py-2 text-sm font-semibold text-blue-800 flex items-center gap-2">
                          <Search className="h-4 w-4" />
                          LCL Rate Lookup — ratecards · charges · agents
                        </summary>
                        <div className="p-3">
                          <PortPairSearchPanel
                            compact
                            defaultPol={standaloneData.pol_code || standaloneData.pol || ""}
                            defaultPod={standaloneData.pod_code || "SIKOP"}
                          />
                        </div>
                      </details>
                    )}

                    {/* LCL Pallet Details */}
                    {standaloneData.service_type !== 'air' && (standaloneData.containers.some(c => c.type === 'LCL') || standaloneData.service_type === 'lcl') && (
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-medium text-amber-700">Pallet Details (LCL)</label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setStandaloneData(prev => ({
                              ...prev,
                              pallets: [...prev.pallets, { qty: 1, length: "", width: "", height: "", weight: "" }]
                            }))}
                            className="text-amber-600 h-6 text-xs"
                          >
                            + Add Pallet Type
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {standaloneData.pallets.map((pallet, idx) => (
                            <div key={idx} className="grid grid-cols-6 gap-2 items-end">
                              <div>
                                <label className="text-xs text-slate-500">Qty</label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={pallet.qty}
                                  onChange={(e) => {
                                    const newPallets = [...standaloneData.pallets];
                                    newPallets[idx].qty = parseInt(e.target.value) || 1;
                                    setStandaloneData(prev => ({ ...prev, pallets: newPallets }));
                                  }}
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">L (cm)</label>
                                <Input
                                  type="number"
                                  value={pallet.length}
                                  onChange={(e) => {
                                    const newPallets = [...standaloneData.pallets];
                                    newPallets[idx].length = e.target.value;
                                    setStandaloneData(prev => ({ ...prev, pallets: newPallets }));
                                  }}
                                  placeholder="120"
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">W (cm)</label>
                                <Input
                                  type="number"
                                  value={pallet.width}
                                  onChange={(e) => {
                                    const newPallets = [...standaloneData.pallets];
                                    newPallets[idx].width = e.target.value;
                                    setStandaloneData(prev => ({ ...prev, pallets: newPallets }));
                                  }}
                                  placeholder="80"
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">H (cm)</label>
                                <Input
                                  type="number"
                                  value={pallet.height}
                                  onChange={(e) => {
                                    const newPallets = [...standaloneData.pallets];
                                    newPallets[idx].height = e.target.value;
                                    setStandaloneData(prev => ({ ...prev, pallets: newPallets }));
                                  }}
                                  placeholder="120"
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">Weight (kg)</label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={pallet.weight}
                                  onChange={(e) => {
                                    const newPallets = [...standaloneData.pallets];
                                    newPallets[idx].weight = e.target.value;
                                    setStandaloneData(prev => ({ ...prev, pallets: newPallets }));
                                  }}
                                  placeholder="174.51"
                                  className="mt-1 bg-white"
                                />
                              </div>
                              {standaloneData.pallets.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newPallets = standaloneData.pallets.filter((_, i) => i !== idx);
                                    setStandaloneData(prev => ({ ...prev, pallets: newPallets }));
                                  }}
                                  className="text-red-500 h-8"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Air Cargo Note - Direct to Offer Builder */}
                    {standaloneData.service_type === 'air' && (
                      <div className="mt-4 p-3 bg-sky-50 rounded-lg border border-sky-200">
                        <p className="text-xs text-sky-700">
                          <span className="font-medium">Air Freight:</span> Weight, Volume, CW and Rate details will be entered in the Offer Builder.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setShowStandaloneForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={startStandaloneOffer}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="proceed-standalone-btn"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Proceed to Offer Builder
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.pending_quotes}</p>
                  <p className="text-xs text-slate-500">Pending Quotes</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Edit3 className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.draft_offers}</p>
                  <p className="text-xs text-slate-500">Draft Offers</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Send className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.sent_offers}</p>
                  <p className="text-xs text-slate-500">Sent Offers</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.accepted_offers}</p>
                  <p className="text-xs text-slate-500">Accepted</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-slate-200"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.conversion_rate}%</p>
                  <p className="text-xs text-slate-500">Conversion</p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Offer Builder Modal */}
        <AnimatePresence>
          {offerBuilder && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8"
              onClick={() => setOfferBuilder(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold flex items-center gap-2">
                        <Sparkles className="h-5 w-5" />
                        Offer Builder
                        {offerBuilder.isClone && (
                          <span className="text-xs bg-white/20 px-2 py-1 rounded ml-2">
                            Clone of {offerBuilder.clonedFrom}
                          </span>
                        )}
                      </h2>
                      <p className="text-blue-100 text-sm mt-1">
                        {offerBuilder.quoteRequest.quote_number} • {offerBuilder.quoteRequest.company_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* Autosave indicator */}
                      {lastAutoSave && (
                        <span className="text-xs text-blue-200 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Saved {lastAutoSave.toLocaleTimeString()}
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={saveOfferDraftLocally}
                        className="text-white hover:bg-white/20"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save Draft
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          // Save before closing
                          saveOfferDraftLocally();
                          setOfferBuilder(null);
                        }}
                        className="text-white hover:bg-white/20"
                      >
                        <X className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Route Info */}
                  <div className="flex items-center gap-4 mt-4 text-sm flex-wrap">
                    <span className="px-3 py-1 bg-white/20 rounded-full">
                      {offerBuilder.quoteRequest.service_type?.toUpperCase()}
                    </span>
                    <span className="flex items-center gap-2">
                      {offerBuilder.quoteRequest.origin_city || offerBuilder.standaloneData?.pol}, {offerBuilder.quoteRequest.origin_country}
                      <ArrowRight className="h-4 w-4" />
                      {offerBuilder.quoteRequest.destination_city || offerBuilder.standaloneData?.pod}, {offerBuilder.quoteRequest.destination_country}
                    </span>
                    {offerBuilder.quoteRequest.container_type && (
                      <span className="px-2 py-1 bg-white/10 rounded">
                        {offerBuilder.quoteRequest.container_quantity}x {offerBuilder.quoteRequest.container_type}
                      </span>
                    )}
                    {offerBuilder.incoterm && (
                      <span className="px-2 py-1 bg-green-500/30 rounded font-medium">
                        {offerBuilder.incoterm}
                      </span>
                    )}
                    {/* Currency Switcher */}
                    <div className="flex items-center gap-1 bg-white/20 rounded-lg px-1">
                      {["EUR", "USD", "GBP", "CHF"].map((curr) => (
                        <button
                          key={curr}
                          onClick={() => setOfferBuilder(prev => ({ ...prev, currency: curr }))}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                            offerBuilder.currency === curr
                              ? "bg-white text-blue-600"
                              : "text-white/80 hover:text-white hover:bg-white/10"
                          }`}
                        >
                          {curr}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 max-h-[70vh] overflow-y-auto">
                  {/* Components List */}
                  <div className="lg:col-span-2 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900">Pricing Components</h3>
                      <span className="text-sm text-slate-500">
                        Drag to reorder • Click to edit
                      </span>
                    </div>

                    {/* Cargo Details & Metrics */}
                    {(() => {
                      const m = computeCargoMetrics(offerBuilder.cargo);
                      const c = offerBuilder.cargo || {};
                      return (
                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200 space-y-3" data-testid="cargo-details-panel">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-indigo-800 flex items-center gap-2">
                              <Package className="h-4 w-4" /> Cargo Details & Metrics
                            </h4>
                            <span className="text-[10px] text-indigo-500 uppercase tracking-wide">drives W/M · CBM · CW</span>
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="text-xs text-slate-600">Weight (kg)</label>
                              <Input type="number" step="0.01" value={c.weight_kg || ""}
                                onChange={(e) => updateCargo("weight_kg", e.target.value)}
                                placeholder="e.g., 1200" className="mt-1 bg-white"
                                data-testid="cargo-weight-input" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Volume (CBM, direct)</label>
                              <Input type="number" step="0.001" value={c.volume_cbm || ""}
                                onChange={(e) => updateCargo("volume_cbm", e.target.value)}
                                placeholder="e.g., 8.5" className="mt-1 bg-white"
                                data-testid="cargo-cbm-input" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Pieces</label>
                              <Input type="number" step="1" value={c.pieces || ""}
                                onChange={(e) => updateCargo("pieces", e.target.value)}
                                placeholder="e.g., 10" className="mt-1 bg-white"
                                data-testid="cargo-pieces-input" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Vol. factor (kg/CBM)</label>
                              <Input type="number" step="1" value={c.volumetric_factor || ""}
                                onChange={(e) => updateCargo("volumetric_factor", e.target.value)}
                                placeholder="167" className="mt-1 bg-white"
                                data-testid="cargo-factor-input" />
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-3">
                            <div>
                              <label className="text-xs text-slate-600">Length (cm)</label>
                              <Input type="number" step="0.1" value={c.length_cm || ""}
                                onChange={(e) => updateCargo("length_cm", e.target.value)}
                                placeholder="L" className="mt-1 bg-white" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Width (cm)</label>
                              <Input type="number" step="0.1" value={c.width_cm || ""}
                                onChange={(e) => updateCargo("width_cm", e.target.value)}
                                placeholder="W" className="mt-1 bg-white" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Height (cm)</label>
                              <Input type="number" step="0.1" value={c.height_cm || ""}
                                onChange={(e) => updateCargo("height_cm", e.target.value)}
                                placeholder="H" className="mt-1 bg-white" />
                            </div>
                            <div className="flex flex-col justify-end">
                              <span className="text-[10px] text-slate-500">
                                {m.dimsCbm ? `Dims → ${m.dimsCbm.toFixed(3)} CBM` : "L×W×H×pcs → CBM"}
                              </span>
                            </div>
                          </div>
                          {/* Computed metrics */}
                          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-indigo-200">
                            <div className="bg-white rounded p-2 text-center">
                              <p className="text-[10px] text-slate-500 uppercase">CBM</p>
                              <p className="font-mono font-semibold text-indigo-700" data-testid="cargo-computed-cbm">{m.cbm.toFixed(3)}</p>
                            </div>
                            <div className="bg-white rounded p-2 text-center">
                              <p className="text-[10px] text-slate-500 uppercase">W/M</p>
                              <p className="font-mono font-semibold text-indigo-700" data-testid="cargo-computed-wm">{m.wm.toFixed(3)}</p>
                            </div>
                            <div className="bg-white rounded p-2 text-center">
                              <p className="text-[10px] text-slate-500 uppercase">CW (air)</p>
                              <p className="font-mono font-semibold text-indigo-700" data-testid="cargo-computed-cw">{m.cw.toFixed(2)}</p>
                            </div>
                          </div>
                          {/* Cargo value + insurance */}
                          <div className="grid grid-cols-4 gap-3 pt-2 border-t border-indigo-200 items-end">
                            <div>
                              <label className="text-xs text-slate-600">Cargo value</label>
                              <Input type="number" step="0.01" value={c.value || ""}
                                onChange={(e) => updateCargo("value", e.target.value)}
                                placeholder="e.g., 50000" className="mt-1 bg-white"
                                data-testid="cargo-value-input" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Currency</label>
                              <select value={c.value_currency || "EUR"}
                                onChange={(e) => updateCargo("value_currency", e.target.value)}
                                className="mt-1 w-full h-9 text-sm border border-slate-200 rounded bg-white px-2"
                                data-testid="cargo-value-currency">
                                {["EUR", "USD", "GBP", "CHF", "CNY"].map(cu => <option key={cu} value={cu}>{cu}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Insurance %</label>
                              <Input type="number" step="0.01" value={c.insurance_rate ?? ""}
                                onChange={(e) => updateCargo("insurance_rate", e.target.value)}
                                placeholder="0.2" className="mt-1 bg-white"
                                data-testid="cargo-insurance-rate" />
                            </div>
                            <div>
                              <Button type="button" size="sm" variant="outline"
                                onClick={addInsuranceLine}
                                className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                                data-testid="add-insurance-btn">
                                + Insurance {m.value ? `(${(Math.max((m.value * m.insuranceRate) / 100, m.insuranceMin)).toFixed(2)} ${m.valueCurrency})` : ""}
                              </Button>
                            </div>
                          </div>
                          <p className="text-[10px] text-indigo-500">
                            Set a line's unit to <b>/w/m</b>, <b>/cbm</b> or <b>/kg</b> to auto-fill its quantity from these metrics. Use unit <b>% of</b> for percentage charges (e.g. BAF).
                          </p>
                        </div>
                      );
                    })()}


                    {(() => {
                      const ordered = draggingId ? offerBuilder.components : orderedComponents();
                      let lastCat = null;
                      return (
                        <Reorder.Group
                          axis="y"
                          values={ordered.map(c => c.component_id)}
                          onReorder={handleFlatReorder}
                          className="space-y-2"
                        >
                          {ordered.map((comp) => {
                            const globalIdx = offerBuilder.components.findIndex(
                              c => c.component_id === comp.component_id
                            );
                            const showHeader = comp.category !== lastCat;
                            lastCat = comp.category;
                            return (
                              <React.Fragment key={comp.component_id}>
                                {showHeader && (
                                  <div
                                    className="flex items-center gap-2 text-sm font-medium pt-2"
                                    style={{ color: CATEGORY_COLORS[comp.category] || "#6B7280" }}
                                  >
                                    <div
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: CATEGORY_COLORS[comp.category] || "#6B7280" }}
                                    />
                                    {(comp.category || "other").replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                                  </div>
                                )}
                              <Reorder.Item
                                value={comp.component_id}
                                onDragStart={() => startDrag(comp.component_id)}
                                onDragEnd={() => endDrag(comp.component_id)}
                                className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                                  comp.is_included 
                                    ? "bg-white border-slate-200 shadow-sm hover:shadow-md" 
                                    : "bg-slate-50 border-slate-100 opacity-60"
                                }${comp.is_custom ? " border-l-4 border-l-purple-400" : ""}`}
                              >
                                <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                
                                <input
                                  type="checkbox"
                                  checked={comp.is_included}
                                  onChange={(e) => updateComponent(globalIdx, "is_included", e.target.checked)}
                                  className="h-4 w-4 rounded border-slate-300"
                                />
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={comp.name}
                                      onChange={(e) => updateComponent(globalIdx, "name", e.target.value)}
                                      className="flex-1 font-medium text-slate-900 bg-transparent border-none focus:ring-0 p-0"
                                    />
                                    {comp.is_custom && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Custom</span>
                                    )}
                                  </div>
                                  {/* Group / category selector — move this line between charge groups */}
                                  <div className="flex items-center gap-1 mt-1" onPointerDown={(e) => e.stopPropagation()}>
                                    <span className="text-[10px] uppercase tracking-wide text-slate-400">Group</span>
                                    <select
                                      value={comp.category || "other"}
                                      onChange={(e) => moveComponentToCategory(globalIdx, e.target.value)}
                                      data-testid={`component-category-select-${comp.component_id}`}
                                      className="h-6 text-xs border border-slate-200 rounded bg-slate-50 px-1 py-0 text-slate-600 focus:ring-1 focus:ring-purple-300 cursor-pointer"
                                      title="Move this line to another charge group"
                                    >
                                      {SERVICE_CATEGORIES.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                      ))}
                                    </select>
                                  </div>
                                  {comp.description && (
                                    <p className="text-xs text-slate-500 mt-0.5">{comp.description}</p>
                                  )}
                                  <input
                                    type="text"
                                    value={comp.notes || ""}
                                    onChange={(e) => updateComponent(globalIdx, "notes", e.target.value)}
                                    placeholder="Add note..."
                                    className="w-full text-xs text-slate-500 bg-transparent border-none focus:ring-0 p-0 mt-1 italic"
                                  />
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  {!isPercentUnit(comp.unit) && (
                                    <Input
                                      type="number"
                                      value={comp.quantity}
                                      onChange={(e) => updateComponent(globalIdx, "quantity", parseFloat(e.target.value) || 0)}
                                      className="w-16 h-8 text-center text-sm"
                                      step="0.001"
                                      min="0"
                                      data-testid={`component-qty-${comp.component_id}`}
                                    />
                                  )}
                                  <span className="text-slate-400">{isPercentUnit(comp.unit) ? "" : "×"}</span>
                                  <div className="relative">
                                    <Input
                                      type="number"
                                      value={comp.unit_price}
                                      onChange={(e) => updateComponent(globalIdx, "unit_price", parseFloat(e.target.value) || 0)}
                                      className={`h-8 text-right text-sm ${isPercentUnit(comp.unit) ? "w-16 pr-4" : "w-20"}`}
                                      step="0.001"
                                      data-testid={`component-price-${comp.component_id}`}
                                    />
                                    {isPercentUnit(comp.unit) && (
                                      <span className="absolute right-1 top-1.5 text-xs text-slate-400">%</span>
                                    )}
                                  </div>
                                  {/* Per-row currency selector */}
                                  <select
                                    value={comp.currency || offerBuilder.currency}
                                    onChange={(e) => updateComponent(globalIdx, "currency", e.target.value)}
                                    className="h-8 text-xs border-slate-200 rounded bg-slate-50 px-1"
                                  >
                                    <option value="EUR">EUR</option>
                                    <option value="USD">USD</option>
                                    <option value="GBP">GBP</option>
                                    <option value="CHF">CHF</option>
                                    <option value="CNY">CNY</option>
                                  </select>
                                  <select
                                    value={normUnit(comp.unit) || "container"}
                                    onChange={(e) => updateComponent(globalIdx, "unit", e.target.value)}
                                    data-testid={`component-unit-select-${comp.component_id}`}
                                    className="h-8 text-xs border border-slate-200 rounded bg-slate-50 px-1 w-20 text-slate-600 cursor-pointer"
                                    title="Charge basis / unit"
                                  >
                                    {(() => {
                                      const base = ["container", "cbm", "w/m", "kg", "shipment", "bl", "document", "pallet", "unit", "%"];
                                      const cur = normUnit(comp.unit);
                                      const opts = cur && !base.includes(cur) ? [cur, ...base] : base;
                                      return opts.map(u => (
                                        <option key={u} value={u}>{u === "%" ? "% of" : `/${u}`}</option>
                                      ));
                                    })()}
                                  </select>
                                  {/* Base-line picker for percentage lines */}
                                  {isPercentUnit(comp.unit) && (
                                    <select
                                      value={comp.base_line_id || ""}
                                      onChange={(e) => updateComponent(globalIdx, "base_line_id", e.target.value)}
                                      data-testid={`component-base-select-${comp.component_id}`}
                                      className="h-8 text-xs border border-amber-300 rounded bg-amber-50 px-1 w-28 text-amber-800 cursor-pointer"
                                      title="Base line for percentage"
                                    >
                                      <option value="">base…</option>
                                      {offerBuilder.components
                                        .filter(x => x.component_id !== comp.component_id && !isPercentUnit(x.unit))
                                        .map(x => (
                                          <option key={x.component_id} value={x.component_id}>{x.name?.slice(0, 20)}</option>
                                        ))}
                                    </select>
                                  )}
                                  {/* Show row total in row currency */}
                                  <span className="font-semibold text-slate-900 w-20 text-right" data-testid={`component-total-${comp.component_id}`}>
                                    {rawComponentAmount(comp, offerBuilder.components).toFixed(3)}
                                  </span>
                                  {/* Show converted amount if different currency */}
                                  {(comp.currency && comp.currency !== offerBuilder.currency) && (
                                    <span className="text-xs text-blue-600 w-20 text-right" title="Converted to display currency">
                                      ≈{getComponentTotalInDisplayCurrency(comp).toFixed(3)} {offerBuilder.currency}
                                    </span>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeComponent(globalIdx)}
                                    className="h-8 w-8 text-slate-400 hover:text-red-500"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </Reorder.Item>
                              </React.Fragment>
                            );
                          })}
                        </Reorder.Group>
                      );
                    })()}

                    {/* Add Component */}
                    <div className="pt-4 border-t space-y-3">
                      {/* Row 1: Search and Custom */}
                      <div className="flex gap-3 items-center">
                        <ComponentSelector 
                          serviceType={offerBuilder.quoteRequest.service_type}
                          onSelect={addComponent}
                          token={token}
                        />
                        <Button
                          variant="outline"
                          onClick={() => {
                            setNewService(prev => ({ ...prev, currency: offerBuilder.currency || "EUR" }));
                            setShowAddServiceModal(true);
                          }}
                          className="border-dashed border-purple-300 text-purple-600 hover:bg-purple-50 whitespace-nowrap shrink-0"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Custom
                        </Button>
                      </div>
                      
                      {/* Row 2: Transport buttons */}
                      {(offerBuilder.quoteRequest.service_type === 'fcl' || offerBuilder.quoteRequest.service_type === 'lcl' || offerBuilder.quoteRequest.service_type === 'inland') && (
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMetransLookup(prev => ({ ...prev, show: true, mode: 'precarriage' }))}
                            className="border-orange-300 text-orange-600 hover:bg-orange-50"
                            title="Rail + Truck Pre-carriage (Metrans)"
                          >
                            <Train className="h-4 w-4 mr-1" />
                            Pre-carriage
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => lookupTruckingSuggestions('precarriage')}
                            disabled={truckingSuggestions.loading}
                            className="border-orange-200 text-orange-500 hover:bg-orange-50 text-xs"
                            title="Suggest rate based on historical transport orders"
                          >
                            {truckingSuggestions.loading && truckingSuggestions.mode === 'precarriage' ? (
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <History className="h-3 w-3 mr-1" />
                            )}
                            Suggest
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMetransLookup(prev => ({ ...prev, show: true, mode: 'oncarriage' }))}
                            className="border-teal-300 text-teal-600 hover:bg-teal-50"
                            title="Rail + Truck On-carriage (Metrans)"
                          >
                            <Truck className="h-4 w-4 mr-1" />
                            On-carriage
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => lookupTruckingSuggestions('oncarriage')}
                            disabled={truckingSuggestions.loading}
                            className="border-teal-200 text-teal-500 hover:bg-teal-50 text-xs"
                            title="Suggest rate based on historical transport orders"
                          >
                            {truckingSuggestions.loading && truckingSuggestions.mode === 'oncarriage' ? (
                              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <History className="h-3 w-3 mr-1" />
                            )}
                            Suggest
                          </Button>
                        </div>
                      )}
                      
                      {/* Editable Port Pair (POL → POD) for rate lookups — port-to-port only, ignores EXW/DDP/DAP door legs */}
                      {offerBuilder.quoteRequest.service_type !== 'air' && (
                        <div className="flex items-end gap-2 flex-wrap" data-testid="builder-port-pair-edit">
                          <div className="flex flex-col">
                            <label className="text-[10px] uppercase tracking-wide text-slate-400">POL (port)</label>
                            <div className="w-52">
                              <PlaceInput
                                value={standaloneData.pol || offerBuilder.quoteRequest?.origin_port || offerBuilder.quoteRequest?.pol || ""}
                                kind="sea"
                                placeholder="e.g. Haiphong"
                                data-testid="builder-pol-input"
                                onSelect={(p) => setStandaloneData(prev => ({ ...prev, pol: p.code || p.name || "", pol_code: p.code || "" }))}
                              />
                            </div>
                          </div>
                          <span className="text-slate-400 pb-2">→</span>
                          <div className="flex flex-col">
                            <label className="text-[10px] uppercase tracking-wide text-slate-400">POD (port)</label>
                            <div className="w-52">
                              <PlaceInput
                                value={standaloneData.pod || offerBuilder.quoteRequest?.destination_port || offerBuilder.quoteRequest?.pod || ""}
                                kind="sea"
                                placeholder="e.g. Koper"
                                data-testid="builder-pod-input"
                                onSelect={(p) => setStandaloneData(prev => ({ ...prev, pod: p.code || p.name || "", pod_code: p.code || "" }))}
                              />
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-400 pb-2">Port-to-port only (door legs ignored)</span>
                        </div>
                      )}

                      {/* Rate Lookup Buttons (LCL rate cards + CMA SpotOn live) */}
                      {(offerBuilder.quoteRequest.service_type === 'lcl' || offerBuilder.quoteRequest.service_type === 'fcl') && (
                        <div className="flex gap-2 flex-wrap">
                          {offerBuilder.quoteRequest.service_type === 'lcl' && (
                            <Button
                              variant="outline"
                              onClick={lookupLCLRates}
                              disabled={lclRates.loading}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              data-testid="builder-lcl-lookup-btn"
                            >
                              {lclRates.loading ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Search className="h-4 w-4 mr-2" />
                              )}
                              LCL Rates (FLE + competition)
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            onClick={lookupCMABuilder}
                            disabled={cmaBuilder.loading}
                            className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                            data-testid="builder-cma-lookup-btn"
                          >
                            {cmaBuilder.loading ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Search className="h-4 w-4 mr-2" />
                            )}
                            CMA Live Rate
                          </Button>
                        </div>
                      )}

                      {cmaBuilder.error && (
                        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="builder-cma-error">
                          {cmaBuilder.error}
                        </div>
                      )}
                      {cmaBuilder.result?.best_offer && (
                        <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3" data-testid="builder-cma-result">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm">
                              <span className="font-semibold text-cyan-800">CMA SpotOn</span>
                              <span className="text-slate-500 ml-2">{cmaBuilder.result.pol_point_code} → {cmaBuilder.result.pod_point_code}</span>
                              <div className="text-slate-700">
                                {cmaBuilder.result.best_offer.total_amount?.toLocaleString()} {cmaBuilder.result.best_offer.currency}
                                {cmaBuilder.result.best_offer.transit_days ? ` · ${cmaBuilder.result.best_offer.transit_days} days` : ''}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => addCMASpotToOffer(cmaBuilder.result.best_offer, 15)}
                              className="bg-cyan-600 hover:bg-cyan-700 text-xs shrink-0"
                              data-testid="builder-cma-add"
                            >
                              <Plus className="h-3 w-3 mr-1" /> Add +15%
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Screenshot/Image Upload Section */}
                      <div className="mt-4 pt-4 border-t border-dashed border-slate-300">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-xs font-medium text-slate-600 flex items-center gap-2">
                            <Upload className="h-3 w-3" />
                            Attach Screenshots / Images
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            className="hidden"
                            id="offer-image-upload"
                          />
                          <label 
                            htmlFor="offer-image-upload"
                            className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Upload className="h-3 w-3" />
                            Upload Image
                          </label>
                        </div>
                        
                        {/* Paste area */}
                        <div 
                          className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                          onPaste={handleImagePaste}
                          onClick={() => document.getElementById('offer-image-upload').click()}
                          tabIndex={0}
                          data-testid="image-paste-area"
                        >
                          <div className="flex flex-col items-center gap-2 text-slate-400">
                            <Upload className="h-6 w-6" />
                            <p className="text-sm">Click to upload or paste screenshot (Ctrl+V)</p>
                            <p className="text-xs">Carrier routing, flight schedules, vessel info</p>
                          </div>
                        </div>

                        {/* Uploaded images preview */}
                        {offerImages.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs text-slate-500">{offerImages.length} image(s) attached</p>
                            <div className="grid grid-cols-2 gap-2">
                              {offerImages.map((img) => (
                                <div 
                                  key={img.id} 
                                  className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50"
                                >
                                  <img 
                                    src={img.url} 
                                    alt={img.name}
                                    className="w-full h-24 object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeOfferImage(img.id)}
                                      className="text-white hover:text-red-400"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">
                                    {img.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 3 Free Comment Sections */}
                    <div className="mt-4 pt-4 border-t border-dashed border-slate-300">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="h-4 w-4 text-indigo-500" />
                        <span className="text-sm font-semibold text-slate-700">Comment Sections</span>
                        <span className="text-xs text-slate-400">(text + images)</span>
                      </div>
                      <div className="space-y-4">
                        {commentSections.map((section) => (
                          <div 
                            key={section.id} 
                            className="border border-slate-200 rounded-lg bg-slate-50/50 overflow-hidden"
                            data-testid={`comment-section-${section.id}`}
                          >
                            {/* Section Header */}
                            <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border-b border-slate-200">
                              <GripHorizontal className="h-3 w-3 text-slate-400" />
                              <input
                                type="text"
                                value={section.title}
                                onChange={(e) => setCommentSections(prev => prev.map(s => 
                                  s.id === section.id ? { ...s, title: e.target.value } : s
                                ))}
                                className="flex-1 bg-transparent text-sm font-medium text-slate-700 border-none outline-none focus:ring-0 p-0"
                                data-testid={`comment-title-${section.id}`}
                              />
                              {(section.text || section.images.length > 0) && (
                                <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                                  {section.images.length > 0 && `${section.images.length} img`}
                                  {section.images.length > 0 && section.text && ' + '}
                                  {section.text && 'text'}
                                </span>
                              )}
                            </div>
                            
                            {/* Text Area */}
                            <div className="p-3">
                              <textarea
                                value={section.text}
                                onChange={(e) => setCommentSections(prev => prev.map(s => 
                                  s.id === section.id ? { ...s, text: e.target.value } : s
                                ))}
                                placeholder="Free text — special explanations, notes, conditions..."
                                className="w-full rounded-md border border-slate-200 text-sm p-2 min-h-[60px] resize-y focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300"
                                rows="2"
                                data-testid={`comment-text-${section.id}`}
                              />
                              
                              {/* Drag/Drop/Paste Image Area */}
                              <div 
                                className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors"
                                onPaste={(e) => {
                                  const items = e.clipboardData?.items;
                                  if (!items) return;
                                  for (const item of items) {
                                    if (item.type.startsWith('image/')) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const file = item.getAsFile();
                                      uploadCommentImage(file, section.id);
                                      return;
                                    }
                                  }
                                }}
                                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-indigo-400', 'bg-indigo-50'); }}
                                onDragLeave={(e) => { e.currentTarget.classList.remove('border-indigo-400', 'bg-indigo-50'); }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.currentTarget.classList.remove('border-indigo-400', 'bg-indigo-50');
                                  const files = e.dataTransfer?.files;
                                  if (files) {
                                    for (const file of files) {
                                      if (file.type.startsWith('image/')) {
                                        uploadCommentImage(file, section.id);
                                      }
                                    }
                                  }
                                }}
                                onClick={() => document.getElementById(`comment-image-upload-${section.id}`).click()}
                                tabIndex={0}
                                data-testid={`comment-drop-area-${section.id}`}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => {
                                    const files = e.target.files;
                                    if (files) {
                                      for (const file of files) {
                                        if (file.type.startsWith('image/')) {
                                          uploadCommentImage(file, section.id);
                                        }
                                      }
                                    }
                                    e.target.value = '';
                                  }}
                                  className="hidden"
                                  id={`comment-image-upload-${section.id}`}
                                />
                                <div className="flex flex-col items-center gap-1 text-slate-400">
                                  <ImageIcon className="h-5 w-5" />
                                  <p className="text-xs">Drop, paste (Ctrl+V) or click to add image</p>
                                  <p className="text-xs text-slate-300">e.g. schedule snippet, carrier screenshot</p>
                                </div>
                              </div>
                              
                              {/* Image Previews */}
                              {section.images.length > 0 && (
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                  {section.images.map((img) => (
                                    <div 
                                      key={img.id} 
                                      className="relative group rounded-lg overflow-hidden border border-slate-200 bg-white"
                                    >
                                      <img 
                                        src={img.url} 
                                        alt={img.name}
                                        className="w-full h-20 object-cover"
                                      />
                                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => { e.stopPropagation(); removeCommentImage(section.id, img.id); }}
                                          className="text-white hover:text-red-400"
                                          data-testid={`remove-comment-img-${section.id}-${img.id}`}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-1 truncate">
                                        {img.name}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* LCL Rate Cards Panel */}
                    {offerBuilder.quoteRequest.service_type === 'lcl' && (lclRates.primary || lclRates.reference.length > 0) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            LCL Rate Comparison
                          </h4>
                          {lclRates.lastLookup && (
                            <span className="text-xs text-blue-600">
                              {lclRates.lastLookup.pol} → {lclRates.lastLookup.pod} ({lclRates.lastLookup.direction})
                            </span>
                          )}
                        </div>

                        {(lclRates.note || lclRates.expired > 0) && (
                          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="lcl-validity-note">
                            {lclRates.note}
                            {lclRates.expired > 0 && (
                              <span className="ml-1">{lclRates.note ? " " : ""}{lclRates.expired} expired rate card{lclRates.expired > 1 ? "s" : ""} hidden.</span>
                            )}
                          </div>
                        )}
                        
                        {/* Primary Rate (Freightlinks) */}
                        {lclRates.primary && (
                          <div className="bg-white rounded-lg p-3 border border-blue-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-green-700 text-sm flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                {lclRates.primary.vendor} (Your Rate)
                              </span>
                              <span className="text-xs text-slate-500">
                                Valid until {lclRates.primary.valid_to}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Rate/CBM:</span>
                                <p className="font-bold text-lg text-green-700">
                                  ${lclRates.primary.rate_per_cbm_with_addon || lclRates.primary.rate_per_cbm}
                                </p>
                                {lclRates.primary.first_leg_addon && (
                                  <p className="text-xs text-amber-600">
                                    Incl. ${lclRates.primary.first_leg_addon}/W/M first leg
                                  </p>
                                )}
                              </div>
                              <div>
                                <span className="text-slate-500">Minimum:</span>
                                <p className="font-medium">${lclRates.primary.minimum_charge || 'N/A'}</p>
                              </div>
                              <div>
                                <span className="text-slate-500">Transit:</span>
                                <p className="font-medium">{lclRates.primary.transit_days || '?'} days</p>
                              </div>
                            </div>
                            {lclRates.primary.transhipment && (
                              <p className="text-xs text-slate-500 mt-2">
                                Via: {lclRates.primary.transhipment}
                              </p>
                            )}
                            <Button
                              size="sm"
                              onClick={() => addLCLFreightToOffer(lclRates.primary, 10)}
                              className="mt-3 bg-green-600 hover:bg-green-700 text-xs"
                              data-testid="lcl-add-primary"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add with 10% margin
                            </Button>
                          </div>
                        )}
                        
                        {/* Reference Rates (Competition) */}
                        {lclRates.reference.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-600 uppercase">Competition Reference</p>
                            {lclRates.reference.map((rate, idx) => {
                              const vendorNames = {
                                'shipco': 'Shipco Transport',
                                'ecu_worldwide': 'ECU Worldwide',
                              };
                              return (
                                <div key={idx} className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                                  <div className="flex items-center justify-between gap-2">
                                    <div>
                                      <span className="font-medium text-slate-700 text-sm">
                                        {vendorNames[rate.vendor] || rate.vendor}
                                      </span>
                                      <span className="text-slate-400 mx-2">|</span>
                                      <span className="text-slate-600">
                                        ${rate.rate_per_cbm}/CBM • Min ${rate.minimum_charge || 'N/A'}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className="text-xs text-slate-500">
                                        {rate.transit_days ? `${rate.transit_days} days` : rate.routing || ''}
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => addLCLFreightToOffer(rate, 10)}
                                        className="h-7 text-xs border-slate-300"
                                        data-testid={`lcl-use-competition-${idx}`}
                                      >
                                        <Plus className="h-3 w-3 mr-1" />
                                        Use
                                      </Button>
                                    </div>
                                  </div>
                                  {rate.rate_note && (
                                    <p className="text-xs text-slate-500 mt-1 pl-1">
                                      {rate.rate_note}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {!lclRates.primary && lclRates.reference.length === 0 && (
                          <p className="text-sm text-blue-600 text-center py-2">
                            No rates found. Upload rate cards for Freightlinks, Shipco, or ECU Worldwide.
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Trucking Rate Suggestions Panel */}
                    {truckingSuggestions.show && (
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                            <History className="h-4 w-4" />
                            Trucking Rate Suggestions ({truckingSuggestions.mode === 'precarriage' ? 'Pre-carriage' : 'On-carriage'})
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setTruckingSuggestions(prev => ({ ...prev, show: false }))}
                            className="h-6 w-6 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {truckingSuggestions.loading ? (
                          <div className="flex items-center justify-center py-4">
                            <RefreshCw className="h-5 w-5 animate-spin text-purple-600" />
                            <span className="ml-2 text-purple-600">Analyzing historical orders...</span>
                          </div>
                        ) : truckingSuggestions.suggestions ? (
                          <>
                            <div className="text-xs text-purple-700 bg-white/60 rounded-lg p-2">
                              <span className="font-medium">{truckingSuggestions.suggestions.origin}</span>
                              <ArrowRight className="h-3 w-3 inline mx-1" />
                              <span className="font-medium">{truckingSuggestions.suggestions.destination}</span>
                            </div>
                            
                            {truckingSuggestions.suggestions.stats.total_matches > 0 ? (
                              <>
                                {/* Price Stats */}
                                <div className="grid grid-cols-3 gap-2 text-center">
                                  <div className="bg-white rounded-lg p-2">
                                    <p className="text-xs text-slate-500">Min</p>
                                    <p className="font-bold text-slate-700">€{truckingSuggestions.suggestions.stats.min_price}</p>
                                  </div>
                                  <div className="bg-purple-100 rounded-lg p-2 border border-purple-300">
                                    <p className="text-xs text-purple-600">Suggested</p>
                                    <p className="font-bold text-purple-800 text-lg">
                                      €{truckingSuggestions.suggestions.stats.suggested_rate}
                                    </p>
                                  </div>
                                  <div className="bg-white rounded-lg p-2">
                                    <p className="text-xs text-slate-500">Max</p>
                                    <p className="font-bold text-slate-700">€{truckingSuggestions.suggestions.stats.max_price}</p>
                                  </div>
                                </div>
                                
                                <p className="text-xs text-purple-600 text-center">
                                  Based on {truckingSuggestions.suggestions.stats.total_matches} similar orders
                                  {truckingSuggestions.suggestions.stats.avg_per_container && (
                                    <span> • Avg €{truckingSuggestions.suggestions.stats.avg_per_container}/container</span>
                                  )}
                                </p>
                                
                                {/* Top Companies */}
                                {truckingSuggestions.suggestions.top_companies?.length > 0 && (
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-purple-800">Top Trucking Companies:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {truckingSuggestions.suggestions.top_companies.slice(0, 3).map((comp, idx) => (
                                        <span key={idx} className="text-xs bg-white px-2 py-1 rounded border border-purple-200">
                                          {comp.name} <span className="text-purple-500">({comp.order_count}x, avg €{comp.avg_price})</span>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Sample Orders */}
                                {truckingSuggestions.sampleOrders?.length > 0 && (
                                  <details className="text-xs">
                                    <summary className="cursor-pointer text-purple-700 hover:text-purple-800 font-medium">
                                      View sample orders ({truckingSuggestions.sampleOrders.length})
                                    </summary>
                                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                                      {truckingSuggestions.sampleOrders.map((order, idx) => (
                                        <div key={idx} className="bg-white p-1.5 rounded text-xs flex justify-between">
                                          <span className="text-slate-500">{order.order_number}</span>
                                          <span className="font-medium">€{order.price}</span>
                                          <span className="text-slate-400">{order.trucking_company}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                )}
                                
                                {/* Add Buttons */}
                                <div className="flex gap-2 pt-2">
                                  <Button
                                    size="sm"
                                    onClick={() => addTruckingFromSuggestion(truckingSuggestions.suggestions.stats.suggested_rate, 0)}
                                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-xs"
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add Suggested Rate
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => addTruckingFromSuggestion(truckingSuggestions.suggestions.stats.suggested_rate, 5)}
                                    className="text-xs border-purple-300 text-purple-600 hover:bg-purple-50"
                                  >
                                    +5%
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => addTruckingFromSuggestion(truckingSuggestions.suggestions.stats.suggested_rate, 10)}
                                    className="text-xs border-purple-300 text-purple-600 hover:bg-purple-50"
                                  >
                                    +10%
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-3">
                                <p className="text-sm text-purple-600">No similar orders found for this route.</p>
                                <p className="text-xs text-purple-500 mt-1">Try adding a custom trucking component manually.</p>
                              </div>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {/* Summary & Details */}
                  <div className="space-y-4">
                    {/* Total */}
                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-5 text-white">
                      <p className="text-slate-400 text-sm">Total Amount</p>
                      <p className="text-3xl font-bold mt-1">
                        {calculateTotal().toFixed(3)} <span className="text-lg font-normal">{offerBuilder.currency}</span>
                      </p>
                      <p className="text-slate-400 text-xs mt-2">
                        {offerBuilder.components.filter(c => c.is_included).length} components included
                        {offerBuilder.components.some(c => c.currency && c.currency !== offerBuilder.currency) && (
                          <span className="ml-1 text-blue-400">(with currency conversion)</span>
                        )}
                      </p>
                      {/* Exchange rate info */}
                      {offerBuilder.components.some(c => c.currency && c.currency !== offerBuilder.currency) && (
                        <div className="mt-3 pt-3 border-t border-slate-700">
                          <p className="text-slate-500 text-xs mb-1 flex items-center gap-1">
                            <RefreshCw className="h-3 w-3" />
                            Live Exchange Rates (vs EUR)
                            {exchangeRatesLastUpdated && (
                              <span className="text-slate-600">
                                - Updated {exchangeRatesLastUpdated.toLocaleDateString()}
                              </span>
                            )}
                          </p>
                          <div className="flex flex-wrap gap-2 text-xs">
                            {["USD", "GBP", "CHF", "CNY"].map(curr => (
                              <span key={curr} className="text-slate-400">
                                {curr}: {exchangeRates[curr]?.toFixed(4) || 'N/A'}
                              </span>
                            ))}
                          </div>
                          <p className="text-amber-400/80 text-xs mt-2 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Exchange rate variances may apply due to rate date and bank vs. internet rates used during invoicing.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Offer Details */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
                      <h4 className="font-semibold text-slate-900">Offer Details</h4>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-slate-500">Total Currency</label>
                          <select
                            value={offerBuilder.currency}
                            onChange={(e) => setOfferBuilder(prev => ({ ...prev, currency: e.target.value }))}
                            className="w-full mt-1 rounded-md border-slate-200 text-sm"
                          >
                            <option value="EUR">EUR - Euro</option>
                            <option value="USD">USD - US Dollar</option>
                            <option value="GBP">GBP - British Pound</option>
                            <option value="CHF">CHF - Swiss Franc</option>
                            <option value="CNY">CNY - Chinese Yuan</option>
                            <option value="JPY">JPY - Japanese Yen</option>
                            <option value="KRW">KRW - Korean Won</option>
                            <option value="INR">INR - Indian Rupee</option>
                          </select>
                          <p className="text-xs text-slate-400 mt-1">All rows will be converted to this currency</p>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Validity (days)</label>
                          <Input
                            type="number"
                            value={offerBuilder.validityDays}
                            onChange={(e) => setOfferBuilder(prev => ({ ...prev, validityDays: parseInt(e.target.value) || 14 }))}
                            className="mt-1"
                            min="1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-xs text-slate-500">Incoterm</label>
                        <select
                          value={offerBuilder.incoterm}
                          onChange={(e) => setOfferBuilder(prev => ({ ...prev, incoterm: e.target.value }))}
                          className="w-full mt-1 rounded-md border-slate-200 text-sm"
                        >
                          <option value="">Select...</option>
                          <option value="EXW">EXW - Ex Works</option>
                          <option value="FCA">FCA - Free Carrier</option>
                          <option value="FOB">FOB - Free On Board</option>
                          <option value="CFR">CFR - Cost and Freight</option>
                          <option value="CIF">CIF - Cost, Insurance & Freight</option>
                          <option value="DAP">DAP - Delivered at Place</option>
                          <option value="DDP">DDP - Delivered Duty Paid</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="text-xs text-slate-500">Transit Time</label>
                        <Input
                          value={offerBuilder.transitTime}
                          onChange={(e) => setOfferBuilder(prev => ({ ...prev, transitTime: e.target.value }))}
                          placeholder="e.g., 18-22 days"
                          className="mt-1"
                        />
                      </div>

                      {/* Ocean Freight Details - Only for FCL/LCL */}
                      {(offerBuilder.quoteRequest.service_type === "fcl" || 
                        offerBuilder.quoteRequest.service_type === "lcl") && (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                          <h5 className="font-medium text-blue-800 flex items-center gap-2">
                            <Ship className="h-4 w-4" />
                            Ocean Freight Details
                          </h5>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-600">Carrier (SSLINE)</label>
                              <Input
                                value={offerBuilder.carrier || ""}
                                onChange={(e) => setOfferBuilder(prev => ({ ...prev, carrier: e.target.value }))}
                                placeholder="e.g., MSC, Maersk, CMA CGM"
                                className="mt-1 bg-white"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Transit Time (TT)</label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  type="number"
                                  value={offerBuilder.transitDays || ""}
                                  onChange={(e) => setOfferBuilder(prev => ({ ...prev, transitDays: e.target.value }))}
                                  placeholder="25"
                                  className="bg-white"
                                />
                                <span className="text-sm text-slate-500">days</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-600">Transhipment (TS)</label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                value={offerBuilder.transhipment || ""}
                                onChange={(e) => setOfferBuilder(prev => ({ ...prev, transhipment: e.target.value }))}
                                placeholder="e.g., Singapore, Piraeus"
                                className="flex-1 bg-white"
                                disabled={offerBuilder.directService}
                              />
                              <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={offerBuilder.directService || false}
                                  onChange={(e) => setOfferBuilder(prev => ({ 
                                    ...prev, 
                                    directService: e.target.checked,
                                    transhipment: e.target.checked ? "" : prev.transhipment
                                  }))}
                                  className="rounded"
                                />
                                Direct Service
                              </label>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-600">OFR Valid Until</label>
                            <Input
                              type="date"
                              value={offerBuilder.ofrValidUntil || ""}
                              onChange={(e) => setOfferBuilder(prev => ({ ...prev, ofrValidUntil: e.target.value }))}
                              className="mt-1 bg-white"
                            />
                          </div>
                          <div className="text-xs text-slate-500 pt-2 border-t border-blue-200">
                            <p>• Bkg subj to eq and space availability</p>
                            <p>• Bkg subj to cgo acceptance</p>
                          </div>
                        </div>
                      )}

                      {/* Air Freight Details - Only for Air */}
                      {offerBuilder.quoteRequest.service_type === "air" && (
                        <div className="p-4 bg-sky-50 rounded-lg border border-sky-200 space-y-3">
                          <h5 className="font-medium text-sky-800 flex items-center gap-2">
                            <Plane className="h-4 w-4" />
                            Air Freight Details
                          </h5>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs text-slate-600">Weight (kgs)</label>
                              <Input
                                type="number"
                                step="0.01"
                                value={offerBuilder.airWeight || ""}
                                onChange={(e) => setOfferBuilder(prev => ({ ...prev, airWeight: e.target.value }))}
                                placeholder="e.g., 500"
                                className="mt-1 bg-white"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Volume (cbm)</label>
                              <Input
                                type="number"
                                step="0.001"
                                value={offerBuilder.airVolume || ""}
                                onChange={(e) => setOfferBuilder(prev => ({ ...prev, airVolume: e.target.value }))}
                                placeholder="e.g., 2.5"
                                className="mt-1 bg-white"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">CW (Chargeable Weight)</label>
                              <Input
                                type="number"
                                step="0.01"
                                value={offerBuilder.airCW || ""}
                                onChange={(e) => {
                                  const newCW = e.target.value;
                                  setOfferBuilder(prev => {
                                    // Update airCW and sync Air Freight component
                                    const updatedComponents = prev.components.map(comp => {
                                      if (comp.name?.toLowerCase().includes('air freight') && comp.category === 'freight') {
                                        return {
                                          ...comp,
                                          quantity: parseFloat(newCW) || 1,
                                          total: (parseFloat(newCW) || 1) * comp.unit_price
                                        };
                                      }
                                      return comp;
                                    });
                                    return { ...prev, airCW: newCW, components: updatedComponents };
                                  });
                                }}
                                placeholder="e.g., 600"
                                className="mt-1 bg-white"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-600">Rate per kg (EUR)</label>
                              <Input
                                type="number"
                                step="0.001"
                                value={offerBuilder.airRatePerKg || ""}
                                onChange={(e) => {
                                  const newRate = e.target.value;
                                  setOfferBuilder(prev => {
                                    // Update airRatePerKg and sync Air Freight component
                                    const cw = parseFloat(prev.airCW) || 1;
                                    const updatedComponents = prev.components.map(comp => {
                                      if (comp.name?.toLowerCase().includes('air freight') && comp.category === 'freight') {
                                        return {
                                          ...comp,
                                          unit_price: parseFloat(newRate) || 0,
                                          quantity: cw,
                                          total: cw * (parseFloat(newRate) || 0)
                                        };
                                      }
                                      return comp;
                                    });
                                    return { ...prev, airRatePerKg: newRate, components: updatedComponents };
                                  });
                                }}
                                placeholder="e.g., 2.50"
                                className="mt-1 bg-white"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Air Freight Total</label>
                              <div className="mt-1 p-2 bg-white rounded border text-right font-mono font-semibold text-sky-700">
                                {(() => {
                                  const cw = parseFloat(offerBuilder.airCW) || 0;
                                  const rate = parseFloat(offerBuilder.airRatePerKg) || 0;
                                  const total = cw * rate;
                                  return total > 0 ? `${total.toFixed(2)} EUR` : "—";
                                })()}
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-slate-600">Carrier / Airline</label>
                              <Input
                                value={offerBuilder.airCarrier || ""}
                                onChange={(e) => setOfferBuilder(prev => ({ ...prev, airCarrier: e.target.value }))}
                                placeholder="e.g., Turkish Cargo, Emirates"
                                className="mt-1 bg-white"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-600">Transit Time</label>
                              <div className="flex items-center gap-2 mt-1">
                                <Input
                                  type="number"
                                  value={offerBuilder.airTransitDays || ""}
                                  onChange={(e) => setOfferBuilder(prev => ({ ...prev, airTransitDays: e.target.value }))}
                                  placeholder="3"
                                  className="bg-white"
                                />
                                <span className="text-sm text-slate-500">days</span>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-sky-600">
                            CW = max(Actual Weight, Volume × 167). Total = CW × Rate/kg
                          </p>
                        </div>
                      )}
                      
                      <div>
                        <label className="text-xs text-slate-500">Routing</label>
                        <Input
                          value={offerBuilder.routing}
                          onChange={(e) => setOfferBuilder(prev => ({ ...prev, routing: e.target.value }))}
                          placeholder="e.g., Ningbo - Shanghai - Koper"
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Carrier Routing Details - Paste from carrier website */}
                      <div>
                        <label className="text-xs text-slate-500 flex items-center gap-2">
                          <Plane className="h-3 w-3" />
                          Carrier Routing Details
                        </label>
                        <textarea
                          value={offerBuilder.vesselSchedule || offerBuilder.carrierRoutingInfo || ""}
                          onChange={(e) => setOfferBuilder(prev => ({ 
                            ...prev, 
                            vesselSchedule: e.target.value,
                            carrierRoutingInfo: e.target.value 
                          }))}
                          placeholder="Paste carrier info here, e.g.:&#10;Air France AF0331M&#10;LJU 20Feb → CDG → EZE 22Feb&#10;Transit: 54h50m"
                          className="w-full mt-1 rounded-lg border-slate-200 text-sm p-3 min-h-[70px] font-mono bg-slate-50"
                          data-testid="offer-carrier-routing-info"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Flight/vessel info, ETD/ETA, transit times from carrier
                        </p>
                      </div>
                      
                      <div>
                        <label className="text-xs text-slate-500">Payment Terms</label>
                        <Input
                          value={offerBuilder.paymentTerms}
                          onChange={(e) => setOfferBuilder(prev => ({ ...prev, paymentTerms: e.target.value }))}
                          placeholder="e.g., 30 days from B/L date"
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs text-slate-500">Special Conditions</label>
                        <textarea
                          value={offerBuilder.specialConditions}
                          onChange={(e) => setOfferBuilder(prev => ({ ...prev, specialConditions: e.target.value }))}
                          placeholder="Any special terms or conditions..."
                          className="w-full mt-1 rounded-md border-slate-200 text-sm resize-none"
                          rows="2"
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs text-slate-500 flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          Internal Notes (not sent to customer)
                        </label>
                        <textarea
                          value={offerBuilder.internalNotes}
                          onChange={(e) => setOfferBuilder(prev => ({ ...prev, internalNotes: e.target.value }))}
                          placeholder="Notes for team..."
                          className="w-full mt-1 rounded-md border-amber-200 bg-amber-50 text-sm resize-none"
                          rows="2"
                        />
                      </div>
                    </div>

                    {/* Matched Rate Cards */}
                    {offerBuilder.matchingRateCards?.length > 0 && (
                      <div className="bg-blue-50 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                          <Sparkles className="h-4 w-4" />
                          Matched Rate Cards
                        </h4>
                        <div className="mt-2 space-y-2">
                          {offerBuilder.matchingRateCards.map(card => (
                            <div key={card.id} className="text-xs text-blue-800 bg-blue-100 rounded px-2 py-1">
                              {card.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        onClick={previewCurrentOffer}
                        className="w-full"
                        data-testid="preview-offer-btn"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Preview Offer
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => saveOffer(true)}
                          className="flex-1"
                          data-testid="save-draft-btn"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save Draft
                        </Button>
                        <Button
                          onClick={() => saveOffer(false)}
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          data-testid="send-offer-btn"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send Offer
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Offer Preview Modal */}
        <AnimatePresence>
          {previewOffer && previewQuote && (
            <OfferPreviewPage
              offer={previewOffer}
              quoteRequest={previewQuote}
              onClose={() => {
                setPreviewOffer(null);
                setPreviewQuote(null);
              }}
              onSend={async () => {
                if (previewOffer.id !== "preview") {
                  await sendOffer(previewOffer.id);
                } else {
                  // Close preview and save+send
                  setPreviewOffer(null);
                  setPreviewQuote(null);
                  await saveOffer(false);
                }
              }}
            />
          )}
        </AnimatePresence>

        {/* Quote Reminder Modal */}
        <AnimatePresence>
          {reminderQuote && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setReminderQuote(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-blue-600" />
                    Set Quote Reminder
                  </h3>
                  <button
                    onClick={() => setReminderQuote(null)}
                    className="p-1 rounded-lg hover:bg-slate-100"
                  >
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900">{reminderQuote.quote_number}</p>
                    <p className="text-sm text-slate-600">{reminderQuote.company_name}</p>
                    <p className="text-sm text-slate-500">
                      {reminderQuote.origin_city} → {reminderQuote.destination_city}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Reminder Date
                    </label>
                    <Input
                      type="date"
                      value={reminderDate}
                      onChange={(e) => setReminderDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Note (optional)
                    </label>
                    <Input
                      type="text"
                      value={reminderNote}
                      onChange={(e) => setReminderNote(e.target.value)}
                      placeholder="e.g., Follow up on pricing"
                      className="w-full"
                    />
                  </div>

                  <p className="text-xs text-slate-500">
                    An email reminder will be sent to the customer on this date if the quote is still pending.
                  </p>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setReminderQuote(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={setQuoteReminder}
                    disabled={sendingReminder || !reminderDate}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingReminder ? (
                      <>
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                        Setting...
                      </>
                    ) : (
                      <>
                        <Bell className="h-4 w-4 mr-2" />
                        Set Reminder
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
          <button
            onClick={() => setActiveTab("quotes")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "quotes"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-quotes"
          >
            Pending Quotes
            {stats?.pending_quotes > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                {stats.pending_quotes}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("offers")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "offers"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-offers"
          >
            My Offers
          </button>
          {/* Rate Cards tab hidden - functionality not working */}
          {/* <button
            onClick={() => setActiveTab("rate-cards")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "rate-cards"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-rate-cards"
          >
            Rate Cards
            <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
              {rateCards.length}
            </span>
          </button> */}
          <button
            onClick={() => setActiveTab("services")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "services"
                ? "border-purple-600 text-purple-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-services"
          >
            <BookOpen className="h-4 w-4 inline mr-1" />
            Service Rates
          </button>
          <button
            onClick={() => setActiveTab("lcl-import")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "lcl-import"
                ? "border-emerald-600 text-emerald-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-lcl-import"
          >
            <Package className="h-4 w-4 inline mr-1" />
            LCL Import
          </button>
          <button
            onClick={() => setActiveTab("port-pair")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "port-pair"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-port-pair"
          >
            <Search className="h-4 w-4 inline mr-1" />
            Port-Pair Search
          </button>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search quotes, offers..."
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          
          {activeTab === "offers" && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border-slate-200 text-sm"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {/* Pending Quotes Tab */}
            {activeTab === "quotes" && (
              <div className="space-y-4">
                {filteredQuotes.length === 0 ? (
                  <div className="text-center py-16 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No pending quote requests</p>
                  </div>
                ) : (
                  filteredQuotes.map((quote, idx) => {
                    const ServiceIcon = SERVICE_ICONS[quote.service_type] || Package;
                    return (
                      <motion.div
                        key={quote.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                        data-testid={`quote-card-${quote.quote_number}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-xl bg-blue-100`}>
                              <ServiceIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-900">{quote.quote_number}</span>
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                  Pending
                                </span>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">
                                {quote.company_name} • {quote.contact_name}
                              </p>
                              <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                                {quote.origin_city}, {quote.origin_country}
                                <ArrowRight className="h-4 w-4" />
                                {quote.destination_city}, {quote.destination_country}
                              </p>
                              {quote.container_type && (
                                <p className="text-xs text-slate-400 mt-2">
                                  {quote.container_quantity}x {quote.container_type} • 
                                  {quote.commodity && ` ${quote.commodity} •`}
                                  {quote.cargo_ready_date && ` Ready: ${quote.cargo_ready_date}`}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openAssignTaskModal(quote)}
                              title="Assign to staff member"
                              data-testid={`assign-task-${quote.quote_number}`}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReminderModal(quote)}
                              title="Set reminder"
                              data-testid={`set-reminder-${quote.quote_number}`}
                            >
                              <Bell className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendReminderNow(quote)}
                              title="Send reminder email now"
                              data-testid={`send-reminder-now-${quote.quote_number}`}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => markQuoteEmailed(quote)}
                              title="Mark as offer sent by email (moves out of pending)"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              data-testid={`mark-emailed-${quote.quote_number}`}
                            >
                              <Send className="h-4 w-4 mr-1.5" />
                              Sent by email
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => findInOfferArchive(quote)}
                              title="Find this customer's offers in the Email Offer Archive"
                              data-testid={`find-in-archive-${quote.quote_number}`}
                            >
                              <Archive className="h-4 w-4 mr-1.5" />
                              Offer Archive
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteQuote(quote)}
                              title="Delete quote"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-quote-${quote.quote_number}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => startOffer(quote)}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`create-offer-${quote.quote_number}`}
                            >
                              <Sparkles className="h-4 w-4 mr-2" />
                              Create Offer
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            )}

            {/* Offers Tab */}
            {activeTab === "offers" && (
              <div className="space-y-4">
                {/* Local Drafts Section */}
                {localDrafts.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Save className="h-5 w-5 text-amber-600" />
                        <h3 className="font-semibold text-amber-900">Unsaved Drafts</h3>
                        <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                          {localDrafts.length}
                        </span>
                      </div>
                      <span className="text-xs text-amber-600">Auto-saved locally</span>
                    </div>
                    <div className="space-y-2">
                      {localDrafts.slice(0, 3).map((draft) => (
                        <div 
                          key={draft.id}
                          className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-100"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                {draft.offerBuilder?.standaloneData?.customer_company || 
                                 draft.selectedQuote?.company_name || 
                                 'Untitled Draft'}
                              </span>
                              {draft.offerBuilder?.isClone && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  Clone of {draft.offerBuilder.clonedFrom}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {draft.offerBuilder?.routing || 
                               `${draft.offerBuilder?.standaloneData?.pol || ''} → ${draft.offerBuilder?.standaloneData?.pod || ''}`}
                              {' • '}Saved {new Date(draft.savedAt).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => loadDraft(draft)}
                              className="text-amber-700 border-amber-300 hover:bg-amber-100"
                            >
                              Continue
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteDraft(draft.id)}
                              className="text-red-500 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {localDrafts.length > 3 && (
                        <p className="text-xs text-amber-600 text-center">
                          +{localDrafts.length - 3} more drafts
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {filteredOffers.length === 0 && localDrafts.length === 0 ? (
                  <div className="text-center py-16 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No offers yet</p>
                  </div>
                ) : filteredOffers.length === 0 ? null : (
                  filteredOffers.map((offer, idx) => {
                    const statusConfig = STATUS_CONFIG[offer.status] || STATUS_CONFIG.draft;
                    const StatusIcon = statusConfig.icon;
                    return (
                      <motion.div
                        key={offer.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                        data-testid={`offer-card-${offer.offer_number}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">{offer.offer_number}</span>
                              <span className={`px-2 py-0.5 text-xs rounded-full flex items-center gap-1 ${statusConfig.color}`}>
                                <StatusIcon className="h-3 w-3" />
                                {statusConfig.label}
                              </span>
                              {offer.revision_number > 1 && (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                                  Rev {offer.revision_number}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              {offer.customer_company} • {offer.quote_number}
                            </p>
                            <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                              {offer.service_type?.toUpperCase()} • {offer.origin}
                              <ArrowRight className="h-4 w-4" />
                              {offer.destination}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">
                              {offer.subtotal?.toFixed(2)} <span className="text-sm font-normal text-slate-500">{offer.currency}</span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Valid until {offer.valid_until?.slice(0, 10)}
                            </p>
                            
                            <div className="flex items-center gap-2 mt-3 justify-end flex-wrap">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => viewOffer(offer)}
                                data-testid={`view-${offer.offer_number}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => cloneOffer(offer)}
                                data-testid={`clone-${offer.offer_number}`}
                              >
                                <Copy className="h-4 w-4 mr-1" />
                                Clone
                              </Button>
                              {offer.status === "draft" && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => editOffer(offer)}
                                    className="bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
                                    data-testid={`edit-${offer.offer_number}`}
                                  >
                                    <Edit3 className="h-4 w-4 mr-1" />
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => sendOffer(offer.id)}
                                    className="bg-blue-600 hover:bg-blue-700"
                                    data-testid={`send-${offer.offer_number}`}
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Send
                                  </Button>
                                </>
                              )}
                              {offer.status === "sent" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => editOffer(offer)}
                                  className="bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
                                  data-testid={`revise-${offer.offer_number}`}
                                >
                                  <Edit3 className="h-4 w-4 mr-1" />
                                  Edit / Revise
                                </Button>
                              )}
                              {/* Delete button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteOffer(offer)}
                                className="text-red-500 hover:bg-red-50 hover:text-red-600"
                                data-testid={`delete-${offer.offer_number}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>
            )}

            {/* Rate Cards Tab */}
            {activeTab === "rate-cards" && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-rate-card-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Rate Card
                  </Button>
                </div>
                
                {rateCards.length === 0 ? (
                  <div className="text-center py-16 text-slate-500">
                    <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No rate cards yet</p>
                    <p className="text-sm mt-1">Create rate cards to auto-populate offers</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rateCards.map((card) => (
                      <motion.div
                        key={card.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900">{card.name}</h3>
                            <p className="text-sm text-slate-500 mt-1">
                              {card.service_type?.toUpperCase()} • {card.carrier || "All carriers"}
                            </p>
                            <p className="text-xs text-slate-400 mt-2">
                              {card.origin_region} → {card.destination_region}
                            </p>
                          </div>
                          {card.is_template && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                              Template
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
                          <span>{card.components?.length || 0} components</span>
                          <span>•</span>
                          <span>Valid: {card.valid_from?.slice(0, 10)} - {card.valid_to?.slice(0, 10)}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Service Rates Tab */}
            {activeTab === "services" && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                    <BookOpen className="h-6 w-6" />
                    Service Rate Templates
                  </h2>
                  <p className="text-purple-700 mt-2">
                    Complete rate structures for all logistics services. Click to expand and view detailed pricing.
                  </p>
                </div>

                {/* Service Categories */}
                <div className="space-y-4">
                  {serviceCategories.map((category) => {
                    const categoryTemplates = serviceTemplates.filter(t => t.category === category.id);
                    if (categoryTemplates.length === 0) return null;
                    
                    return (
                      <div key={category.id} className="space-y-3">
                        <h3 
                          className="text-lg font-semibold flex items-center gap-2"
                          style={{ color: category.color }}
                        >
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: category.color }}
                          />
                          {category.name}
                        </h3>
                        
                        {categoryTemplates.map((template) => (
                          <motion.div
                            key={template.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                          >
                            {/* Header - Clickable */}
                            <button
                              onClick={() => setExpandedService(expandedService === template.id ? null : template.id)}
                              className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                            >
                              <div>
                                <h4 className="font-semibold text-slate-900">{template.name}</h4>
                                <p className="text-sm text-slate-500 mt-1">
                                  {template.description}
                                  {template.location && (
                                    <span className="ml-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                      {template.location}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <ChevronRight 
                                className={`h-5 w-5 text-slate-400 transition-transform ${
                                  expandedService === template.id ? "rotate-90" : ""
                                }`}
                              />
                            </button>
                            
                            {/* Expanded Content */}
                            {expandedService === template.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="border-t border-slate-200"
                              >
                                <div className="p-5 space-y-4">
                                  {/* Base Rates */}
                                  {template.base_rates && template.base_rates.length > 0 && (
                                    <div>
                                      <h5 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                                        <DollarSign className="h-4 w-4 text-green-600" />
                                        Base Rates
                                      </h5>
                                      <div className="bg-green-50 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                          <thead className="bg-green-100">
                                            <tr>
                                              <th className="text-left px-4 py-2 font-medium text-green-800">Item</th>
                                              <th className="text-right px-4 py-2 font-medium text-green-800">Rate</th>
                                              <th className="text-left px-4 py-2 font-medium text-green-800">Unit</th>
                                              <th className="text-left px-4 py-2 font-medium text-green-800">Notes</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {template.base_rates.map((rate, idx) => (
                                              <tr key={idx} className="border-t border-green-100">
                                                <td className="px-4 py-2 text-slate-700">{rate.name}</td>
                                                <td className="px-4 py-2 text-right font-mono font-medium text-green-700">
                                                  {rate.rate}{rate.unit === "%" ? "%" : ` ${rate.currency}`}
                                                </td>
                                                <td className="px-4 py-2 text-slate-500">{rate.unit}</td>
                                                <td className="px-4 py-2 text-slate-500 text-xs">{rate.notes || "-"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* Tiered Rates */}
                                  {template.tiered_rates && template.tiered_rates.length > 0 && (
                                    <div>
                                      <h5 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                                        <Layers className="h-4 w-4 text-blue-600" />
                                        Tiered Rates (by Cargo Value)
                                      </h5>
                                      <div className="bg-blue-50 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                          <thead className="bg-blue-100">
                                            <tr>
                                              <th className="text-left px-4 py-2 font-medium text-blue-800">Cargo Value Range</th>
                                              <th className="text-right px-4 py-2 font-medium text-blue-800">Rate</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {template.tiered_rates.map((tier, idx) => (
                                              <tr key={idx} className="border-t border-blue-100">
                                                <td className="px-4 py-2 text-slate-700">
                                                  {tier.max_value 
                                                    ? `€${tier.min_value.toLocaleString()} - €${tier.max_value.toLocaleString()}`
                                                    : `> €${tier.min_value.toLocaleString()}`
                                                  }
                                                </td>
                                                <td className="px-4 py-2 text-right font-mono font-medium text-blue-700">
                                                  {tier.rate}{tier.unit.includes("%") ? "%" : ` EUR`} / {tier.unit.replace("% of cargo value", "cargo value")}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* Surcharges */}
                                  {template.surcharges && template.surcharges.length > 0 && (
                                    <div>
                                      <h5 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                                        <Plus className="h-4 w-4 text-orange-600" />
                                        Surcharges
                                      </h5>
                                      <div className="bg-orange-50 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                          <thead className="bg-orange-100">
                                            <tr>
                                              <th className="text-left px-4 py-2 font-medium text-orange-800">Item</th>
                                              <th className="text-right px-4 py-2 font-medium text-orange-800">Rate</th>
                                              <th className="text-left px-4 py-2 font-medium text-orange-800">Notes</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {template.surcharges.map((surcharge, idx) => (
                                              <tr key={idx} className="border-t border-orange-100">
                                                <td className="px-4 py-2 text-slate-700">{surcharge.name}</td>
                                                <td className="px-4 py-2 text-right font-mono font-medium text-orange-700">
                                                  {surcharge.rate} {surcharge.currency}/{surcharge.unit}
                                                </td>
                                                <td className="px-4 py-2 text-slate-500 text-xs">{surcharge.notes || "-"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* Conditional Charges */}
                                  {template.conditional_charges && template.conditional_charges.length > 0 && (
                                    <div>
                                      <h5 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                                        Conditional Charges
                                      </h5>
                                      <div className="bg-amber-50 rounded-lg overflow-hidden">
                                        <table className="w-full text-sm">
                                          <thead className="bg-amber-100">
                                            <tr>
                                              <th className="text-left px-4 py-2 font-medium text-amber-800">Item</th>
                                              <th className="text-right px-4 py-2 font-medium text-amber-800">Rate</th>
                                              <th className="text-left px-4 py-2 font-medium text-amber-800">Condition</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {template.conditional_charges.map((charge, idx) => (
                                              <tr key={idx} className="border-t border-amber-100">
                                                <td className="px-4 py-2 text-slate-700">{charge.name}</td>
                                                <td className="px-4 py-2 text-right font-mono font-medium text-amber-700">
                                                  {charge.rate === 0 ? "At cost" : `${charge.rate} ${charge.currency}/${charge.unit}`}
                                                </td>
                                                <td className="px-4 py-2 text-amber-600 text-xs italic">{charge.condition}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {/* Subject To / Requirements / Exclusions */}
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {template.subject_to && template.subject_to.length > 0 && (
                                      <div className="p-4 bg-red-50 rounded-lg">
                                        <h6 className="font-medium text-red-800 mb-2 text-sm">Subject To</h6>
                                        <ul className="text-xs text-red-700 space-y-1">
                                          {template.subject_to.map((item, idx) => (
                                            <li key={idx}>• {item}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {template.requirements && template.requirements.length > 0 && (
                                      <div className="p-4 bg-blue-50 rounded-lg">
                                        <h6 className="font-medium text-blue-800 mb-2 text-sm">Required Documents</h6>
                                        <ul className="text-xs text-blue-700 space-y-1">
                                          {template.requirements.map((item, idx) => (
                                            <li key={idx}>• {item}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {template.exclusions && template.exclusions.length > 0 && (
                                      <div className="p-4 bg-slate-50 rounded-lg">
                                        <h6 className="font-medium text-slate-800 mb-2 text-sm">Exclusions</h6>
                                        <ul className="text-xs text-slate-600 space-y-1">
                                          {template.exclusions.map((item, idx) => (
                                            <li key={idx}>• {item}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>

                                  {/* Notes */}
                                  {template.notes && (
                                    <div className="p-4 bg-slate-100 rounded-lg flex items-start gap-2">
                                      <Info className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                                      <p className="text-sm text-slate-600">{template.notes}</p>
                                    </div>
                                  )}

                                  {/* Standard Notes for Ocean Freight */}
                                  {template.standard_notes && template.standard_notes.length > 0 && (
                                    <div className="p-4 bg-indigo-50 rounded-lg">
                                      <h6 className="font-medium text-indigo-800 mb-2 text-sm">Standard Terms</h6>
                                      <ul className="text-xs text-indigo-700 space-y-1">
                                        {template.standard_notes.map((note, idx) => (
                                          <li key={idx}>• {note}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Ocean Freight Template Fields */}
                                  {template.template_fields && (
                                    <div className="p-4 bg-cyan-50 rounded-lg">
                                      <h6 className="font-medium text-cyan-800 mb-2 text-sm">Offer Template Format</h6>
                                      <div className="font-mono text-xs text-cyan-700 space-y-1 bg-white p-3 rounded border border-cyan-200">
                                        <p>SSLINE: [carrier name]</p>
                                        <p>TT: [X] days</p>
                                        <p>TS: [place] or "direct service"</p>
                                        <p>OFR valid thru: {template.template_fields.validity}</p>
                                        <p>Bkg subj to eq and space availability</p>
                                        <p>Bkg subj to cgo acceptance</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* LCL Import Comparison Tab */}
        {activeTab === "lcl-import" && (
          <div className="space-y-6">
            {/* Header with Seed Button */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">LCL Import Rate Comparison</h2>
                <p className="text-slate-600 text-sm mt-1">
                  Compare import LCL rates from multiple vendors to Genova, Italy
                </p>
              </div>
              <Button
                onClick={seedSinergyRates}
                variant="outline"
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                data-testid="seed-sinergy-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Sinergy Rates
              </Button>
            </div>

            {/* Rate Cards Overview */}
            {lclImport.rateCards.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <h3 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Active Import Rate Cards
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {lclImport.rateCards.map((card) => (
                    <div key={card.id} className="bg-white rounded-lg p-3 border border-emerald-100">
                      <p className="font-medium text-slate-900">{card.name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                          {card.vendor}
                        </span>
                        <span>{card.entry_count} rates</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Valid: {card.validity_start} to {card.validity_end}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Search Box */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Search className="h-4 w-4" />
                Search Origin Port
              </h3>
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Input
                    value={lclImport.searchQuery}
                    onChange={(e) => {
                      setLclImport(prev => ({ ...prev, searchQuery: e.target.value }));
                      searchLclImportPorts(e.target.value);
                    }}
                    placeholder="Enter port name (e.g., Shanghai, Ho Chi Minh, Mumbai...)"
                    className="pr-10"
                    data-testid="lcl-import-search"
                  />
                  {lclImport.searchQuery && (
                    <button
                      onClick={() => setLclImport(prev => ({ ...prev, searchQuery: "", searchResults: [], comparison: null }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Button
                  onClick={() => compareLclImportRates(lclImport.searchQuery)}
                  disabled={lclImport.searchQuery.length < 2 || lclImport.loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="compare-rates-btn"
                >
                  {lclImport.loading ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Compare Rates
                </Button>
              </div>

              {/* Search Results Dropdown */}
              {lclImport.searchResults.length > 0 && !lclImport.comparison && (
                <div className="mt-3 border border-slate-200 rounded-lg max-h-60 overflow-y-auto">
                  {lclImport.searchResults.map((result, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setLclImport(prev => ({ ...prev, searchQuery: result.pol_name, searchResults: [] }));
                        compareLclImportRates(result.pol_name);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    >
                      <span className="font-medium">{result.pol_name}</span>
                      {result.country && (
                        <span className="text-slate-500 ml-2">({result.country})</span>
                      )}
                      {result.min_rate && (
                        <span className="float-right text-emerald-600 font-medium">
                          from ${result.min_rate}/CBM
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Comparison Results */}
            {lclImport.comparison && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Package className="h-5 w-5 text-emerald-600" />
                      Rate Comparison: {lclImport.comparison.origin}
                      {lclImport.comparison.origin_country && (
                        <span className="text-slate-500 font-normal">
                          ({lclImport.comparison.origin_country})
                        </span>
                      )}
                      <ArrowRight className="h-4 w-4 text-slate-400" />
                      {lclImport.comparison.destination}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {lclImport.comparison.vendor_count} vendors compared
                    </p>
                  </div>
                  {lclImport.comparison.cheapest_vendor && (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Cheapest Rate</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        ${lclImport.comparison.cheapest_rate}/CBM
                      </p>
                    </div>
                  )}
                </div>

                {/* Comparison Table */}
                {lclImport.comparison.comparison && lclImport.comparison.comparison.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Vendor</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Rate/CBM</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Min CBM</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Transit</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Service</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Surcharges</th>
                          <th className="text-center py-3 px-4 text-sm font-semibold text-slate-700">Valid To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lclImport.comparison.comparison.map((rate, idx) => (
                          <tr 
                            key={idx} 
                            className={`border-b border-slate-100 hover:bg-slate-50 ${
                              rate.vendor === lclImport.comparison.cheapest_vendor ? 'bg-emerald-50' : ''
                            }`}
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {rate.vendor === lclImport.comparison.cheapest_vendor && (
                                  <span className="text-emerald-600" title="Cheapest">
                                    <CheckCircle className="h-4 w-4" />
                                  </span>
                                )}
                                <div>
                                  <p className="font-medium text-slate-900">{rate.vendor_name}</p>
                                  {rate.rate_card && (
                                    <p className="text-xs text-slate-500">{rate.rate_card}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className={`font-bold text-lg ${
                                rate.vendor === lclImport.comparison.cheapest_vendor 
                                  ? 'text-emerald-600' 
                                  : 'text-slate-900'
                              }`}>
                                ${rate.rate_per_cbm}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right text-slate-600">
                              {rate.minimum_cbm} CBM
                            </td>
                            <td className="py-3 px-4 text-center">
                              {rate.transit_days ? (
                                <span className="text-slate-600">{rate.transit_days} days</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">
                              {rate.service || '-'}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-wrap gap-1">
                                {rate.surcharges && rate.surcharges.map((s, i) => (
                                  <span 
                                    key={i}
                                    className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full"
                                    title={s === 'LSS' ? 'Low Sulphur Surcharge: $5/CBM' : s === 'ETS' ? 'Emissions Trading System: $5/CBM' : s}
                                  >
                                    {s}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center text-sm text-slate-500">
                              {rate.valid_to}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Surcharge Info */}
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Additional Surcharges (not included in base rate)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-amber-700 font-medium">LSS</p>
                      <p className="text-amber-600">$5/CBM (min 1 CBM)</p>
                    </div>
                    <div>
                      <p className="text-amber-700 font-medium">ETS</p>
                      <p className="text-amber-600">$5/CBM (min 1 CBM)</p>
                    </div>
                    <div>
                      <p className="text-amber-700 font-medium">W/M Ratio</p>
                      <p className="text-amber-600">1 CBM = 750 kgs</p>
                    </div>
                    <div>
                      <p className="text-amber-700 font-medium">FOB</p>
                      <p className="text-amber-600">Excl. loading charges</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Regions Overview */}
            {lclImport.regions.length > 0 && !lclImport.comparison && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Available Regions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lclImport.regions.map((region) => (
                    <div 
                      key={region.region}
                      className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      onClick={() => setLclImport(prev => ({ ...prev, selectedRegion: region.region }))}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-900">{region.region_name}</h4>
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                          {region.port_count} ports
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">
                        {region.sample_ports && region.sample_ports.slice(0, 3).join(", ")}
                        {region.sample_ports && region.sample_ports.length > 3 && "..."}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Port-Pair Search Tab */}
        {activeTab === "port-pair" && (
          <div className="space-y-6" data-testid="port-pair-tab-content">
            <PortPairSearchPanel />
            <details className="rounded-xl border border-slate-200 bg-white">
              <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-slate-700 flex items-center gap-2" data-testid="fl-charges-admin-toggle">
                <Settings className="h-4 w-4" />
                Manage Freightlinks Charges (admin)
              </summary>
              <div className="p-4 border-t border-slate-200">
                <FreightlinksChargesAdmin />
              </div>
            </details>
          </div>
        )}

        {/* Add Custom Service Modal */}
        {showAddServiceModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 text-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Plus className="h-5 w-5" />
                    Add Custom Service
                  </h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowAddServiceModal(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Service Name */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Service Name *</label>
                  <Input
                    value={newService.name}
                    onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Ocean Freight, THC, Customs Clearance"
                    className="mt-1"
                  />
                </div>

                {/* Category / Section */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Category / Section *</label>
                  <select
                    value={newService.category}
                    onChange={(e) => setNewService(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full mt-1 rounded-lg border-slate-200 text-sm p-2"
                  >
                    {SERVICE_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name} - {cat.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Description</label>
                  <Input
                    value={newService.description}
                    onChange={(e) => setNewService(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the service"
                    className="mt-1"
                  />
                </div>

                {/* Pricing Row */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Quantity</label>
                    <Input
                      type="number"
                      min="1"
                      value={newService.quantity}
                      onChange={(e) => setNewService(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Unit Price</label>
                    <Input
                      type="number"
                      step="0.001"
                      value={newService.unit_price}
                      onChange={(e) => setNewService(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Currency</label>
                    <select
                      value={newService.currency}
                      onChange={(e) => setNewService(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full mt-1 rounded-lg border-slate-200 text-sm p-2"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                      <option value="CHF">CHF</option>
                      <option value="CNY">CNY</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Unit</label>
                    <select
                      value={newService.unit}
                      onChange={(e) => setNewService(prev => ({ ...prev, unit: e.target.value }))}
                      className="w-full mt-1 rounded-lg border-slate-200 text-sm p-2"
                    >
                      {UNIT_TYPES.map(unit => (
                        <option key={unit.id} value={unit.id}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Calculated Total */}
                <div className="p-3 bg-slate-100 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Line Total:</span>
                    <span className="font-bold text-lg">
                      {(newService.quantity * newService.unit_price).toFixed(3)} {newService.currency}
                    </span>
                  </div>
                </div>

                {/* Save as Template Option */}
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newService.saveAsTemplate}
                      onChange={(e) => setNewService(prev => ({ ...prev, saveAsTemplate: e.target.checked }))}
                      className="mt-1 rounded border-green-300"
                    />
                    <div>
                      <span className="font-medium text-green-800">Save as template for future offers</span>
                      <p className="text-xs text-green-600 mt-0.5">
                        This service will appear in the search dropdown for all future offers
                      </p>
                    </div>
                  </label>
                </div>

                {/* Notes / Comments */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Notes / Comments</label>
                  <textarea
                    value={newService.notes}
                    onChange={(e) => setNewService(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes, conditions, or comments about this service..."
                    rows={3}
                    className="w-full mt-1 rounded-lg border-slate-200 text-sm px-3 py-2"
                  />
                </div>

                {/* Quick Category Buttons */}
                <div>
                  <label className="text-xs text-slate-500 mb-2 block">Quick Select Category:</label>
                  <div className="flex flex-wrap gap-2">
                    {SERVICE_CATEGORIES.slice(0, 6).map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setNewService(prev => ({ ...prev, category: cat.id }))}
                        className={`px-3 py-1 text-xs rounded-full transition-all ${
                          newService.category === cat.id
                            ? "text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        style={{
                          backgroundColor: newService.category === cat.id ? CATEGORY_COLORS[cat.id] : undefined
                        }}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddServiceModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={addCustomService}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Service
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Metrans Rail Rate Lookup Modal */}
        {metransLookup.show && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Train className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {metransLookup.mode === 'precarriage' ? 'Pre-carriage' : 'On-carriage'} Rate Lookup
                      </h3>
                      <p className="text-sm text-slate-500">Metrans Rail + Truck Service</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMetransLookup(prev => ({ ...prev, show: false }))}
                    className="p-2 hover:bg-slate-100 rounded-lg"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Transport Mode Selection */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-slate-700 block mb-2">Transport Mode</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(TRANSPORT_MODES).map(([key, mode]) => (
                      <button
                        key={key}
                        onClick={() => !mode.comingSoon && setMetransLookup(prev => ({ ...prev, transportMode: key }))}
                        disabled={mode.comingSoon}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          metransLookup.transportMode === key
                            ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500'
                            : mode.comingSoon 
                              ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed'
                              : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <mode.icon className={`h-4 w-4 mb-1 ${metransLookup.transportMode === key ? 'text-orange-600' : 'text-slate-500'}`} />
                        <p className="text-xs font-medium">{mode.label}</p>
                        {mode.comingSoon && <span className="text-xs text-amber-600">Coming soon</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Show Metrans lookup only for rail_truck mode */}
                {metransLookup.transportMode === 'rail_truck' && (
                  <>
                    {/* Country & Container */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">Country</label>
                        <select
                          value={metransLookup.destinationCountry}
                          onChange={(e) => setMetransLookup(prev => ({ ...prev, destinationCountry: e.target.value }))}
                          className="w-full rounded-lg border-slate-200 text-sm"
                        >
                          <option value="HU">Hungary</option>
                          <option value="PL">Poland</option>
                          <option value="CZ">Czech Republic</option>
                          <option value="SK">Slovakia</option>
                          <option value="RO">Romania</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">Container</label>
                        <select
                          value={metransLookup.containerType}
                          onChange={(e) => setMetransLookup(prev => ({ ...prev, containerType: e.target.value }))}
                          className="w-full rounded-lg border-slate-200 text-sm"
                        >
                          <option value="20">20' Container</option>
                          <option value="40">40' Container</option>
                        </select>
                      </div>
                    </div>

                    {/* Destination Search */}
                    <div className="mb-4">
                      <label className="text-sm font-medium text-slate-700 block mb-1">Destination City</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={metransLookup.destination}
                          onChange={(e) => {
                            setMetransLookup(prev => ({ ...prev, destination: e.target.value }));
                            searchMetransDestinations(e.target.value, metransLookup.destinationCountry);
                          }}
                          placeholder="Search city..."
                          className="pl-10"
                        />
                      </div>
                      {metransLookup.searchResults.length > 0 && (
                        <div className="mt-1 border rounded-lg max-h-32 overflow-y-auto bg-white shadow-sm">
                          {metransLookup.searchResults.map((r, idx) => (
                            <button
                              key={idx}
                              onClick={() => setMetransLookup(prev => ({ 
                                ...prev, 
                                destination: r.destination,
                                searchResults: []
                              }))}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-0"
                            >
                              {r.destination} {r.postal_code && <span className="text-slate-400">({r.postal_code})</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Weight */}
                    <div className="mb-4">
                      <label className="text-sm font-medium text-slate-700 block mb-1">Gross Weight (kg)</label>
                      <Input
                        type="number"
                        value={metransLookup.weightKg}
                        onChange={(e) => setMetransLookup(prev => ({ ...prev, weightKg: parseInt(e.target.value) || 0 }))}
                        placeholder="25000"
                      />
                      <p className="text-xs text-slate-500 mt-1">Cargo + container tara weight</p>
                    </div>

                    {/* Lookup Button */}
                    <Button
                      onClick={lookupMetransRate}
                      disabled={!metransLookup.destination || metransLookup.loading}
                      className="w-full bg-orange-600 hover:bg-orange-700 mb-4"
                    >
                      {metransLookup.loading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      Look Up Rate
                    </Button>

                    {/* Results */}
                    {metransLookup.result && (
                      <div className={`p-4 rounded-lg ${metransLookup.result.found ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                        {metransLookup.result.found ? (
                          (() => {
                            const depotOptions = getMetransDepotOptions();
                            const selectedRate = getSelectedMetransRate();
                            const hasMultipleDepots = depotOptions.length > 1;
                            
                            return (
                              <>
                                <div className="flex items-center gap-2 mb-3">
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                  <span className="font-medium text-green-800">Rate Found</span>
                                  {hasMultipleDepots && (
                                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                      {depotOptions.length} depot options
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">Destination:</span>
                                    <span className="font-medium">{metransLookup.result.destination}</span>
                                  </div>
                                  
                                  {/* Via Depot - Dropdown if multiple options */}
                                  <div className="flex justify-between items-center">
                                    <span className="text-slate-600">Via Depot:</span>
                                    {hasMultipleDepots ? (
                                      <select
                                        value={metransLookup.selectedDepotIndex}
                                        onChange={(e) => setMetransLookup(prev => ({ 
                                          ...prev, 
                                          selectedDepotIndex: parseInt(e.target.value) 
                                        }))}
                                        className="rounded-lg border-slate-300 text-sm font-medium py-1 px-2 bg-white"
                                        data-testid="metrans-depot-selector"
                                      >
                                        {depotOptions.map((opt, idx) => (
                                          <option key={idx} value={idx}>
                                            {opt.via_depot} ({(opt.rate + (opt.toll || 0)).toFixed(0)}€{opt.toll > 0 ? ' incl. toll' : ''})
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="font-medium">{selectedRate?.via_depot}</span>
                                    )}
                                  </div>
                                  
                                  <div className="flex justify-between">
                                    <span className="text-slate-600">Weight Bracket:</span>
                                    <span className="font-medium">{metransLookup.result.container_type} {metransLookup.result.weight_bracket}</span>
                                  </div>
                                  <div className="flex justify-between border-t pt-2 mt-2">
                                    <span className="text-slate-600">Base Rate:</span>
                                    <span className="font-medium">{selectedRate?.rate?.toFixed(2)} EUR</span>
                                  </div>
                                  {selectedRate?.toll > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-600">Road Toll:</span>
                                      <span className="font-medium">+{selectedRate.toll?.toFixed(2)} EUR</span>
                                    </div>
                                  )}
                                  
                                  {/* Surcharges Link */}
                                  <div className="flex justify-between items-center pt-1">
                                    <span className="text-slate-600">Surcharges:</span>
                                    <a 
                                      href="https://metrans.eu/customer-portal/additional-surcharge/" 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-orange-600 hover:text-orange-700 text-xs underline flex items-center gap-1"
                                    >
                                      View Metrans surcharges
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  </div>
                                </div>

                                {/* Profit Margin */}
                                <div className="mt-4 pt-4 border-t">
                                  <label className="text-sm font-medium text-slate-700 block mb-2">Your Profit Margin</label>
                                  <div className="flex gap-2">
                                    <select
                                      value={metransLookup.profitType}
                                      onChange={(e) => setMetransLookup(prev => ({ ...prev, profitType: e.target.value }))}
                                      className="rounded-lg border-slate-200 text-sm"
                                    >
                                      <option value="percentage">%</option>
                                      <option value="fixed">EUR</option>
                                    </select>
                                    <Input
                                      type="number"
                                      value={metransLookup.profitValue}
                                      onChange={(e) => setMetransLookup(prev => ({ ...prev, profitValue: parseFloat(e.target.value) || 0 }))}
                                      className="flex-1"
                                      min="0"
                                    />
                                  </div>
                                  <div className="mt-3 p-3 bg-slate-900 rounded-lg text-white">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-slate-400">Customer Price:</span>
                                      <span className="text-xl font-bold">
                                        {calculateSellingPrice(selectedRate?.rate || 0, selectedRate?.toll || 0).toFixed(2)} EUR
                                      </span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">
                                      Base rate + toll not visible to customer
                                    </p>
                                  </div>
                                </div>

                                {/* Add to Offer Button */}
                                <Button
                                  onClick={addMetransRateToOffer}
                                  className="w-full mt-4 bg-green-600 hover:bg-green-700"
                                  data-testid="metrans-add-to-offer-btn"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add to Offer
                                </Button>
                              </>
                            );
                          })()
                        ) : (
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <span className="text-amber-800">No rate found for this destination</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Truck Only Mode */}
                {metransLookup.transportMode === 'truck' && (
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <Truck className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      For direct truck transport, use the <strong>"Custom"</strong> button to add a trucking charge manually.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setMetransLookup(prev => ({ ...prev, show: false }));
                        setNewService(prev => ({
                          ...prev,
                          name: metransLookup.mode === 'precarriage' ? 'Pre-carriage (Truck)' : 'On-carriage (Truck)',
                          category: metransLookup.mode === 'precarriage' ? 'origin_charges' : 'destination_charges',
                          currency: offerBuilder.currency || "EUR",
                          notes: TRUCK_RATE_NOTES
                        }));
                        setShowAddServiceModal(true);
                      }}
                      className="mt-3"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Truck Transport
                    </Button>
                  </div>
                )}

                {/* Tilt Truck Mode */}
                {metransLookup.transportMode === 'tilt_truck' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">Tilt/Curtain-side truck for LDM-based cargo</p>
                    
                    {/* From / To Locations */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">From</label>
                        <Input
                          value={tiltTruckCalc.fromLocation}
                          onChange={(e) => setTiltTruckCalc(prev => ({ ...prev, fromLocation: e.target.value }))}
                          placeholder="Origin location"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">To</label>
                        <Input
                          value={tiltTruckCalc.toLocation}
                          onChange={(e) => setTiltTruckCalc(prev => ({ ...prev, toLocation: e.target.value }))}
                          placeholder="Destination location"
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Load Type Selection */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-2">Load Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(TILT_TRUCK_OPTIONS).map(([key, opt]) => (
                          <button
                            key={key}
                            onClick={() => setTiltTruckCalc(prev => ({ ...prev, loadType: key }))}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              tiltTruckCalc.loadType === key
                                ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-medium text-sm">{opt.label}</p>
                            <p className="text-xs text-slate-500">{opt.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Price Input */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Price (EUR)</label>
                      <Input
                        type="number"
                        min="0"
                        value={tiltTruckCalc.price}
                        onChange={(e) => setTiltTruckCalc(prev => ({ ...prev, price: e.target.value }))}
                        placeholder="Enter trucking price"
                        className="w-full"
                      />
                    </div>

                    {/* Comments */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Comments / Notes</label>
                      <textarea
                        value={tiltTruckCalc.comments}
                        onChange={(e) => setTiltTruckCalc(prev => ({ ...prev, comments: e.target.value }))}
                        placeholder="e.g., Loading/unloading included, waiting time terms..."
                        className="w-full rounded-lg border-slate-200 text-sm p-2 min-h-[60px]"
                      />
                    </div>

                    {/* Profit Margin */}
                    <div className="pt-3 border-t">
                      <label className="text-sm font-medium text-slate-700 block mb-2">Your Profit Margin</label>
                      <div className="flex gap-2">
                        <select
                          value={tiltTruckCalc.profitType}
                          onChange={(e) => setTiltTruckCalc(prev => ({ ...prev, profitType: e.target.value }))}
                          className="rounded-lg border-slate-200 text-sm"
                        >
                          <option value="percentage">%</option>
                          <option value="fixed">EUR</option>
                        </select>
                        <Input
                          type="number"
                          value={tiltTruckCalc.profitValue}
                          onChange={(e) => setTiltTruckCalc(prev => ({ ...prev, profitValue: parseFloat(e.target.value) || 0 }))}
                          className="flex-1"
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Price Summary */}
                    {tiltTruckCalc.price && (
                      <div className="p-3 bg-slate-900 rounded-lg text-white">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Customer Price:</span>
                          <span className="text-xl font-bold">
                            {(() => {
                              const basePrice = parseFloat(tiltTruckCalc.price) || 0;
                              const profit = tiltTruckCalc.profitType === 'percentage' 
                                ? basePrice * (tiltTruckCalc.profitValue / 100)
                                : tiltTruckCalc.profitValue;
                              return (basePrice + profit).toFixed(2);
                            })()} EUR
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {TILT_TRUCK_OPTIONS[tiltTruckCalc.loadType]?.label} • {tiltTruckCalc.fromLocation || '?'} → {tiltTruckCalc.toLocation || '?'}
                        </p>
                      </div>
                    )}

                    {/* Add to Offer Button */}
                    <Button
                      onClick={() => {
                        const basePrice = parseFloat(tiltTruckCalc.price) || 0;
                        if (basePrice <= 0) {
                          toast.error('Please enter a valid price');
                          return;
                        }
                        const profit = tiltTruckCalc.profitType === 'percentage' 
                          ? basePrice * (tiltTruckCalc.profitValue / 100)
                          : tiltTruckCalc.profitValue;
                        const sellingPrice = basePrice + profit;
                        const loadOpt = TILT_TRUCK_OPTIONS[tiltTruckCalc.loadType];
                        
                        const componentName = metransLookup.mode === 'precarriage'
                          ? `Pre-carriage Tilt Truck (${loadOpt.label})`
                          : `On-carriage Tilt Truck (${loadOpt.label})`;
                        
                        // Combine user comments with standard truck notes
                        const userNotes = tiltTruckCalc.comments ? `${tiltTruckCalc.comments}\n\n` : '';
                        const standardNotes = TRUCK_RATE_NOTES;
                        
                        const newComponent = {
                          component_id: `tilt_truck_${Date.now()}`,
                          name: componentName,
                          category: metransLookup.mode === 'precarriage' ? 'origin_charges' : 'destination_charges',
                          quantity: 1,
                          unit_price: Math.round(sellingPrice * 1000) / 1000,
                          currency: 'EUR',
                          unit: 'shipment',
                          description: `${tiltTruckCalc.fromLocation} → ${tiltTruckCalc.toLocation} (${loadOpt.ldm} LDM / <${loadOpt.maxWeight/1000}t gross)`,
                          notes: userNotes + standardNotes,
                          is_included: true,
                        };
                        
                        setOfferBuilder(prev => ({
                          ...prev,
                          components: [...prev.components, newComponent]
                        }));
                        
                        toast.success(`Added ${componentName} to offer`);
                        setMetransLookup(prev => ({ ...prev, show: false }));
                        // Reset form
                        setTiltTruckCalc({
                          fromLocation: '',
                          toLocation: '',
                          loadType: '6ldm_11t',
                          price: '',
                          comments: '',
                          profitType: 'percentage',
                          profitValue: 10,
                        });
                      }}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      disabled={!tiltTruckCalc.price || !tiltTruckCalc.fromLocation || !tiltTruckCalc.toLocation}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Tilt Truck to Offer
                    </Button>
                  </div>
                )}

                {/* Warehouse + Transloading Calculator */}
                {metransLookup.transportMode === 'warehouse' && (
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    {/* Cargo Type Selection */}
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-2">Cargo Type</label>
                      <div className="grid grid-cols-1 gap-2">
                        {Object.entries(TRANSLOADING_RATES).map(([key, rate]) => (
                          <button
                            key={key}
                            onClick={() => setTransloadingCalc(prev => ({ ...prev, cargoType: key }))}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              transloadingCalc.cargoType === key
                                ? 'border-green-500 bg-green-50 ring-1 ring-green-500'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <p className="font-medium text-sm">{rate.label}</p>
                            <p className="text-xs text-slate-500">{rate.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Container Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">Container Type</label>
                        <select
                          value={transloadingCalc.containerType}
                          onChange={(e) => setTransloadingCalc(prev => ({ ...prev, containerType: e.target.value }))}
                          className="w-full rounded-lg border-slate-200 text-sm"
                        >
                          {transloadingCalc.cargoType.includes('oversized') ? (
                            <>
                              {transloadingCalc.cargoType !== 'oversized_export' && <option value="20">20' Flat Rack</option>}
                              <option value="40">40' Flat Rack</option>
                            </>
                          ) : (
                            <>
                              <option value="20">20' Container</option>
                              <option value="40">40'/40HC Container</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">Quantity</label>
                        <Input
                          type="number"
                          min="1"
                          value={transloadingCalc.containerQty}
                          onChange={(e) => setTransloadingCalc(prev => ({ ...prev, containerQty: parseInt(e.target.value) || 1 }))}
                        />
                      </div>
                    </div>

                    {/* Storage Details - varies by cargo type */}
                    <div className="grid grid-cols-2 gap-4">
                      {TRANSLOADING_RATES[transloadingCalc.cargoType]?.storage?.unit === 'ton' ? (
                        <div>
                          <label className="text-sm font-medium text-slate-700 block mb-1">Cargo Weight (tons)</label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={transloadingCalc.cargoTons}
                            onChange={(e) => setTransloadingCalc(prev => ({ ...prev, cargoTons: parseFloat(e.target.value) || 0 }))}
                            placeholder="0"
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="text-sm font-medium text-slate-700 block mb-1">Cargo Volume (CBM)</label>
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            value={transloadingCalc.cargoCbm}
                            onChange={(e) => setTransloadingCalc(prev => ({ ...prev, cargoCbm: parseFloat(e.target.value) || 0 }))}
                            placeholder="0"
                          />
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-slate-700 block mb-1">Storage Days</label>
                        <Input
                          type="number"
                          min="0"
                          value={transloadingCalc.storageDays}
                          onChange={(e) => setTransloadingCalc(prev => ({ ...prev, storageDays: parseInt(e.target.value) || 0 }))}
                          placeholder="0"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {TRANSLOADING_RATES[transloadingCalc.cargoType]?.storage?.free_days || 7} days free
                        </p>
                      </div>
                    </div>

                    {/* Loose cargo - Palletizing */}
                    {transloadingCalc.cargoType.includes('loose') && (
                      <div className="bg-amber-50 rounded-lg p-3 space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={transloadingCalc.needsPalletizing}
                            onChange={(e) => setTransloadingCalc(prev => ({ ...prev, needsPalletizing: e.target.checked }))}
                            className="rounded border-slate-300"
                          />
                          <span className="font-medium">Needs Palletizing</span>
                          <span className="text-slate-500">(€12/pallet + €18 palletizing fee)</span>
                        </label>
                        {transloadingCalc.needsPalletizing && (
                          <div className="ml-6">
                            <label className="text-xs text-slate-600 block mb-1">Number of Pallets</label>
                            <Input
                              type="number"
                              min="0"
                              value={transloadingCalc.palletCount}
                              onChange={(e) => setTransloadingCalc(prev => ({ ...prev, palletCount: parseInt(e.target.value) || 0 }))}
                              placeholder="0"
                              className="h-8 text-sm w-24"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Surcharges */}
                    <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-slate-600 uppercase">Surcharges</p>
                      <div className="grid grid-cols-2 gap-2">
                        {/* Standard surcharges for non-oversized */}
                        {!transloadingCalc.cargoType.includes('oversized') && (
                          <>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={transloadingCalc.hasHeavyCargo}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, hasHeavyCargo: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              Heavy cargo (&gt;2.5t/pc) +€100
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={transloadingCalc.needsFixingLashing}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, needsFixingLashing: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              Fixing/Lashing (min €125)
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={transloadingCalc.isAdrImo}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, isAdrImo: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              ADR/IMO (+30%)
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={transloadingCalc.isReefer}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, isReefer: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              Reefer (+30%)
                            </label>
                          </>
                        )}
                        
                        {/* Oversized Export surcharges */}
                        {transloadingCalc.cargoType === 'oversized_export' && (
                          <>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={transloadingCalc.needsFixingLashing}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, needsFixingLashing: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              Fixing/Lashing (min €540)
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={transloadingCalc.needsLashingCertificate}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, needsLashingCertificate: e.target.checked }))}
                                className="rounded border-slate-300"
                              />
                              Lashing Certificate (€300)
                            </label>
                          </>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div>
                          <label className="text-xs text-slate-600 block mb-1">Printing (pages)</label>
                          <Input
                            type="number"
                            min="0"
                            value={transloadingCalc.printingPages}
                            onChange={(e) => setTransloadingCalc(prev => ({ ...prev, printingPages: parseInt(e.target.value) || 0 }))}
                            placeholder="0"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600 block mb-1">Cargo Value (EUR)</label>
                          <Input
                            type="number"
                            min="0"
                            value={transloadingCalc.cargoValue}
                            onChange={(e) => setTransloadingCalc(prev => ({ ...prev, cargoValue: parseFloat(e.target.value) || 0 }))}
                            placeholder="0"
                            className="h-8 text-sm"
                          />
                          <p className="text-xs text-slate-400">+0.02% if &gt;€300k</p>
                        </div>
                      </div>
                    </div>

                    {/* Cost Breakdown */}
                    {(() => {
                      const costs = calculateTransloadingCosts();
                      const rateCard = TRANSLOADING_RATES[transloadingCalc.cargoType];
                      return (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-medium text-green-800">Cost Breakdown</span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">{rateCard?.label || 'Transloading'}:</span>
                              <span className="font-medium">€{costs.transloadingCost?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Admin Fee:</span>
                              <span className="font-medium">€{costs.adminFee?.toFixed(2) || '0.00'}</span>
                            </div>
                            {costs.storageCost > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-600">Storage ({costs.storageUnit}):</span>
                                <span className="font-medium">€{costs.storageCost.toFixed(2)}</span>
                              </div>
                            )}
                            {costs.palletizingCost > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-600">Palletizing:</span>
                                <span className="font-medium">€{costs.palletizingCost.toFixed(2)}</span>
                              </div>
                            )}
                            {costs.surcharges > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-600">Surcharges:</span>
                                <span className="font-medium">€{costs.surcharges.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between border-t pt-2 mt-2">
                              <span className="text-slate-700 font-medium">Total Cost:</span>
                              <span className="font-bold">€{costs.totalCost?.toFixed(2) || '0.00'}</span>
                            </div>
                          </div>

                          {/* Profit Margin */}
                          <div className="mt-4 pt-3 border-t border-green-200">
                            <label className="text-sm font-medium text-slate-700 block mb-2">Your Profit Margin</label>
                            <div className="flex gap-2">
                              <select
                                value={transloadingCalc.profitType}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, profitType: e.target.value }))}
                                className="rounded-lg border-slate-200 text-sm"
                              >
                                <option value="percentage">%</option>
                                <option value="fixed">EUR</option>
                              </select>
                              <Input
                                type="number"
                                value={transloadingCalc.profitValue}
                                onChange={(e) => setTransloadingCalc(prev => ({ ...prev, profitValue: parseFloat(e.target.value) || 0 }))}
                                className="flex-1"
                                min="0"
                              />
                            </div>
                            <div className="mt-3 p-3 bg-slate-900 rounded-lg text-white">
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-400">Customer Price:</span>
                                <span className="text-xl font-bold">
                                  €{(transloadingCalc.profitType === 'percentage' 
                                    ? (costs.totalCost || 0) * (1 + transloadingCalc.profitValue / 100)
                                    : (costs.totalCost || 0) + transloadingCalc.profitValue
                                  ).toFixed(2)}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                Cost breakdown not visible to customer
                              </p>
                            </div>
                          </div>

                          {/* Add to Offer Button */}
                          <Button
                            onClick={addTransloadingToOffer}
                            className="w-full mt-4 bg-green-600 hover:bg-green-700"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add to Offer
                          </Button>
                        </div>
                      );
                    })()}

                    {/* Conditions Note */}
                    <div className="text-xs text-slate-500 space-y-1">
                      <p className="font-medium">Conditions:</p>
                      <ul className="list-disc pl-4">
                        <li>Drayage CY-CFS-CY included</li>
                        {!transloadingCalc.cargoType.includes('oversized') && (
                          <>
                            <li>Goods must be forkliftable</li>
                            <li>ADR/IMO & RF: direct manipulation only</li>
                          </>
                        )}
                        {transloadingCalc.cargoType.includes('oversized') && (
                          <li>Excludes 3rd party billing (customs manifest, dunnage removal, etc.)</li>
                        )}
                        {transloadingCalc.cargoType.includes('loose') && (
                          <li>Max 20kg/carton for manual handling</li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

        {/* Contact Confirmation Modal */}
        {showContactModal && pendingContact && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-600 to-blue-500 p-4 text-white">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  New Contact Detected
                </h3>
                <p className="text-sm text-blue-100 mt-1">
                  This contact is not in your CRM. Would you like to add them?
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Contact Name</Label>
                  <Input
                    value={pendingContact.name || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input
                    value={pendingContact.email || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@company.com"
                    className="mt-1"
                    disabled
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Company Name</Label>
                  <Input
                    value={pendingContact.company_name || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="ACME Corporation"
                    className="mt-1"
                  />
                  {pendingContact.suggested_company && (
                    <p className="text-xs text-blue-600 mt-1">
                      💡 Suggested: {pendingContact.suggested_company} (matches email domain)
                    </p>
                  )}
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Phone (optional)</Label>
                  <Input
                    value={pendingContact.phone || ''}
                    onChange={(e) => setPendingContact(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+386 1 234 5678"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 p-4 bg-slate-50 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowContactModal(false);
                    setPendingContact(null);
                  }}
                >
                  Skip
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={saveConfirmedContact}
                  disabled={savingContact}
                >
                  {savingContact ? "Saving..." : "Add to CRM"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Assign Task Modal */}
        {assignTaskModal.show && assignTaskModal.quote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4"
            >
              <div className="bg-blue-600 px-6 py-4 rounded-t-xl">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Assign Quote to Staff
                </h2>
                <p className="text-blue-100 text-sm mt-1">
                  {assignTaskModal.quote.quote_number} • {assignTaskModal.quote.company_name}
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Assign to <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedStaff}
                    onChange={(e) => setSelectedStaff(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="assign-staff-select"
                  >
                    <option value="">Select staff member...</option>
                    {staffList.map((staff) => (
                      <option key={staff.email} value={staff.email}>
                        {staff.full_name || staff.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Due Date
                  </label>
                  <Input
                    type="date"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="task-due-date"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Note / Instructions
                  </label>
                  <textarea
                    value={taskNote}
                    onChange={(e) => setTaskNote(e.target.value)}
                    placeholder="Add any special instructions or notes..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={3}
                    data-testid="task-note"
                  />
                </div>

                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <p className="text-slate-600">
                    <strong>Route:</strong> {assignTaskModal.quote.origin_city} → {assignTaskModal.quote.destination_city}
                  </p>
                  {assignTaskModal.quote.container_type && (
                    <p className="text-slate-500 mt-1">
                      {assignTaskModal.quote.container_quantity}x {assignTaskModal.quote.container_type}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 p-4 bg-slate-50 border-t rounded-b-xl">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setAssignTaskModal({ show: false, quote: null, loading: false })}
                  disabled={assignTaskModal.loading}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={handleAssignTask}
                  disabled={assignTaskModal.loading || !selectedStaff}
                  data-testid="confirm-assign-task"
                >
                  {assignTaskModal.loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign Task
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </main>
    </OffersModuleLayout>
  );
}

// Component Selector for adding new components - Searchable Combobox with Portal
function ComponentSelector({ serviceType, onSelect, token }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const inputBoxRef = useRef(null);
  
  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/pricing/component-templates?service_type=${serviceType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTemplates(response.data);
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [serviceType, token]);
  
  const filtered = templates.filter(t => 
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.category.toLowerCase().includes(search.toLowerCase())
  );
  
  const grouped = filtered.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});
  
  // Update dropdown position when opening
  useEffect(() => {
    if (open && inputBoxRef.current) {
      const rect = inputBoxRef.current.getBoundingClientRect();
      // Use viewport-relative position for fixed positioning
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 320) // Minimum width of 320px
      });
    }
  }, [open]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inputRef.current && !inputRef.current.contains(e.target)) {
        // Also check if click is inside the portal dropdown
        const dropdown = document.getElementById("component-selector-dropdown");
        if (dropdown && dropdown.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  // Dropdown portal content
  const dropdownContent = open && (
    <div
      id="component-selector-dropdown"
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        minWidth: '320px',
        maxWidth: '450px',
        zIndex: 9999
      }}
      className="bg-white rounded-xl border border-slate-200 shadow-2xl max-h-80 overflow-hidden"
    >
      {loading ? (
        <div className="p-4 text-center text-slate-500 text-sm">Loading components...</div>
      ) : filtered.length === 0 ? (
        <div className="p-4 text-center text-slate-500 text-sm">
          {search ? `No components matching "${search}"` : "No components available"}
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto p-2">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="mb-2">
              <p 
                className="text-xs font-medium px-2 py-1 uppercase flex items-center gap-2"
                style={{ color: CATEGORY_COLORS[category] }}
              >
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: CATEGORY_COLORS[category] }}
                />
                {category.replace(/_/g, " ")}
              </p>
              {items.map(t => (
                <button
                  key={t.id}
                  onClick={() => {
                    onSelect(t);
                    setSearch("");
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 rounded-lg flex items-center justify-between group ${
                    t.is_custom ? "border-l-2 border-purple-400 bg-purple-50/50" : ""
                  }`}
                >
                  <span className="group-hover:text-blue-700 flex items-center gap-2">
                    {t.name}
                    {t.is_custom && (
                      <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">saved</span>
                    )}
                  </span>
                  <span className="text-xs text-slate-400 group-hover:text-blue-500">
                    {t.default_rate > 0 && `${t.default_rate} ${t.currency || 'EUR'}`}
                    {t.default_rate > 0 && " • "}
                    {t.unit?.replace("per_", "/")}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
  
  return (
    <div className="relative flex-1 min-w-[200px]" ref={inputRef}>
      <div className="relative" ref={inputBoxRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search & add component..."
          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="add-component-input"
        />
        {search && (
          <button
            onClick={() => {
              setSearch("");
              setOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {/* Portal-rendered dropdown to avoid overflow clipping */}
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </div>
  );
}
